import { useState, useCallback, useEffect, useRef, useMemo } from 'react'
import { isNativeOneclickDepositEnabled } from '../config/flags'
import { isAtomicSameChain } from '../utils/sameChainGate'
import { parseUnits } from 'viem'
import { useQuery } from '@tanstack/react-query'
import { AlertTriangle, WifiOff } from 'lucide-react'
import { floorToDecimals } from '@/utils/format'
import { log } from '@/utils/log'
import { cctpReverseSource } from '@/utils/cctpReverse'
import { deriveDestinationRecipient } from '@/utils/recipient'
import { Layout } from '@/components/Layout'
import { SwapCard } from '@/components/SwapCard'
import { computeDexDisplayTotals } from '@/components/FeeSection/dexTotal'
import { sameChainTokenBalanceGate, sameChainMinGate } from '@/utils/sameChainGate'
import { WalletModal } from '@/components/WalletModal'
import { ChainPickerModal } from '@/components/ChainPickerModal'
import { TokenSelectorModal } from '@/components/TokenSelectorModal'
import { RecipientModal } from '@/components/RecipientModal'
import { ProgressPanel } from '@/components/ProgressPanel'
import { ActivityDrawer, type ActivityTransfer } from '@/components/ActivityDrawer'
import { CancelReviewModal } from '@/components/CancelReviewModal'
import { FiatCurrencyModal, CURRENCIES } from '@/components/FiatCurrencyModal'
import { ReviewTransferModal } from '@/components/ReviewTransferModal'
import { RecipientRiskModal } from '@/components/RecipientRiskModal'
import {
  prefetchRecipientCode,
  type RecipientCodeRisk,
  type RecipientRiskConfirm,
} from '@/utils/recipientCodeGuard'
import { useWallet } from '@/hooks/useWallet'
import { useTronWallet } from '@/hooks/useTronWallet'
import { useBridge } from '@/hooks/useBridge'
import { useDexBridge, type DexBridgeStep } from '@/hooks/useDexBridge'
import { useCancelOrder } from '@/hooks/useCancelOrder'
import { cancelDisplayFields, generateCancelNonce, cancelDeadline } from '@/services/cancelAuth'
import { useThemeColors } from '@/hooks/useThemeColors'
import { useFiatPrices } from '@/hooks/useFiatPrices'
import { useServerStatus } from '@/contexts/ServerStatusContext'
import { balanceApi, bridgeApi, dexApi, referralApi } from '@/services/api'
import { getAllActiveDexOrders, getDexOrder, cleanupStaleDexOrders, type StoredDexOrder } from '@/utils/dexOrderStorage'
import { reconcileDexOrders } from '@/utils/reconcileDexOrders'
import { makeSteps, makeDexSteps, type ProgressStep, type StepFailureKind } from '@/utils/steps'
// DEV-375 — the render layer asks the SAME question `classifyError` asks, of the same display text.
// See the export's own comment for why this is a marker test and not a second error class.
import { isReceiptUnknownMessage } from '@/utils/errors'
import { dexEstimateMatchesDestDriver, dexEstimateMatchesAmount } from '@/components/FeeSection/dexTotal'
import { Near1ClickDepositPanel } from '@/components/Near1ClickDepositPanel'
import { TOKEN_PICKER_LIST, type TokenSelection } from '@/config/tokens'
import type { RouteConfig, DexEstimateResponse } from '@/services/api'
import { CCTP_CONFIG } from '@parse/shared'
import { storedOrderCancelControl, buildActiveDexCancelControl } from '@/utils/cancelEligibility'
import { BRIDGE_CHAINS, DEX_CHAINS, EVM_DEST_CHAINS, ALL_CHAINS, CHAIN_REGISTRY } from '@/config/chains'
import { FEESPLITTER_CHAINS, CHAIN_NAME_TO_ID } from '@/config/feeSplitter'
import { getFeeConfig } from '@/hooks/useFeeQuote'
import { FONT_BODY } from '@/config/theme'
import { isNativeMaxGasReserveEnabled, isFailureStepStateEnabled, isCrossChainFeeDisplayEnabled, isWalletTypeLabelEnabled, isPrequoteReceiveBlankEnabled } from '@/config/flags'
import { buildRecipientWalletLabel, walletLogoKind } from '@/utils/walletLabel'
import { useUI } from '@/hooks/useUI'
import type { Chain, ChainBalanceMap } from '@/types'

// Per-chain block-explorer base URLs (network-aware), keyed by OUR Chain names. Previously this derived the
// explorer from wagmi chain objects keyed by a normalised display name — but wagmi names like "Arbitrum One",
// "OP Mainnet", "BNB Smart Chain" (and testnet "Sepolia") don't normalise to our chain keys, so those chains
// silently got no "View on explorer" link. A direct map removes that fragility.
// Typed as Record<every EVM Chain> so adding a new EVM chain to the `Chain` union without an explorer entry
// is a COMPILE error — guaranteeing every route keeps its "View on explorer" link. (solana/tron handled below.)
// Feature flag — the NEAR 1Click "Deposit details" panel is hidden for now (kept in the tree for later).
// Flip to true to re-surface the deposit address / send amount / quote-deadline card on the Tron corridor.
const SHOW_ONECLICK_DEPOSIT_PANEL = false

const EVM_EXPLORERS: Record<Exclude<Chain, 'solana' | 'tron'>, { mainnet: string; testnet: string }> = {
  ethereum: { mainnet: 'https://etherscan.io', testnet: 'https://sepolia.etherscan.io' },
  base: { mainnet: 'https://basescan.org', testnet: 'https://sepolia.basescan.org' },
  optimism: { mainnet: 'https://optimistic.etherscan.io', testnet: 'https://sepolia-optimism.etherscan.io' },
  arbitrum: { mainnet: 'https://arbiscan.io', testnet: 'https://sepolia.arbiscan.io' },
  polygon: { mainnet: 'https://polygonscan.com', testnet: 'https://amoy.polygonscan.com' },
  bnb: { mainnet: 'https://bscscan.com', testnet: 'https://testnet.bscscan.com' },
  avalanche: { mainnet: 'https://snowtrace.io', testnet: 'https://testnet.snowtrace.io' },
  // Robinhood (#174) is mainnet-only; both entries point at the same Blockscout instance since a
  // testnet-build RH tx never occurs (RH is absent from every testnet chain set).
  robinhood: { mainnet: 'https://robinhoodchain.blockscout.com', testnet: 'https://robinhoodchain.blockscout.com' },
}

// Explorer URL helpers — direct per-chain map for EVM, manual for Solana/Tron.
function getExplorerUrl(txHash: string, chain: Chain | null): string {
  if (!chain) return '#'
  if (chain === 'solana') {
    const cluster = import.meta.env.VITE_NETWORK === 'mainnet' ? '' : '?cluster=devnet'
    return `https://explorer.solana.com/tx/${txHash}${cluster}`
  }
  if (chain === 'tron') {
    return import.meta.env.VITE_NETWORK === 'mainnet'
      ? `https://tronscan.org/#/transaction/${txHash}`
      : `https://nile.tronscan.org/#/transaction/${txHash}`
  }
  const exp = EVM_EXPLORERS[chain]
  if (exp) return `${exp[import.meta.env.VITE_NETWORK === 'mainnet' ? 'mainnet' : 'testnet']}/tx/${txHash}`
  return '#'
}

// Parse a timestamp for Activity sorting/display. SQLite's CURRENT_TIMESTAMP is UTC with NO timezone marker
// ("2026-06-28 22:14:34"); `new Date()` would parse that bare form as LOCAL time, skewing the epoch by the
// machine's UTC offset — which made backend-history rows sort/display hours off from localStorage rows. Coerce
// the bare DB form to UTC; pass ISO strings / epoch numbers through untouched.
function parseActivityTimestamp(v: string | number | null | undefined): number | undefined {
  if (v == null) return undefined
  if (typeof v === 'number') return v
  const s = String(v)
  const iso = /[TZ]|[+-]\d{2}:?\d{2}$/.test(s) ? s : s.replace(' ', 'T') + 'Z'
  const t = new Date(iso).getTime()
  return Number.isNaN(t) ? undefined : t
}

// Top-level error-banner selector (#182). The banner MUST track the error of whichever flow has an
// active order on screen — never the currently-SELECTED route's error, which is decoupled from the
// in-flight order. Binding to selection let a stale "Transaction cancelled by user" (a CCTP burn
// rejection) paint over a live/just-completed DEX order → a false-failure that invites a double-send.
//
// Keys on ORDER-ACTIVE (a progress panel exists), NOT merely in-flight: a DEX order stays
// dexStep !== 'idle' through its terminal 'complete'/'error' panel state, so the sibling CCTP error
// stays suppressed until the order is RESET (Start-New, which drops dexStep back to 'idle') — merely
// DISMISSING the panel hides it but leaves dexStep non-idle, so suppression persists past dismissal.
// Without this, the stale error would re-slam the instant the order terminalizes, over the
// "Swap Complete" panel (GATE-2 adversarial finding F1).
// The CCTP bridgeError and the DEX 'complete' state are STICKY (persist until reset()), so suppression
// spans the panel's whole lifetime. (A DEX 'error' auto-dismisses after ~15s, but useDexBridge's
// clearError zeroes dexError and dexStep TOGETHER, so the banner and the "Swap Failed" panel drop in
// the same tick — no stale-error window opens.)
//
// When NO order is active it falls back to the pre-#182 selection-driven binding, so a genuine,
// uncontradicted cancellation still shows (I2) and a no-route selection still shows nothing (I3).
// Pure + exported for the red→green closure test (BridgePage.errorBanner.test.tsx).
export function selectBannerError(a: {
  dexStep: DexBridgeStep
  burnTxHash: string | null
  isTransferring: boolean
  isDexRoute: boolean
  isCctpRoute: boolean
  dexError: string | null
  bridgeError: string | null
}): string | null {
  const dexOrderActive = a.dexStep !== 'idle' //  a DEX progress panel is on screen (in-flight OR terminal)
  const cctpOrderActive = !!a.burnTxHash || a.isTransferring // a CCTP progress panel is on screen
  if (dexOrderActive) return a.dexError // track the DEX order's OWN error (null while healthy) — I1
  if (cctpOrderActive) return a.bridgeError // track the CCTP transfer's OWN error — I1
  if (a.isDexRoute) return a.dexError // no active order → selection-driven, exactly as pre-#182 — I2
  if (a.isCctpRoute) return a.bridgeError
  return null // no route, no active order → no banner (CTA shows "Route Not Available") — I3
}

/**
 * #301a GATE-4 F1 — THE ONE PREDICATE BOTH CCTP SITES READ. Do not inline either half of it.
 *
 * A USDC→USDC pair is a CCTP corridor only when the chains DIFFER and BOTH have a deployed CCTP
 * contract. `BRIDGE_CHAINS` (`config/chains.ts:61`) is exactly ethereum/base/solana/arbitrum/polygon/
 * optimism — no robinhood, bnb, tron or avalanche.
 *
 * TWO call sites depend on this being the SAME condition, which is the whole reason it is one
 * function rather than two copies of the same boolean:
 *   1. `selectedRoute`'s SYNTHETIC fallback — it may only fabricate a `provider:'cctp'` row for a
 *      pair CCTP actually serves.
 *   2. the dest re-validation effect's EXEMPTION — it may only skip re-validating a pair for which
 *      that synthetic route will actually be produced.
 *
 * They WERE two copies, and the desync was the defect. `34d1593` added the `BRIDGE_CHAINS` guard to
 * (1) but left (2) asserting "USDC→USDC cross-chain … Always valid, never reset it" — true before
 * that commit, false after it. So a USDC holder opening the receive picker and tapping Robinhood got
 * no synthetic route (correct) AND no dest re-validation (wrong): the CTA read "Route Not Available",
 * `lastDestChain:'robinhood'` was persisted, and nothing auto-recovered — even though the live table
 * carries `base/USDC → robinhood/USDG` and `base/USDC → robinhood/ETH`, which the effect would have
 * landed on. Sharing the predicate means a future change to one side cannot silently leave the other
 * behind again.
 *
 * Blast radius of narrowing (2) to exactly (1)'s condition, replayed against the live 756-row table
 * (2026-08-05), over every USDC→USDC cross-chain pair: 30 BRIDGE_CHAINS×BRIDGE_CHAINS pairs stay
 * exempt and untouched (every real CCTP corridor); 34 bnb/tron pairs newly run the effect but return
 * at its own `dests.includes(selectedDestToken)` guard because the table really does carry USDC there
 * — unchanged; 8 `robinhood`-as-SOURCE pairs run and return at `dests.length === 0` — unchanged; the
 * 8 `→ robinhood` pairs are the fix, each landing USDG. Zero working corridors change dest token.
 */
function isSyntheticCctpPair(
  sourceChain: string,
  destChain: string,
  sourceToken: string,
  destToken: string,
): boolean {
  return (
    sourceToken === 'USDC' &&
    destToken === 'USDC' &&
    sourceChain !== destChain &&
    (BRIDGE_CHAINS as readonly string[]).includes(sourceChain) &&
    (BRIDGE_CHAINS as readonly string[]).includes(destChain)
  )
}

/**
 * #261 F9 — status → DEX phase, for the ProgressPanel opened from Activity.
 *
 * The live panel already has a `dexStep`; a drawer row has only a `status`, and it arrives from FIVE
 * sources (completed transfers, pending CCTP transfers, stored DEX orders, backend history, claim
 * history), so the input is a WIDER union than `DexOrderStatus` and must not be typed as one.
 *
 * The `'pending'`-with-a-hash arm is not redundant with the default: a naive `'pending' →
 * 'fetching_quote'` would REGRESS a backend-history row whose source tx is already on-chain (the same
 * panel renders its explorer link) to "Fetching Quote — active". Today's always-makeSteps path maps
 * it to `'burning'`, i.e. source-row active; keying on the hash preserves that.
 */
function drawerDexPhase(status: string, sourceTxHash?: string | null): string {
  switch (status) {
    case 'failed':
    case 'error':
    case 'mint_failed':
      return 'error'
    case 'complete':
    case 'mint_success':
    case 'recovered':
      return 'complete'
    case 'polling':
      return 'polling'
    case 'signed':
    case 'confirmed':
      return 'broadcasting'
    case 'pending':
      return sourceTxHash ? 'polling' : 'fetching_quote'
    default:
      return sourceTxHash ? 'polling' : 'fetching_quote'
  }
}

/**
 * #261 F9 (APPROACH 2) — the ONE place that answers "which step builder does this route get?".
 *
 * There are two step-building sites: the live panel and the panel opened from Activity. Bug #2 was
 * precisely that they disagreed — the live one branched on same-chain and called `makeDexSteps`, the
 * drawer one had no branch at all and always called `makeSteps`, so the same swap was narrated as a
 * cross-chain bridge ("Moving between chains", "Delivering to {chain}") the moment it was reopened.
 * Two independent argument lists is how that drift happened and how it would happen again; routing
 * both through this function means the CHOICE exists once even though the argument derivations
 * legitimately differ (the live site has a `dexStep`, the drawer site has only a `status`).
 *
 * Both argument objects are built by the caller and only one is used. They are plain object literals
 * over already-computed values — evaluating the unused one costs an allocation and has no effect.
 */
function buildRouteSteps(
  sameChain: boolean,
  sameChainArgs: Parameters<typeof makeDexSteps>[0],
  crossChainArgs: Parameters<typeof makeSteps>[0],
): ProgressStep[] {
  return sameChain ? makeDexSteps(sameChainArgs) : makeSteps(crossChainArgs)
}

/**
 * #261 F9 — display copy for the confirm row's sublabel on a drawer-opened same-chain swap.
 *
 * `makeDexSteps` renders whatever it is handed VERBATIM (steps.ts), and the live panel hands it
 * deBridge's own "Fulfilled"/"Created". Passing a drawer row's `status` straight through would print
 * the raw token "failed" as user-facing copy. `undefined` falls through to the builder's own
 * "Confirming swap" default.
 */
function drawerOrderSublabel(status: string): string | undefined {
  if (status === 'failed') return 'Swap failed'
  if (status === 'complete') return 'Swap complete'
  return undefined
}

/**
 * #261 F9 (I-4′) — the ONE terminal title, used by all three ProgressPanel mounts, for failure AND
 * success. Before this there were three independent expressions, so the same swap read "Swap Failed"
 * live and "Transfer Failed" from Activity — and a cross-chain DEX transfer read "Swap Failed" too,
 * because the live mount's Swap/Transfer flip only ever governed the IN-PROGRESS title.
 *
 * The two CHAINS are supplied by the caller — not a precomputed `sameChain` boolean — because the
 * comparison itself is the trap. Every mount reads a source that can be null/undefined, and
 * `null === null` is `true`, so a bare `a === b` at a call site answers "same chain" for a panel that
 * has no chains at all and labels a cross-chain failure "Swap Failed". Making the emptiness check part
 * of the helper means no call site can get it wrong: the live DEX mount's pre-F9 expression
 * (`selectedRoute?.sourceChain === selectedRoute?.destChain`, both `undefined` whenever no route is
 * selected) is exactly that mistake, and it is the drift I-4′ exists to remove.
 *
 * The mounts still choose WHICH pair to hand over, because they legitimately differ: the drawer knows
 * the transfer it reopened, the two live mounts know the transfer they ran.
 *
 * `legacy` is what the mount printed before F9 and is returned verbatim while the flag is off, so the
 * gate is a real gate (F-C) rather than a partial rollout.
 */
function terminalPanelTitle(
  outcome: 'complete' | 'failed' | 'unknown',
  atomicSameChain: boolean,
  legacy: string,
): string {
  // ── DEV-375 — the neutral THIRD title, and it is returned BEFORE the flag check on purpose. ────
  // `legacy` is "what this mount printed before #261 F9", and no mount ever printed anything for an
  // outcome that could not be rendered — so there is nothing to preserve here and no flag-off branch
  // to be faithful to. Gating it would leave "Transfer Failed" on screen for a delivered transfer on
  // the flag-OFF (production) build, which is the whole bug (decision P-B: both flag branches).
  //
  // Route-neutral for the same reason the copy is neutral: a swap and a bridge are equally
  // unconfirmed, and the Swap/Transfer split exists to describe the ROUTE, not the outcome.
  if (outcome === 'unknown') return 'Confirmation Unavailable'
  if (!isFailureStepStateEnabled()) return legacy
  // #614 — "Swap" describes an ATOMIC same-chain swap; a same-chain deposit-flow order (Tron
  // TRX↔USDT via 1Click) is a Transfer. The caller passes the threaded predicate.
  if (atomicSameChain) return outcome === 'complete' ? 'Swap Complete' : 'Swap Failed'
  return outcome === 'complete' ? 'Transfer Complete' : 'Transfer Failed'
}

/**
 * #261 F9 (I-3) — the error footer's button label. "Cancel" is what every mount showed pre-F9.
 *
 * ── DEV-375 ask (3): A BROADCAST HASH DEMOTES THE RESET, IN BOTH FLAG BRANCHES ───────────────────
 * `hashPresent` means a value-moving transaction is already on chain for this panel. Its primary
 * control may then not offer to RESET the form: `onStartNew` is `reset()`/`resetDex()` at both live
 * mounts, which clears the card — and with it the only on-screen reference to the in-flight hash —
 * while the transfer is still live. So the button becomes a plain "Close" and its handler is swapped
 * to `onDismiss` at the call site. Relabelling alone would not do: the label was never the hazard,
 * the handler was.
 *
 * Scoped to `'resets-the-form'` DELIBERATELY. The drawer mount's `onStartNew` is
 * `() => setProgressTransfer(null)` — it initiates neither a resubmit nor a reset, so I3 has nothing
 * to bite on there, and demoting it would change flag-OFF copy ("Cancel") for no safety gain. The
 * drawer mount is listed in SPACE B as a member that is in-scope-and-unchanged, not as one nobody
 * looked at.
 *
 * Applies on BOTH sides of `VITE_FAILURE_STEP_STATE` (decision P-B): the flag gates a per-step
 * RENDER experiment, not whether the app may offer a user a one-click re-spend.
 */
function startNewLabelFor(kind: 'dismiss-only' | 'resets-the-form', hashPresent = false): string {
  if (kind === 'resets-the-form' && hashPresent) return 'Close'
  if (!isFailureStepStateEnabled()) return 'Cancel'
  return kind === 'dismiss-only' ? 'Close' : 'Start over'
}

/**
 * #261 F9 (I-3) — the SUCCESS footer's button label, for the SAME `onStartNew` the error footer's
 * button fires. Same defect, same fix, one branch away: `ProgressPanel` hardcoded "Start new transfer"
 * there, so the drawer mount — whose `onStartNew` only calls `setProgressTransfer(null)` — offered to
 * start a transfer it cannot start, exactly as the error footer offered to cancel one it cannot cancel.
 *
 * "Start new transfer" is what EVERY mount showed pre-F9, so that is the whole flag-off branch (F-C) —
 * and it stays the flag-on label on the two live mounts, where `onStartNew` really does reset the form.
 */
function completeLabelFor(kind: 'dismiss-only' | 'resets-the-form'): string {
  if (!isFailureStepStateEnabled()) return 'Start new transfer'
  return kind === 'dismiss-only' ? 'Close' : 'Start new transfer'
}

/**
 * Every token the LIVE route table carries as a SOURCE on `chain`.
 *
 * SOURCE-ONLY BY CONSTRUCTION (#301a RE-ENTRY 3). This used to take a `side` and, for `'dest'`,
 * filter on `destChain` ALONE — which is the whole reason the dest arm of the chain picker was
 * deleted: a dest filter that cannot see the SOURCE half of the pair reports every dest token
 * reachable from ANY source, so it happily "validates" a pair no route carries. The parameter is
 * gone rather than left unused, so a future caller cannot reintroduce the dest reading.
 */
function routedSourceTokensOn(routes: RouteConfig[], chain: Chain): string[] {
  return routes.filter(r => r.sourceChain === chain).map(r => r.sourceToken)
}

/**
 * The STABLE tier's preference order for `chainPickFallbackToken`, stated EXPLICITLY.
 *
 * It used to be implicit — "the first non-native entry in `TOKEN_PICKER_LIST`" — and that list is
 * declared USDC-first (`config/tokens.ts:25`/`:26`/`:30` = USDC → USDT → USDG). So a stable holder
 * who picked Tron landed on `tron/USDC`, which `isTronUsdcCrossChain`
 * (`packages/shared/src/route-policy.ts:28`) refuses on EVERY cross-chain corridor. The live route
 * table is not a sufficient oracle against that: it still advertises 25 rows carrying tron USDC, so
 * the "is it routed?" check passes and the corridor is refused later, downstream.
 *
 * USDT-first also matches the #240 source effect's own stable ordering (`?? find('USDT') ??
 * find('USDC')`, below), so the two no longer disagree about which stable to land on.
 */
const STABLE_TIER_ORDER: string[] = ['USDT', 'USDC', 'USDG']

/**
 * #301a RE-ENTRY — what a SOURCE CHAIN PICK should leave selected, decided from the live route table.
 *
 * SOURCE-SIDE ONLY. The dest arm that used to call this was DELETED in RE-ENTRY 3: replayed against
 * the live 756-row table, all 32 regressions and all 44 picker-vs-effect disagreements originated
 * there, because `routedSourceTokensOn`'s old `'dest'` mode filtered on `destChain` alone and so
 * could not know the source half of the pair. The dest-revalidation effect (`:593`-`:616`) already
 * does know it, and is now the SOLE route-aware writer of dest-token state.
 *
 * The chain-picker `onSelect` used to gate its reset on the token's static `availableOn`, which is
 * wrong in BOTH directions on a launched corridor:
 *   - `USDC`/`USDT` declare no `availableOn` at all (they are the "all-chain" stables), so the guard
 *     never fired for them. A user holding the default USDC who picked Robinhood KEPT USDC — and
 *     Robinhood carries 0 of 756 live routes with USDC or USDT on either side (verified against prod
 *     `GET /api/dex/routes`, 2026-08-05; RH routes ETH and USDG only).
 *   - the fallback was the constant `'USDC'`, so when the guard DID fire (holding SOL, picking RH) it
 *     actively SET and PERSISTED that same zero-route pair.
 * The first path leaves the user on a dead pair; the second manufactures one.
 *
 * Returns the symbol to switch to, or `undefined` to LEAVE THE SELECTION ALONE. `undefined` is the
 * answer whenever the current token is genuinely routed on the picked chain: a deliberate, valid
 * choice must never be overwritten.
 *
 * SEPARATE-FIXER ROUND — CLASS-PRESERVING, not native-first-unconditionally. The prior version always
 * tried the chain's native before its stable, which on the verified live table (all nine chains route
 * their own native) made the stable tier below it dead code: a **USDC holder** who picked Robinhood
 * landed on **ETH** — a volatile asset — when `USDG` is routed there and is the landing that actually
 * preserves what the user was holding. The fix is to key the preference on the CLASS of
 * `currentToken`, not on the picked chain alone. Every candidate is still read out of `routes`, never
 * a hardcoded symbol:
 *   - current token is NATIVE (`TOKEN_PICKER_LIST` entry with `isNative: true`, e.g. SOL, ETH, BNB):
 *     1. the picked chain's own native, when routed there (#254's native-out preference)
 *     2. the picked chain's stable, in the explicit `STABLE_TIER_ORDER` above
 *     3. any routed token
 *   - current token is STABLE / non-native (USDC, USDT, USDG, or unrecognized): the same two tiers,
 *     STABLE FIRST:
 *     1. the picked chain's stable, in `STABLE_TIER_ORDER`
 *     2. the picked chain's own native, when routed there
 *     3. any routed token
 * The landing is therefore itself routed, which makes the handler idempotent — re-picking the same
 * chain returns `undefined` and writes nothing, so it cannot ping-pong with the revalidation effects.
 *
 * HOW THIS RELATES TO THE #240 SOURCE EFFECT (`:659`-`:685`) — CORRECTING A FALSE CLAIM. An earlier
 * version of this comment said the two orderings "agree". They do not, and did not; a reviewer
 * reading it was being told the opposite of the truth, so the claim is corrected here rather than
 * quietly dropped:
 *   - they agree ONLY when the current token is NATIVE. The #240 effect is native-first
 *     UNCONDITIONALLY — it never looks at the current token's class — so for a STABLE current token
 *     the two deliberately DIVERGE: this helper preserves the class, that effect does not. The
 *     divergence is the point of the class-preserving round (a USDC holder must not silently land on
 *     ETH); the effect's own native-first ordering on the NON-picker paths is filed as #309 and is
 *     explicitly not touched here.
 *   - within the stable tier they now MATCH (USDT before USDC). They did not before `STABLE_TIER_ORDER`
 *     existed — see that constant for the `tron/USDC` pair the old declaration-order read manufactured.
 */
function chainPickFallbackToken(
  routes: RouteConfig[],
  chain: Chain,
  currentToken: string,
): string | undefined {
  // The pre-#301a static rule, kept verbatim for the two no-evidence cases below.
  const staticFallback = (): string | undefined => {
    const def = TOKEN_PICKER_LIST.find(t => t.symbol === currentToken)
    return def?.availableOn && !def.availableOn.includes(chain) ? 'USDC' : undefined
  }
  // ROUTES NOT IN YET. `dexRoutes` is `useQuery(...).data ?? []` — `[]` until the request lands. With
  // no table there is no evidence to decide on, so behave EXACTLY as before this change rather than
  // resetting blindly: a pick during that window is no worse than it was, which is the bar here.
  if (routes.length === 0) return staticFallback()
  const routed = routedSourceTokensOn(routes, chain)
  // The table came back but carries no SOURCE row for this chain (partial/failed table). Same
  // reasoning as above — no evidence, so do not invent a landing.
  if (routed.length === 0) return staticFallback()
  if (routed.includes(currentToken)) return undefined
  const native = CHAIN_REGISTRY[chain]?.nativeCurrency
  const routedNative = native && routed.includes(native) ? native : undefined
  const routedStable =
    STABLE_TIER_ORDER.find(s => routed.includes(s))
    // Any OTHER non-native the table carries here still counts as a STABLE landing. There are none
    // today (`TOKEN_PICKER_LIST`'s non-natives are exactly the three in `STABLE_TIER_ORDER`), but
    // without this tail a stable added later — and not added to that constant — would push a stable
    // holder into a volatile native, which is the very failure the class-preserving rule exists for.
    ?? TOKEN_PICKER_LIST.find(t => !t.isNative && !STABLE_TIER_ORDER.includes(t.symbol) && routed.includes(t.symbol))?.symbol
  const currentIsNative = !!TOKEN_PICKER_LIST.find(t => t.symbol === currentToken)?.isNative
  // Class-preserving: a native holder lands on native-first; a stable holder lands on stable-first.
  // Falls through to "any routed token" only when neither tier the current class prefers exists.
  return currentIsNative
    ? routedNative ?? routedStable ?? routed[0]
    : routedStable ?? routedNative ?? routed[0]
}

export function BridgePage() {
  // Wallet hook (wagmi + solana adapter)
  const {
    evmAddress,
    solanaAddress,
    evmChainId,
    isEvmConnected,
    isSolanaConnected,
    // #261 F3 — the SELF-REPORTED connector/adapter names, for the recipient label and the SwapCard
    // brand icons. Display hints only: they are never compared, never signed, and never reach the
    // recipient derivation. Both already existed on the hook (useWallet.ts:11,:14 / :66,:69) and were
    // simply not destructured here.
    evmConnectorName,
    solanaWalletName,
  } = useWallet()

  // Tron wallet hook
  const {
    tronAddress,
    tronConnected,
    // #261 F3 — Tron's equivalent of the two names above; the adapter object, whose `.adapter.name`
    // is the self-reported wallet name (useTronWallet.ts:52).
    activeTronWallet,
  } = useTronWallet()

  // Bridge hook for bridge state and operations
  const {
    state: bridgeState,
    feeEstimate,
    isTransferring,
    transferStep,
    error: bridgeError,
    burnTxHash,
    burnError,
    mintTxHash,
    mintError,
    pendingTransfers,
    resumeTransfer,
    dismissTransfer,
    dismissAllTransfers,
    triggerRecoveryCheck,
    setSourceChain,
    setDestinationChain,
    setDestinationAddress,
    setAmount,
    swapDirection,
    estimateFees,
    executeBridge,
    reset,
  } = useBridge()

  // ============================================
  // DEX integration — hooks and state
  // ============================================
  const {
    currentStep: dexStep,
    transferId: dexTransferId,
    amount: dexSubmittedAmount,
    estimate: _dexEstimate,
    sourceTxHash: dexSourceTxHash,
    orderStatus: dexOrderStatus,
    bridgeResponse: dexBridgeResponse,
    error: dexError,
    // #261 F9 — the phase the live swap failed in, for the same-chain per-step render.
    failedStep: dexFailedStep,
    // DEV-375 finding #1 — WHICH leg failed (source vs downstream), for the cross-chain mapping below.
    failedLeg: dexFailedLeg,
    executeDexBridge,
    reset: resetDex,
    clearError: clearDexError,
    applyReconciledTerminal,
  } = useDexBridge()

  // Wallet-signed cancel for in-flight cross-chain orders (Batch B.1 PART 3).
  const { cancelOrder, cancelStates, resetCancelState } = useCancelOrder()
  // Cancel review modal (Batch B.1-polish ITEM 1). nonce + deadline are minted ONCE here when the review
  // opens and are reused for the signature, so what the user reviews ("Expires") is what they sign.
  const [cancelReview, setCancelReview] = useState<{ order: StoredDexOrder; nonce: string; deadline: number } | null>(null)
  const openCancelReview = useCallback((order: StoredDexOrder) => {
    resetCancelState(order.transferId)
    setCancelReview({ order, nonce: generateCancelNonce(), deadline: cancelDeadline() })
  }, [resetCancelState])

  // Auto-dismiss the DEX error banner after 15s so a transient failure (e.g. "fees exceed amount")
  // doesn't linger indefinitely. Re-arms on each new error; cleared on change/unmount.
  useEffect(() => {
    if (!dexError || isReceiptUnknownMessage(dexError)) return
    const t = setTimeout(() => clearDexError(), 15_000)
    return () => clearTimeout(t)
  }, [dexError, clearDexError])

  // Token selection state (restored from localStorage across refreshes)
  const [selectedSourceToken, setSelectedSourceToken] = useState(() =>
    localStorage.getItem('parsebridge:lastSourceToken') || 'USDC'
  )
  const [selectedDestToken, setSelectedDestToken] = useState(() =>
    localStorage.getItem('parsebridge:lastDestToken') || 'USDC'
  )
  const [isTokenSelectorOpen, setIsTokenSelectorOpen] = useState(false)
  const [tokenSelectorSide, setTokenSelectorSide] = useState<'source' | 'destination'>('source')

  // Fetch DEX routes (server-driven).
  //
  // #301a RE-ENTRY 3 — `isRoutesLoading` is the query's OWN first-load state, NOT `dexRoutes.length
  // === 0`. The two are different facts: an empty array is also what a genuinely empty (or failed,
  // or partial) table looks like AFTER the request lands, and gating a control on that would leave
  // it permanently unavailable instead of transiently pending. react-query v5's `isLoading` is
  // `isPending && isFetching` — true only while the FIRST fetch is in flight with no data — which is
  // exactly the cold-load window this gates.
  const { data: dexRoutes = [], isLoading: isRoutesLoading } = useQuery<RouteConfig[]>({
    queryKey: ['dex-routes'],
    queryFn: async () => {
      const res = await dexApi.getRoutes()
      return res.success && res.data ? res.data : []
    },
    // #614 (GATE-4 G4R2-4 / G4R3-1) — every PRE-SUBMIT same-chain seam now keys on this list's `provider`,
    // so after the operator's Phase-B row flip an already-open tab must converge on its own, not only on
    // its next focus/mount: a 60 s timer bounds the stale window the runbook states. The endpoint is a
    // synchronous route_configs read behind generalLimiter; one poll per minute per open tab is affordable.
    staleTime: 30_000,
    refetchInterval: 60_000,
  })

  // Route detection — find matching route for current token/chain selection
  const selectedRoute = useMemo((): RouteConfig | null => {
    if (!bridgeState.sourceChain || !bridgeState.destinationChain) return null

    // Check DEX routes first
    const dexRoute = dexRoutes.find(
      r =>
        r.sourceChain === bridgeState.sourceChain &&
        r.destChain === bridgeState.destinationChain &&
        r.sourceToken === selectedSourceToken &&
        r.destToken === selectedDestToken,
    )
    if (dexRoute) return dexRoute

    // Fall back to a SYNTHETIC CCTP route if both tokens are USDC and the chains differ — but ONLY
    // between two chains CCTP actually serves.
    //
    // #301a — this arm used to synthesise `provider:'cctp'` for ANY USDC→USDC cross-chain pair, with
    // no check that either chain has a CCTP deployment. `BRIDGE_CHAINS` (`config/chains.ts:61`, "chains
    // with deployed CCTP contracts") is exactly ethereum/base/solana/arbitrum/polygon/optimism — no
    // robinhood, bnb, tron or avalanche. So a USDC holder picking Robinhood as the receive chain was
    // handed a FABRICATED route the app then asserts is a valid bridge: `isCctpRoute` goes true, the
    // CCTP /quote/estimate fires for a chain Circle does not serve and errors "Invalid destination
    // chain: …" into a spurious banner — the exact failure the note at :547-549 says must never
    // happen — and the CTA reads "Bridge" for a corridor that cannot settle.
    //
    // THE ORDERING IS LOAD-BEARING. The real-row lookup above runs FIRST and returns unconditionally,
    // so this guard can only ever suppress the SYNTHETIC row; every genuine `dexRoutes` row still wins
    // exactly as before, including the live `provider:'cctp'` rows and the bnb/tron USDC→USDC
    // `near_1click`/`debridge_cross` rows. Verified against the live 756-row table (2026-08-05): the
    // guard suppresses 16 pairs, all Robinhood, and every one of them carries ZERO rows today; the 26
    // bnb/tron pairs it would otherwise block are all covered by a real row that wins first; 0 pairs
    // that work today stop working. It also closes the cold-load/failed-fetch window, where an empty
    // `dexRoutes` meant EVERY unsupported pair fell through to the fabricated route.
    //
    // When the guard fails there is deliberately NO new error path — fall through to `null`, which is
    // already what an unroutable selection produces ("Route Not Available", :713).
    //
    // GATE-4 F1 — the condition itself now lives in `isSyntheticCctpPair` (module scope), because the
    // dest re-validation effect's exemption MUST be the same condition and was silently left behind
    // when this guard was added. Semantics here are byte-for-byte what they were; only the location
    // moved. See that function's docblock.
    if (
      isSyntheticCctpPair(
        bridgeState.sourceChain,
        bridgeState.destinationChain,
        selectedSourceToken,
        selectedDestToken,
      )
    ) {
      return {
        sourceChain: bridgeState.sourceChain,
        destChain: bridgeState.destinationChain,
        sourceToken: 'USDC',
        destToken: 'USDC',
        provider: 'cctp',
        affiliateFeeBps: 0,
      }
    }

    return null
  }, [bridgeState.sourceChain, bridgeState.destinationChain, selectedSourceToken, selectedDestToken, dexRoutes])

  const isDexRoute = selectedRoute != null && selectedRoute.provider !== 'cctp'
  // CCTP only supports its own chains (no bnb/tron). Fire the CCTP /quote/estimate ONLY for a
  // real CCTP route — never for a DEX route, and never for an invalid/no-route selection (which
  // would error "Invalid destination chain: …" and surface a spurious "unknown error" banner).
  const isCctpRoute = selectedRoute?.provider === 'cctp'
  const isDexTransferring = dexStep !== 'idle' && dexStep !== 'complete' && dexStep !== 'error'
  // CCTP counterpart of isDexTransferring: true while a CCTP transfer is actively in-flight and NOT terminal.
  // Note isTransferring (== burn.isBurning) is TRUE only during the burn phase — after the burn tx is submitted
  // the CTA re-enables (see useBridge.ts "user can start new transfer after burn is submitted") even though the
  // transfer is still cross-chain in-flight (attesting/minting). So "in-flight" must also cover burnTxHash-set-
  // but-not-terminal, else a sibling reset (below) would wipe a live post-burn transfer's progress tracking.
  const isCctpInFlight = isTransferring || (!!burnTxHash && transferStep !== 'complete' && transferStep !== 'error')

  // Reverse input: user types in receive field → source auto-fills
  const [inputMode, setInputMode] = useState<'send' | 'receive'>('send')
  const [receiveTarget, setReceiveTarget] = useState('')
  const bumpedRef = useRef(false)
  // Monotonic counter to discard stale DEX reverse-quote responses (race guard).
  const reverseSeqRef = useRef(0)

  /**
   * #336 R9 — a receive target belongs to the ROUTE it was quoted against, and to nothing else.
   *
   * `setInputMode`/`setReceiveTarget` are written at only two places, both inside the two amount-field
   * handlers (:2207, :2213-2214). Neither `handleTokenSelect` nor `handleSwapDirection` reset them, so
   * a reverse-quoted target OUTLIVED its route — permanently, not for a debounce window: SwapCard's
   * receive-field sync keys on `[estimatedReceived]`, which does not change when the dest TOKEN does,
   * and its `lastDispatchedRecvRef` (SwapCard/index.tsx:351) already equals the old target, so nothing
   * downstream re-syncs it either.
   *
   * REPRO: reverse-quote base USDC → optimism USDT at receive 1000 (the source settles ~1005.32, fee
   * correct). Switch the dest token to ETH. `receiveTarget` is still '1000' and the source strings
   * still match, so `dexEstimateMatchesDestDriver` PASSES and the fee renders as bps × 1000 — for 1000
   * ETH. The staleness guard added by R3/R7 is not wrong here; it is answering a narrower question
   * ("was this bps quoted for this amount?") than the one that matters ("for this ROUTE?").
   *
   * The reset is deliberately at the ROUTE level rather than threaded through that predicate: it also
   * fixes the receive FIELD, which is the larger lie (the field itself displayed a figure belonging to
   * the previous pair), and a predicate that has to know about route identity is a second place for
   * the two to fall out of step.
   *
   * FIRST MOUNT IS A NO-OP BY CONSTRUCTION: the ref is seeded with the first render's identity, so the
   * body cannot run before the user has changed something. (Both writes are to the initial values, so
   * React would bail out anyway — but "harmless by accident" is not "cannot happen", and this is a
   * money-display path.)
   *
   * DELIBERATELY NOT TOUCHED: `bridgeState.amount`. Clearing the SEND field on a token switch is a
   * separate product decision, not this fix.
   */
  const routeIdentity = `${selectedSourceToken}|${selectedDestToken}|${bridgeState.sourceChain}|${bridgeState.destinationChain}`
  const lastRouteIdentityRef = useRef(routeIdentity)
  useEffect(() => {
    if (lastRouteIdentityRef.current === routeIdentity) return
    lastRouteIdentityRef.current = routeIdentity
    setInputMode('send')
    setReceiveTarget('')
    bumpedRef.current = false
    // Discard any reverse quote still IN FLIGHT for the route just left: its `.then` would otherwise
    // write a source amount sized for a pair the user is no longer on (`setAmount`, :2255). Same
    // mechanism the keystroke race-guard uses (:2234) — fail CLOSED, never a figure for the wrong pair.
    reverseSeqRef.current++
  }, [routeIdentity])

  // Debounce the amount feeding the DEX estimate so a burst of keystrokes collapses to one request
  // (the /estimate query is otherwise unthrottled). ~500ms mirrors the CCTP estimate path.
  const [debouncedDexAmount, setDebouncedDexAmount] = useState(bridgeState.amount)
  useEffect(() => {
    const t = setTimeout(() => setDebouncedDexAmount(bridgeState.amount), 500)
    return () => clearTimeout(t)
  }, [bridgeState.amount])

  // DEX fee estimate (for deBridge routes — Jupiter skips estimate)
  const { data: dexFeeEstimate, isFetching: isDexEstimateFetching, dataUpdatedAt: dexEstimateUpdatedAt } = useQuery<DexEstimateResponse | null>({
    queryKey: ['dex-estimate', bridgeState.sourceChain, bridgeState.destinationChain, selectedSourceToken, selectedDestToken, debouncedDexAmount, tronAddress],
    queryFn: async () => {
      if (!isDexRoute || !selectedRoute) return null
      const amountNum = parseFloat(debouncedDexAmount || '0')
      if (amountNum <= 0) return null
      const res = await dexApi.getEstimate({
        sourceChain: selectedRoute.sourceChain,
        destChain: selectedRoute.destChain,
        sourceToken: selectedRoute.sourceToken,
        destToken: selectedRoute.destToken,
        amount: debouncedDexAmount,
        // Tron source: pass the wallet so the backend reads the user's energy for an accurate burn cost.
        ...(selectedRoute.sourceChain === 'tron' && tronAddress ? { senderAddress: tronAddress } : {}),
      })
      if (res.success && res.data) return res.data
      // 1Click HARD below-minimum: the backend returns a 400 carrying the route floor (minAmountUsd) instead
      // of a quote. Surface it as a belowMinimum MARKER so opexGate can disable the CTA (parity with deBridge's
      // 200-with-large-opex below-min). Every OTHER 400 (missing params / token-not-found / native-dest) has
      // no minAmountUsd → null, unchanged. The marker deliberately carries no quote fields; consumers gate on
      // `belowMinimum` before reading them (hence the sentinel cast).
      if (res.minAmountUsd && res.minAmountUsd > 0) {
        return { belowMinimum: true, minAmountUsd: res.minAmountUsd } as unknown as DexEstimateResponse
      }
      // #614 — a SAME-CHAIN 1Click cell (Tron TRX↔USDT) that 1Click cannot serve right now answers a
      // structured 503 (fail closed, no deBridge). Surface the server's sentence on the CTA through the
      // routeUnsupported marker channel (the CTA prints `message` verbatim) instead of the generic
      // "Fee unavailable"; `routeUnavailable` keeps the 8 s retry alive and blanks the receive.
      if (res.error?.code === 'SAME_CHAIN_ROUTE_UNAVAILABLE') {
        return {
          routeUnavailable: true,
          routeUnsupported: true,
          routeUnsupportedCode: 'SAME_CHAIN_ROUTE_UNAVAILABLE',
          routeUnsupportedReason: res.error.message,
        } as unknown as DexEstimateResponse
      }
      // #614 (GATE-4 G4R2-1) — the provider's minimum for a same-chain 1Click cell sits ABOVE our tier right
      // now (1Click's TRX-side floor is a TRX quantity; a TRX rally lifts it). The API deliberately names no
      // number; the CTA prints the server's sentence. TERMINAL like belowMinimum (no `routeUnavailable`, so
      // the 8 s poll stops — the floor moves slowly and the query re-keys on the next amount the user types).
      if (res.error?.code === 'PROVIDER_MIN_ABOVE_TIER') {
        return {
          routeUnsupported: true,
          routeUnsupportedCode: 'PROVIDER_MIN_ABOVE_TIER',
          routeUnsupportedReason: res.error.message,
        } as unknown as DexEstimateResponse
      }
      return null
    },
    enabled: isDexRoute && !!selectedRoute && parseFloat(debouncedDexAmount || '0') > 0,
    staleTime: 30_000,
    // Don't re-hit /estimate on tab-back — the 30s staleTime / 8s cold-retry already keep it fresh,
    // and a focus refetch needlessly re-bills the quote path for non-converting tab switches.
    refetchOnWindowFocus: false,
    // Cold corridors return null until the fee-oracle warms the cache (~1 poll cycle). Retry every
    // 8s WHILE null so a freshly-opened route fills in within ~8s instead of waiting the 30s
    // staleTime; stop polling once we have an estimate.
    // #614 — a transient same-chain 1Click refusal is data (so the CTA can show its sentence) but must
    // keep polling like a cold corridor, so the button recovers on its own when 1Click is back.
    refetchInterval: (query) => (query.state.data && !(query.state.data as { routeUnavailable?: boolean }).routeUnavailable ? false : 8000),
  })

  // Keep the selected DEST token valid for the current corridor. Connecting a Solana/Tron wallet
  // (or switching the receive chain) changes destinationChain but leaves the old token selected —
  // e.g. "BNB" lingers after switching to Tron, producing an invalid "base ETH → tron BNB" combo
  // that reads as "Route Not Available". When the current dest token has no route from the current
  // source on the new chain, reset it to a sensible default (prefer a stablecoin).
  useEffect(() => {
    const { sourceChain, destinationChain } = bridgeState
    if (!sourceChain || !destinationChain) return
    // CCTP-CORRIDOR EXEMPTION — narrowed at GATE-4 F1, and deliberately expressed as the SAME
    // predicate `selectedRoute`'s synthetic arm uses (`isSyntheticCctpPair`, module scope). The two
    // must stay in step: this may only skip re-validation for a pair that the synthetic arm will
    // actually produce a route for. It previously read "USDC→USDC cross-chain … Always valid, never
    // reset it", which stopped being true the moment `34d1593` added the `BRIDGE_CHAINS` guard over
    // there — leaving USDC→USDC to a NON-CCTP chain with no synthetic route AND no re-validation, i.e.
    // a persisted "Route Not Available" nothing recovers. Now: a real CCTP corridor is still exempt
    // (untouched, exactly as before); anything else falls through and is re-validated against
    // `dexRoutes` below, which is what lands `→ robinhood` on its genuinely routed USDG.
    if (isSyntheticCctpPair(sourceChain, destinationChain, selectedSourceToken, selectedDestToken)) return
    const dests = dexRoutes
      .filter(r => r.sourceChain === sourceChain && r.sourceToken === selectedSourceToken && r.destChain === destinationChain)
      .map(r => r.destToken)
    // No route at all from this source to this chain → leave as-is (CTA shows "Route Not Available").
    if (dests.length === 0 || dests.includes(selectedDestToken)) return
    // #254 — when the source is a native token, a native sender switching the dest chain most
    // likely wants native-out too, so prefer the dest chain's native BEFORE the stablecoin default —
    // but only when that corridor actually exists in `dests` (else fall back exactly as before).
    // Stablecoin sources are unaffected: `srcIsNative` is false, so the USDT→USDC→first order stands.
    const srcIsNative = !!TOKEN_PICKER_LIST.find(t => t.symbol === selectedSourceToken)?.isNative
    const destNative = CHAIN_REGISTRY[destinationChain as Chain]?.nativeCurrency
    const next =
      (srcIsNative && destNative ? dests.find(t => t === destNative) : undefined)
      ?? dests.find(t => t === 'USDT')
      ?? dests.find(t => t === 'USDC')
      ?? dests[0]
    if (next) setSelectedDestToken(next)
  }, [bridgeState.sourceChain, bridgeState.destinationChain, selectedSourceToken, selectedDestToken, dexRoutes])

  // #240 — INVARIANT: the selected SOURCE token must exist on the chain it is selected for, after
  // EVERY network change. Not sixteen patches.
  //
  // Sixteen call sites move sourceChain/destinationChain — balance smart-select (`:1041`/`:1044`),
  // the localStorage restore (`:1074`-`:1084`), wallet-family select (`:1720`-`:1732`), the token
  // picker's chain pin (`:1764`-`:1784`), swap-direction, and the two chain-picker onSelects — and
  // only the two chain-picker onSelects (`:2755`/`:2765`) re-validated. So restoring
  // `lastSourceToken:'SOL'` beside `lastSourceChain:'base'`, or connecting an EVM wallet with SOL
  // selected, left a combo that cannot route: the CTA reads "Route Not Available" and nothing tells
  // the user which half is wrong.
  //
  // This observes the RESULT (the chain,token pair) instead of each cause, so it covers every call
  // site that exists today AND every one added later — which is the whole reason it is not written
  // as a reset at each site.
  //
  // ROUND-2 FIX — SOURCE ONLY, NO DEST ARM HERE. This effect used to also carry a dest arm with an
  // IDENTICAL dependency array to the dest effect above (`:467`-`:490`), racing it on every commit.
  // Being declared second, its blunt `'USDC'` landed LAST and overwrote effect 1's route-aware
  // choice — e.g. switching the destination to Tron used to correctly land on USDT (the working
  // corridor); this effect then stomped it back to USDC, which `isTronUsdcCrossChain` refuses. The
  // dest arm added no coverage of its own (effect 1 already observes the same (chain,token) result
  // for every one of the sixteen sites) — only a second writer. Deleting it makes effect 1 the SOLE
  // writer of dest-token state again, structurally, not by execution-order luck.
  //
  // ROUTE-AWARE FALLBACK — NOT A BLIND 'USDC'. A bare `'USDC'` constant is wrong on its own terms:
  // Tron cross-chain refuses it, and Robinhood does not carry it AT ALL (USDG + native ETH only —
  // config/tokens.ts:30/33), so the invariant's own fallback could violate the invariant it states.
  // Preference order mirrors effect 1's dest fallback and #254's native-out preference: this chain's
  // native, then USDT, then USDC, then whatever the live route table actually offers as a source
  // token here — each candidate is gated on `dexRoutes` actually carrying it, so the fallback can
  // never itself land on a combo nothing routes.
  //
  // SKIPPED WHILE dexRoutes IS EMPTY. `dexRoutes` is `useQuery(...).data ?? []` (see the routes
  // query above) — `[]` until the request lands. Writing during that window would reset a token the
  // not-yet-arrived route table would have validated, with no restore path.
  //
  // NO localStorage WRITE. The fallback is a transient IN-SESSION correction, not a new saved
  // preference. Persisting it would let it survive a reload and, combined with the known-open #308
  // (the restore validates the saved CHAIN against `BRIDGE_CHAINS`, which excludes bnb/tron/
  // robinhood), permanently overwrite a legitimate saved token a later #308 fix could otherwise have
  // restored.
  useEffect(() => {
    const { sourceChain } = bridgeState
    if (!sourceChain) return
    if (dexRoutes.length === 0) return

    const chain = sourceChain as Chain

    // Valid unless the token declares a restricted `availableOn` that excludes this chain. USDC and
    // USDT carry no `availableOn` at all (all-chain), which is also what makes 'USDC' a TERMINATING
    // fallback candidate — the fallback value can never itself trip this predicate.
    const isValidOn = (token: string): boolean => {
      const def = TOKEN_PICKER_LIST.find(t => t.symbol === token)
      if (!def?.availableOn) return true
      if (def.availableOn.includes(chain)) return true
      return dexRoutes.some(r => r.sourceChain === chain && r.sourceToken === token)
    }
    if (isValidOn(selectedSourceToken)) return

    const routedSourceTokens = dexRoutes.filter(r => r.sourceChain === chain).map(r => r.sourceToken)
    const native = CHAIN_REGISTRY[chain]?.nativeCurrency
    const next =
      (native && routedSourceTokens.includes(native) ? native : undefined)
      ?? routedSourceTokens.find(t => t === 'USDT')
      ?? routedSourceTokens.find(t => t === 'USDC')
      ?? routedSourceTokens[0]
    if (next) setSelectedSourceToken(next)
  }, [bridgeState.sourceChain, selectedSourceToken, dexRoutes])

  // #614 (reviewer B7) — a reverse-quote (receive-mode) refusal on a same-chain 1Click cell arrives with
  // the send field cleared, so no forward estimate exists to carry the SAME_CHAIN_ROUTE_UNAVAILABLE
  // marker; the reverse path records its own. Cleared on the next reverse call, on any corridor change,
  // and as soon as a forward estimate lands (the user typed a send amount, superseding the reverse).
  const [reverseRouteUnavailable, setReverseRouteUnavailable] = useState<string | null>(null)
  useEffect(() => { setReverseRouteUnavailable(null) }, [bridgeState.sourceChain, bridgeState.destinationChain, selectedSourceToken, selectedDestToken])
  useEffect(() => { if (dexFeeEstimate) setReverseRouteUnavailable(null) }, [dexFeeEstimate])

  /**
   * #614 — THE same-chain predicate for this page. TRUE only for an ATOMIC same-chain swap (deBridge /
   * Jupiter: one wallet tx). A same-chain `near_1click` order (Tron TRX↔USDT) is a deposit → solver-fill
   * flow: it takes the cross-chain template, the wider delivery estimate, the cross-chain fee gates and
   * a real "Received" leg. Every site below that used to read `sourceChain === destinationChain` reads
   * this instead. A route we cannot classify (no route selected, or a legacy row with no provider) counts
   * as ATOMIC per G6 — the fail-closed edge is a row KNOWN to be near_1click.
   */
  const atomicSameChain = useMemo(
    // PRE-SUBMIT seams read the corridor ON SCREEN (the selected route). GATE-4 R1A-2: the hook keeps
    // `bridgeResponse` until the next submit/reset, so keying these seams on the executed order's provider
    // would price a freshly-picked corridor on the PREVIOUS order's rail (fee netting, gates, min hints,
    // Max reserve, the review modal). Phase A no longer needs the response here: GET /dex/routes carries
    // the preview overlay, so the selected route already reads the previewed provider on beta.
    () => isAtomicSameChain(bridgeState.sourceChain, bridgeState.destinationChain, selectedRoute?.provider),
    [bridgeState.sourceChain, bridgeState.destinationChain, selectedRoute?.provider],
  )
  // The EXECUTED order's own predicate: the /bridge response outranks the picker for everything that
  // describes the transfer that RAN — the live panel's template, title and hop copy, and the completed
  // Activity row. Never for a pre-submit seam (see above).
  const executedAtomicSameChain = useMemo(
    () => isAtomicSameChain(bridgeState.sourceChain, bridgeState.destinationChain, dexBridgeResponse?.provider ?? selectedRoute?.provider),
    [bridgeState.sourceChain, bridgeState.destinationChain, dexBridgeResponse?.provider, selectedRoute?.provider],
  )

  // CTA label logic
  const ctaLabel = useMemo(() => {
    if (!selectedRoute) return 'Route Not Available'
    if (selectedRoute.provider === 'cctp') return 'Bridge'
    // #614 — a deposit-address rail is a bridge-shaped flow even on one chain.
    if (selectedRoute.provider === 'near_1click') return 'Bridge & Swap'
    if (selectedRoute.sourceChain === selectedRoute.destChain) return 'Swap'
    return 'Bridge & Swap'
  }, [selectedRoute, bridgeState.sourceChain, bridgeState.destinationChain])

  // DEX order rehydration from localStorage
  const [storedDexOrders, setStoredDexOrders] = useState<StoredDexOrder[]>([])

  useEffect(() => {
    cleanupStaleDexOrders()
    setStoredDexOrders(getAllActiveDexOrders())
  }, [])

  // Refresh stored orders when DEX step changes to complete or error
  useEffect(() => {
    if (dexStep === 'complete' || dexStep === 'error') {
      setStoredDexOrders(getAllActiveDexOrders())
    }
  }, [dexStep])

  // Reconcile persisted, non-terminal DEX orders against the backend. The in-flight polling in
  // useDexBridge only tracks the CURRENT order and stops on unmount/navigation — so a cross-chain
  // order that fulfilled after the user left the page would otherwise show "In progress" forever in
  // Activity. On mount and on an interval, re-poll each unsettled order's status and mark it complete
  // once the backend reports fulfillment. An order that 404s (unknown to the backend — e.g. a reset
  // DB, or a same-chain swap that lost its terminal state) is marked failed so it STOPS being polled
  // instead of hot-looping the endpoint forever (issue #64). Per-cycle logic lives in
  // reconcileDexOrders so it can be unit-tested without a live interval.
  // Latest live-order snapshot read by the durable reconcile loop below, whose interval closure is created
  // ONCE (empty-dep effect). Refs avoid re-subscribing the interval every time the tracked order advances.
  const dexTransferIdRef = useRef(dexTransferId)
  dexTransferIdRef.current = dexTransferId
  const dexStepRef = useRef(dexStep)
  dexStepRef.current = dexStep
  // DEV-375 finding #8 — a receipt-unknown live view sets `dexStep:'error'`, not 'polling' (see
  // `dexReceiptUnknown` below), so without this the reconcile loop's gate could never call in to
  // correct it once the backend later reports the truth, in EITHER direction.
  const dexErrorRef = useRef(dexError)
  dexErrorRef.current = dexError

  useEffect(() => {
    let cancelled = false
    const reconcile = async () => {
      const changed = await reconcileDexOrders({ getOrderStatus: dexApi.getOrderStatus })
      if (cancelled) return
      if (changed) setStoredDexOrders(getAllActiveDexOrders())
      // Durable completion for the LIVE order (near_1click especially). The in-session poll in useDexBridge
      // dies when the tab is suspended (mobile) or navigated, but a 1Click fill can land minutes later; this
      // reconcile loop is the durable path. When it settles the order we're STILL showing as 'polling', mirror
      // that terminal into the live view so the progress panel flips to complete/error and the spinner stops.
      // Flipping dexStep→'complete' also drives the existing completion effect (balances + Activity refresh).
      // Guarded to the tracked, still-polling order so it can never resurrect a reset flow or double-fire once
      // the in-session poll has already terminalized.
      const liveId = dexTransferIdRef.current
      // DEV-375 finding #8 — widened from `dexStepRef.current === 'polling'` alone: a receipt-unknown
      // live view is `dexStep:'error'` with a marker-bearing message (I10), and it must be correctable
      // to EITHER terminal once the backend reports it, not left stuck once softened to UNKNOWN. A
      // genuinely-failed/complete live view is unaffected — `isReceiptUnknownMessage` is false for it.
      const isCorrectableLiveView = dexStepRef.current === 'polling' ||
        (dexStepRef.current === 'error' && isReceiptUnknownMessage(dexErrorRef.current))
      if (liveId && isCorrectableLiveView) {
        const tracked = getDexOrder(liveId)
        if (tracked?.orderStatus === 'complete') applyReconciledTerminal(liveId, 'complete')
        else if (tracked?.orderStatus === 'failed') applyReconciledTerminal(liveId, 'error')
      }
    }

    reconcile()
    const id = setInterval(reconcile, 15_000)
    return () => { cancelled = true; clearInterval(id) }
  }, [applyReconciledTerminal])

  // Wallet modal state
  const [modalTarget, setModalTarget] = useState<'source' | 'destination' | null>(null)
  const [modalChainFamily, setModalChainFamily] = useState<'evm' | 'solana' | 'tron' | 'all'>('evm')
  const [autoOpenedEvmDestPicker, setAutoOpenedEvmDestPicker] = useState(false)
  const [autoOpenedEvmSourcePicker, setAutoOpenedEvmSourcePicker] = useState(false)
  const isWalletModalOpen = modalTarget !== null

  // Chain picker state
  const [chainPicker, setChainPicker] = useState<'send' | 'receive' | null>(null)

  // All-chain balances (replaces per-chain sourceBalance/destBalance)
  const [allBalances, setAllBalances] = useState<ChainBalanceMap>({})
  const [isBalancesLoading, setIsBalancesLoading] = useState(false)
  // GATE-4 fix (finding 2) — did the load that most recently cleared `isBalancesLoading` actually
  // succeed? Threaded to SwapCard so its established-absent latch does not treat a completed-but-
  // FAILED first load the same as a completed-and-empty one. Starts `true` (no load has failed yet).
  const [lastBalanceLoadSucceeded, setLastBalanceLoadSucceeded] = useState(true)

  // Fee config (for minAmountByDest)
  const [minAmountByDest, setMinAmountByDest] = useState<Record<string, number>>({})
  // Backend config base minimum-to-send (CCTP MIN_AMOUNT, ~$1). The fallback floor for sendMinUsd
  // when no live route quote exists (e.g. while picking a chain). Sourced from /quote/config, never
  // hardcoded, so the chain-picker gate and the swap-card min hint share ONE origin (#27).
  const [configMinUsd, setConfigMinUsd] = useState<number>(1)
  // #60: the #28 platform fee floor (net ~$0.10). Added to the raw contract min in the pre-quote
  // fallback so the displayed minimum is fee-inclusive (~$1.10). Approximate "for now" — the live
  // quote replaces it with the exact fee-aware min (minSourceUsd) once an amount is entered.
  const PLATFORM_MIN_FEE_USD = 0.10

  // RecipientModal state
  const [isRecipientModalOpen, setIsRecipientModalOpen] = useState(false)

  // Activity drawer state
  const [activityOpen, setActivityOpen] = useState(false)
  // The transfer whose ProgressPanel is shown inline (opened by clicking a row in the Activity drawer).
  const [progressTransfer, setProgressTransfer] = useState<ActivityTransfer | null>(null)
  const selectedProgressRef = useRef<HTMLDivElement>(null)

  // Review transfer modal state
  const [isReviewOpen, setIsReviewOpen] = useState(false)

  // Progress panel dismiss state (mutual-exclusion with SwapCard)
  const [progressDismissed, setProgressDismissed] = useState(false)
  /**
   * DEV-375 — ONE stable dismiss handler, shared by both live mounts' `onDismiss` AND by the
   * hash-present `onStartNew`.
   *
   * A second `() => setProgressDismissed(true)` arrow would behave identically at runtime and be a
   * different function object, which makes "the reset was DEMOTED, not merely relabelled" impossible
   * to assert from outside the component — a "Close" button still wired to `reset()` would pass every
   * label check. Sharing the reference makes the demotion checkable by identity.
   */
  const dismissLiveProgress = useCallback(() => setProgressDismissed(true), [])
  const hasActiveTransfer = !!(burnTxHash || isTransferring) || (dexStep !== 'idle')

  // Current-session completed transfers (survives progress panel dismiss, cleared on page reload)
  const [completedTransfers, setCompletedTransfers] = useState<ActivityTransfer[]>([])

  // Capture CCTP transfer completion into activity list + refresh balances
  useEffect(() => {
    if (transferStep === 'complete' && burnTxHash && bridgeState.sourceChain && bridgeState.destinationChain) {
      setCompletedTransfers(prev => {
        if (prev.some(t => t.burnTxHash === burnTxHash)) return prev
        return [{
          id: burnTxHash,
          amount: bridgeState.amount,
          sourceToken: selectedSourceToken,
          destToken: selectedDestToken,
          sourceChain: bridgeState.sourceChain!,
          destinationChain: bridgeState.destinationChain!,
          status: 'complete',
          burnTxHash,
          mintTxHash: mintTxHash ?? null,
          timestamp: Date.now(),
        }, ...prev].slice(0, 50)
      })
      fetchAllBalances()
    }
  }, [transferStep, burnTxHash]) // eslint-disable-line react-hooks/exhaustive-deps

  // Capture DEX transfer completion into activity list + refresh balances
  useEffect(() => {
    if (dexStep === 'complete' && dexSourceTxHash && bridgeState.sourceChain && bridgeState.destinationChain) {
      setCompletedTransfers(prev => {
        if (prev.some(t => t.burnTxHash === dexSourceTxHash)) return prev
        return [{
          id: dexSourceTxHash,
          amount: dexSubmittedAmount || bridgeState.amount,
          sourceToken: selectedSourceToken,
          destToken: selectedDestToken,
          sourceChain: bridgeState.sourceChain!,
          destinationChain: bridgeState.destinationChain!,
          status: 'complete',
          burnTxHash: dexSourceTxHash,
          // #614 — the rail, so Activity and the reopened panel can tell an atomic swap from a
          // same-chain deposit flow without guessing from the chains. The EXECUTED order's provider (the
          // /bridge response) outranks the picker (reviewer B5) — same source as `executedAtomicSameChain`.
          provider: dexBridgeResponse?.provider ?? selectedRoute?.provider ?? null,
          // ATOMIC same-chain swaps settle in ONE tx — the dest tx IS the swap tx, so the dest
          // "View on explorer" links it immediately. Cross-chain AND same-chain deposit-flow orders
          // (#614) leave mintTxHash null until the fill is recovered — the user's own deposit tx must
          // never be presented as the delivery.
          mintTxHash: executedAtomicSameChain ? dexSourceTxHash : null,
          timestamp: Date.now(),
        }, ...prev].slice(0, 50)
      })
      fetchAllBalances()
    }
  }, [dexStep, dexSourceTxHash]) // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-show progress on terminal state
  useEffect(() => {
    if (transferStep === 'complete' || transferStep === 'error' ||
        dexStep === 'complete' || dexStep === 'error') {
      setProgressDismissed(false)
    }
  }, [transferStep, dexStep])

  // Reset dismissed when new transfer starts
  useEffect(() => {
    if (isTransferring || dexStep === 'signing' || dexStep === 'broadcasting') {
      setProgressDismissed(false)
    }
  }, [isTransferring, dexStep])

  // Backend transfer history (persists across reloads — fetched by wallet address)
  const [backendHistory, setBackendHistory] = useState<ActivityTransfer[]>([])

  useEffect(() => {
    // Query history for ALL connected wallets so cross-chain transfers (e.g. Solana source + Tron dest, whose
    // legs are on different addresses) are returned — a single address misses them.
    const addrs = [evmAddress, solanaAddress, tronAddress].filter(Boolean) as string[]
    if (addrs.length === 0) {
      setBackendHistory([])
      return
    }
    bridgeApi.getTransferHistory(addrs).then(res => {
      if (res.success && res.data) {
        setBackendHistory(res.data.map((t: any) => ({
          id: t.id || t.burnTxHash || crypto.randomUUID(),
          amount: t.amount,
          // #614 — the rail the backend recorded; absent on legacy rows → treated as non-atomic.
          provider: t.provider ?? null,
          // Real token labels from the backend (CCTP → USDC; DEX may be USDT/native). Fall back to USDC for
          // legacy rows. Replaces the previous hardcoded 'USDC' that mislabeled USDT/native transfers.
          sourceToken: t.sourceToken ?? 'USDC',
          destToken: t.destToken ?? 'USDC',
          sourceChain: t.sourceChain as Chain,
          destinationChain: t.destinationChain as Chain,
          status: t.status,
          burnTxHash: t.burnTxHash ?? null,
          mintTxHash: t.mintTxHash ?? null,
          timestamp: parseActivityTimestamp(t.createdAt),
        })))
      }
    }).catch(() => {}) // Silent fail — Activity just stays empty
  }, [evmAddress, solanaAddress, tronAddress])

  // Claim history for ActivityDrawer (referral reward claims)
  const [claimHistory, setClaimHistory] = useState<ActivityTransfer[]>([])

  useEffect(() => {
    const addr = evmAddress
    if (!addr) {
      setClaimHistory([])
      return
    }
    referralApi.getClaimsByWallet(addr).then(res => {
      if (res.success && res.data) {
        setClaimHistory(res.data.map(c => {
          const chainId = CHAIN_NAME_TO_ID[c.chain]
          const fsConfig = chainId ? FEESPLITTER_CHAINS[chainId] : undefined
          const explorerUrl = fsConfig && c.txHash
            ? `${fsConfig.explorerUrl}/tx/${c.txHash}`
            : undefined
          return {
            id: `claim-${c.txHash}`,
            amount: c.amount,
            sourceToken: c.tokenSymbol,
            destToken: c.tokenSymbol,
            sourceChain: (c.chain as Chain) || 'ethereum',
            destinationChain: (c.chain as Chain) || 'ethereum',
            status: 'complete',
            burnTxHash: c.txHash,
            explorerUrl: explorerUrl ?? null,
            timestamp: new Date(c.claimedAt).getTime(),
            type: 'claim' as const,
          }
        }))
      }
    }).catch(() => {})
  }, [evmAddress])

  // Fiat currency state
  const [fiatCurrency, setFiatCurrency] = useState(() =>
    localStorage.getItem('parsebridge:fiatCurrency') || 'usd'
  )
  const [isFiatModalOpen, setIsFiatModalOpen] = useState(false)
  const fiatSymbol = CURRENCIES.find(c => c.code.toLowerCase() === fiatCurrency)?.symbol ?? '$'
  const { rate: fiatRate, stale: fiatStale, formatFiatAmount } = useFiatPrices(fiatCurrency)

  // Smart-select ref (fires once, never re-triggers)
  const smartSelectFiredRef = useRef(false)

  // #301 item 3 / invariant I9 — no cross-address bleed.
  //
  // The three balance legs now run CONCURRENTLY and merge as each resolves, so two invocations can be
  // in flight at once (account switch mid-fetch, or the 30s tick firing during a manual refresh). Every
  // invocation stamps itself with a monotonic generation; a leg whose generation is no longer current
  // is DISCARDED rather than merged. Without this a previous wallet's balance survives into the new
  // address's map and feeds `sourceBalance` (:770), the SwapCard prop, Max, and the
  // insufficient-balance gate — i.e. a fund-relevant decision made on another account's money.
  const balanceFetchGenRef = useRef(0)
  // The address SET this map describes. On a change we RESET the map rather than merging into it —
  // merging would leave the old account's chains sitting beside the new account's.
  const balanceAddressKeyRef = useRef<string>('')

  // 30s timer — drives periodic balance/fee refresh. Display value used by FeeSection (Phase 3).
  const [secondsRemaining, setSecondsRemaining] = useState(30)
  // secondsRemaining passed to SwapCard → FeeSection

  const { serverDown } = useServerStatus()

  // Track which addresses we've already checked for recovery
  const recoveryCheckedRef = useRef<Set<string>>(new Set())

  // ============================================
  // Chain-derived addresses (replaces dual-slot wallet model)
  // ============================================
  const sourceAddress = bridgeState.sourceChain === 'tron'
    ? tronAddress ?? null
    : bridgeState.sourceChain === 'solana'
      ? solanaAddress ?? null
      : evmAddress ?? null

  // Recipient derivation (fund-critical — this IS the mint recipient in the signed burn; see #107 +
  // deriveDestinationRecipient for the see-what-you-sign precedence). An explicit RecipientModal choice
  // wins over the ambient connected account; otherwise it defaults to the connected wallet (self-bridge).
  const destinationAddress = deriveDestinationRecipient({
    destManuallyChosen: bridgeState.destManuallyChosen,
    destinationAddress: bridgeState.destinationAddress,
    destinationChain: bridgeState.destinationChain,
    evmAddress: evmAddress ?? null,
    solanaAddress: solanaAddress ?? null,
    tronAddress: tronAddress ?? null,
  })

  // Derive source/dest balance from allBalances
  const sourceBalance = bridgeState.sourceChain ? allBalances[bridgeState.sourceChain] ?? null : null
  const destBalance = bridgeState.destinationChain ? allBalances[bridgeState.destinationChain] ?? null : null

  // The dest-side "Balance:" is sourced from allBalances — the CONNECTED dest wallet keyed by chain. It is
  // NOT the balance of an externally-pasted recipient (we never fetch arbitrary addresses). Only surface it
  // when the recipient IS the connected wallet; otherwise it would show the user's OWN balance against
  // someone else's address (reported: pasted address shows a different balance than it holds).
  const connectedDestWallet = bridgeState.destinationChain === 'tron'
    ? (tronAddress ?? null)
    : bridgeState.destinationChain === 'solana'
      ? (solanaAddress ?? null)
      : (evmAddress ?? null)
  // #459 — the case-sensitivity of the destination address family, READ from the chain registry
  // instead of re-derived here as `=== 'solana' || === 'tron'`. That literal was correct only by
  // accident of which chains exist: a base58check or bech32 destination (#374) compared
  // case-insensitively would report "this is your wallet" for an address that is NOT the user's,
  // three lines above a fund-critical recipient row. The null guard is not defensive noise:
  // `destinationChain` is `Chain | null` and `CHAIN_REGISTRY[null]` would throw in render. A null
  // chain resolves to `false` here, which is the EVM arm — byte-identical to the old predicate,
  // where `null === 'solana'` was likewise false.
  //
  // DELIBERATELY NOT the block above. `connectedDestWallet` (:1128-1132) selects WHICH CONNECTED
  // WALLET to compare against — a wallet-FAMILY question, `addrType`'s job, not a casing one. Same
  // membership today, different predicate; folding the two together would be the mistake this
  // registry exists to prevent, inside a single file.
  const destCaseSensitive = bridgeState.destinationChain
    ? CHAIN_REGISTRY[bridgeState.destinationChain].caseSensitive
    : false
  const recipientIsConnectedWallet = !!destinationAddress && !!connectedDestWallet && (
    destCaseSensitive
      ? destinationAddress === connectedDestWallet                                 // base58: case-sensitive
      : destinationAddress.toLowerCase() === connectedDestWallet.toLowerCase()     // EVM: case-insensitive
  )

  // #143 / findings S7 — the ONE string the review modal may render.
  //
  // It must be the DERIVED value alone. The previous prop was
  // `destinationAddress || bridgeState.destinationAddress || ''`, which can resolve to a different
  // string than the one actually signed as the mint recipient (the burn at `executeTransfer` and
  // `executeBridge` both use the derived value only) and than the one `recipientIsConnectedWallet`
  // above is computed from. While the row asserted "(your wallet)" unconditionally that divergence
  // was silent; now that the modal branches on the flag it would render a warning and a full address
  // for a string the user is not sending to — strictly worse than the bug it replaces.
  //
  // `?? ''`, not `|| ''`: deriveDestinationRecipient returns `string | null`, so the coalesce is
  // doing null-handling, not cosmetic fallback. The empty case is rendered as "Not set" by the modal
  // rather than as a labelled blank address (destination wallet disconnected).
  const reviewRecipientAddress = destinationAddress ?? ''

  // ============================================
  // #261 F3 — the connected-wallet DISPLAY names (label + brand icon)
  // ============================================
  // STRICTLY ADDITIVE TO THE BLOCK ABOVE. Nothing here feeds `destinationAddress`,
  // `recipientIsConnectedWallet`, or `reviewRecipientAddress` — those three are the fund-critical
  // values and they are computed above, unchanged. These memos only decide what TEXT and which LOGO
  // sit BESIDE the recipient string. `utils/recipient.ts` is untouched by this change (MP-2 /
  // ADDENDUM A3: its diff is zero lines), so the shown string remains the signed string by
  // construction rather than by review.

  /**
   * Which family's self-reported wallet name applies, decided by the CHAIN — the same EVM/Solana/Tron
   * split `recipientIsConnectedWallet` uses above (H7).
   *
   * A user can hold an EVM and a Solana wallet connected simultaneously (the app maintains one slot
   * per ecosystem), so "the connected wallet" is only well-defined relative to a chain. Reading the
   * wrong slot would label an EVM destination "Phantom" — a confident wrong answer on the last screen
   * before signing, which is worse than the generic copy F3 replaces.
   */
  const walletNameForChain = useCallback((chain: Chain | null): string | undefined => {
    if (chain === 'tron') return activeTronWallet?.adapter?.name
    if (chain === 'solana') return solanaWalletName
    return evmConnectorName
  }, [activeTronWallet, solanaWalletName, evmConnectorName])

  /**
   * The review card's recipient label — `MetaMask · Robinhood network`, rendered in parentheses in
   * place of `(your wallet)`.
   *
   * The flag gate is on the memo's OUTPUT (`undefined` when off) rather than at the render site, so
   * flag-OFF exercises the modal's own absent-prop fallback — i.e. the path that ALREADY ships — and
   * H2's "byte-identical to today" is the same code in both states, not a second implementation of
   * today's copy. The modal re-checks the flag and re-runs the fail-closed guard regardless
   * (`ReviewTransferModal/index.tsx`), because a prop cannot vouch for its own producer.
   *
   * Gated on `recipientIsConnectedWallet` too: a PASTED third-party recipient renders the full
   * address and its own warning, and must never be labelled with the user's wallet name.
   */
  const recipientWalletLabel = useMemo(() => {
    if (!isWalletTypeLabelEnabled()) return undefined
    if (!recipientIsConnectedWallet) return undefined
    return buildRecipientWalletLabel({
      connectorName: walletNameForChain(bridgeState.destinationChain),
      destinationChain: bridgeState.destinationChain,
    }) ?? undefined
  }, [recipientIsConnectedWallet, bridgeState.destinationChain, walletNameForChain])

  /** SwapCard SEND chip — always the connected SOURCE wallet's brand. */
  const sourceWalletKind = useMemo(
    () => (isWalletTypeLabelEnabled() ? walletLogoKind(walletNameForChain(bridgeState.sourceChain)) : null),
    [bridgeState.sourceChain, walletNameForChain],
  )

  /**
   * SwapCard RECEIVE chip — the connected wallet's brand ONLY when the recipient IS that wallet
   * (ADDENDUM A4 / F3-M2, fund-safety). The receive chip renders `destinationAddress`, which can be a
   * PASTED third-party address; branding that with the user's own wallet logo would recreate, in icon
   * form, the exact false "this is mine" cue F3 exists to remove. `null` → the generic glyph.
   */
  const destWalletKind = useMemo(() => {
    if (!isWalletTypeLabelEnabled()) return null
    if (!recipientIsConnectedWallet) return null
    return walletLogoKind(walletNameForChain(bridgeState.destinationChain))
  }, [recipientIsConnectedWallet, bridgeState.destinationChain, walletNameForChain])

  /**
   * RecipientModal's "Use connected address" option (parent spec item 3). Deliberately NOT gated on
   * `recipientIsConnectedWallet`: that option is offered precisely when the connected address is not
   * yet the recipient, and naming its provider is the whole point — the user sees the option is their
   * MetaMask, not their Robinhood app.
   */
  const recipientModalWalletKind = useMemo(
    () => (isWalletTypeLabelEnabled() ? walletLogoKind(walletNameForChain(bridgeState.destinationChain)) : null),
    [bridgeState.destinationChain, walletNameForChain],
  )

  /**
   * Fixer pass, F-3 — the RecipientModal's connected-address option NAME text, routed through the
   * SAME `buildRecipientWalletLabel` the review card uses (`recipientWalletLabel` above), so the
   * WalletConnect "via" wording (H6), the destination-network half (A1), and the brand-collision
   * " Wallet" suffix (H5) all apply here too. Previously the modal rendered the bare
   * `recipientModalWalletKind` above — a different, shorter story about the same address than the
   * review card told one screen later.
   *
   * Deliberately NOT gated on `recipientIsConnectedWallet`, for the identical reason
   * `recipientModalWalletKind` above isn't: this option is offered precisely when the connected
   * address is not yet the CHOSEN recipient, and naming its provider is the whole point.
   */
  const recipientModalWalletLabel = useMemo(
    () => (isWalletTypeLabelEnabled()
      ? buildRecipientWalletLabel({
          connectorName: walletNameForChain(bridgeState.destinationChain),
          destinationChain: bridgeState.destinationChain,
        })
      : null),
    [bridgeState.destinationChain, walletNameForChain],
  )

  // ============================================
  // #144 (R2) — recipient "code here, not there" risk prompt
  // ============================================
  // The guard itself lives in `utils/recipientCodeGuard.ts` and runs INSIDE the signing paths (all
  // three CCTP entry points plus the DEX path), not here — a verdict computed at review-modal open
  // can be stale by signing time, because `prevDestChainRef` mutates before the early return in the
  // destination-sync effect below. This block only supplies the UI half: a promise the guard awaits.
  const [recipientRisk, setRecipientRisk] = useState<
    { risk: RecipientCodeRisk; resolve: (accepted: boolean) => void } | null
  >(null)

  const confirmRecipientRisk = useCallback<RecipientRiskConfirm>(
    (risk) => new Promise<boolean>((resolve) => setRecipientRisk({ risk, resolve })),
    [],
  )

  const answerRecipientRisk = useCallback((accepted: boolean) => {
    setRecipientRisk((pending) => {
      pending?.resolve(accepted)
      return null
    })
  }, [])

  // PREFETCH. The sweep is six `eth_getCode` reads against six separate rate-limited public
  // endpoints for the structural worst case (a plain EOA, which has code nowhere and so cannot
  // short-circuit). Warming the memo when the (recipient, destChain) pair CHANGES overlaps those
  // reads with the user's own dwell time on the amount/review screens, so the revalidation the
  // signing path performs is a cache hit costing zero extra round-trips. Fire-and-forget by design:
  // it can never fail the render, and the signing-time call is what actually gates the transfer.
  useEffect(() => {
    prefetchRecipientCode({
      recipient: destinationAddress,
      destChain: bridgeState.destinationChain,
    })
  }, [destinationAddress, bridgeState.destinationChain])

  // ============================================
  // Fee config fetch on mount
  // ============================================
  useEffect(() => {
    getFeeConfig().then(config => {
      setMinAmountByDest(config.minAmountByDest)
      if (Number.isFinite(config.minAmountUsd) && config.minAmountUsd > 0) {
        setConfigMinUsd(config.minAmountUsd)
      }
    }).catch(err => {
      log.warn('Failed to fetch fee config:', err)
    })
  }, [])

  // ============================================
  // Multi-chain balance fetch
  // ============================================
  const fetchAllBalances = useCallback(async () => {
    // Stamp this invocation BEFORE the early return: a disconnect must invalidate legs already in
    // flight, or the disconnected wallet's balance lands in the map after `setAllBalances({})`.
    const addressKey = `${evmAddress ?? ''}|${solanaAddress ?? ''}|${tronAddress ?? ''}`
    const gen = balanceFetchGenRef.current + 1
    balanceFetchGenRef.current = gen
    const addressSetChanged = balanceAddressKeyRef.current !== addressKey
    balanceAddressKeyRef.current = addressKey

    if (!evmAddress && !solanaAddress && !tronAddress) {
      setAllBalances({})
      // GATE-4 fix — this generation is already current (stamped above) and nothing is in flight
      // for it, so this early return IS "no wallets, nothing in flight": clear the flag as part of
      // that. Without this, an earlier invocation's `finally` is gen-gated (see below) and skips its
      // clear once THIS invocation bumps the generation, and the 30s timer also stops while
      // disconnected — so the flag would otherwise wedge `true` with no fetch left to ever clear it.
      setIsBalancesLoading(false)
      return
    }

    // I9 — RESET, never merge, when the address set changed.
    if (addressSetChanged) setAllBalances({})

    setIsBalancesLoading(true)
    try {
      // `merged` accumulates exactly what the serial implementation built, and is what smart-select
      // reads below — deliberately NOT the state map, which under a same-address refresh can still
      // carry entries from the previous cycle.
      const merged: ChainBalanceMap = {}

      // Merge EVERY EVM balance the API returned — both bridgeable (CCTP) AND dex-only chains.
      // BNB/BSC is dexRoutable but NOT bridgeable, so looping BRIDGE_CHAINS alone silently dropped
      // its balance (it was fetched + returned, just never merged). Mirror the api.ts BALANCE_CHAINS
      // union (BRIDGE ∪ DEX) so any dex-only chain's balance shows.
      const EVM_LEG_CHAINS = [...new Set([...BRIDGE_CHAINS, ...DEX_CHAINS])]
        .filter(chain => chain !== 'solana' && chain !== 'tron')

      /**
       * Land one leg's result. LEG-SCOPED REPLACE, not a bare merge: the keys this leg owns are
       * cleared first, so a chain that failed this cycle goes ABSENT from the map exactly as it did
       * when the whole map was rebuilt and assigned in one go (invariant I2 — a failed leg is absent,
       * never zero, and never a stale value wearing a fresh timestamp). Chains owned by the OTHER legs
       * are untouched, which is what makes progressive merge possible.
       */
      const landLeg = (ownedChains: readonly Chain[], patch: ChainBalanceMap) => {
        if (gen !== balanceFetchGenRef.current) return // I9 — a newer fetch owns the map now
        Object.assign(merged, patch)
        setAllBalances(prev => {
          const next: ChainBalanceMap = { ...prev }
          for (const chain of ownedChains) delete next[chain]
          return Object.assign(next, patch)
        })
      }

      // The three legs run CONCURRENTLY (#301 item 3): total latency is now the SLOWEST leg, not the
      // sum. `allSettled` — a rejected leg must not deprive the other two of their result, which is
      // exactly what the single serial `try` did.
      //
      // Each leg reports whether it actually landed data, not merely whether its promise settled.
      // `balanceApi.getAllBalances`/`getBalance` (→ `fetchApi`, `services/api.ts`) NEVER reject: every
      // failure path — network error, non-OK response, bad JSON — is caught internally and resolved as
      // `{success:false, error:{...}}`. So in production `Promise.allSettled` status is ALWAYS
      // 'fulfilled'; `status === 'fulfilled'` alone cannot detect a failed leg, and the I8 gate below
      // needs `success` too.
      type LegOutcome = { label: string; success: boolean; reason?: unknown }
      const legs: Promise<LegOutcome>[] = []

      // Leg 1: EVM chains (all share one address)
      if (evmAddress) {
        legs.push((async () => {
          const evmResponse = await balanceApi.getAllBalances(evmAddress)
          const patch: ChainBalanceMap = {}
          const success = evmResponse.success && !!evmResponse.data
          if (success) {
            for (const chain of EVM_LEG_CHAINS) {
              const entry = evmResponse.data![chain]
              if (entry) {
                patch[chain] = entry
              }
            }
          }
          landLeg(EVM_LEG_CHAINS, patch)
          return { label: 'evm', success, reason: success ? undefined : evmResponse.error }
        })())
      }

      // Leg 2: Solana (separate address)
      if (solanaAddress) {
        legs.push((async () => {
          const solResponse = await balanceApi.getBalance(solanaAddress, 'solana')
          const patch: ChainBalanceMap = {}
          const success = solResponse.success && !!solResponse.data
          if (success) {
            patch.solana = solResponse.data!
          }
          landLeg(['solana'], patch)
          return { label: 'solana', success, reason: success ? undefined : solResponse.error }
        })())
      }

      // Leg 3: Tron (separate address, like Solana)
      if (tronAddress) {
        legs.push((async () => {
          const tronResp = await balanceApi.getBalance(tronAddress, 'tron')
          const patch: ChainBalanceMap = {}
          const success = tronResp.success && !!tronResp.data
          if (success) {
            patch.tron = tronResp.data!
          }
          landLeg(['tron'], patch)
          return { label: 'tron', success, reason: success ? undefined : tronResp.error }
        })())
      }

      const legResults = await Promise.allSettled(legs)

      // A stale invocation has already had every one of its legs discarded above; it must not run
      // smart-select either.
      if (gen !== balanceFetchGenRef.current) return

      // I8 — smart-select fires ONLY on a COMPLETE map: every attempted leg both SETTLED and actually
      // SUCCEEDED. This decision picks the chain funds leave from, and it is one-shot and permanent
      // (`smartSelectFiredRef` below is never cleared) — firing it on a map missing its EVM leg (e.g.
      // that leg's request timed out and resolved `{success:false}`, landing an empty patch) would
      // point a Base user at Solana with no way back except localStorage. On any failure — rejected OR
      // fulfilled-but-unsuccessful — leave the ref FALSE and let the 30s tick retry.
      const allLegsFulfilled = legResults.every(r => r.status === 'fulfilled')
      const allLegsSucceeded = allLegsFulfilled
        && legResults.every(r => r.status === 'fulfilled' && r.value.success)
      // GATE-4 fix (finding 2) — same value smart-select gates on, surfaced to SwapCard's latch. Safe
      // to write unconditionally here: the `gen !== balanceFetchGenRef.current` return above already
      // makes everything past it current-generation-only.
      setLastBalanceLoadSucceeded(allLegsSucceeded)
      if (!allLegsSucceeded) {
        for (const r of legResults) {
          if (r.status === 'rejected') {
            log.warn('Balance leg failed:', r.reason)
          } else if (!r.value.success) {
            log.warn('Balance leg failed:', r.value.label, r.value.reason)
          }
        }
      }

      // Smart-select: first successful fetch, no localStorage (G4 + G8)
      if (allLegsSucceeded && !smartSelectFiredRef.current && Object.keys(merged).length > 0) {
        const hasLocalStorageSource = localStorage.getItem('parsebridge:lastSourceChain')
        if (!hasLocalStorageSource) {
          smartSelectFiredRef.current = true

          // Find highest-balance chain
          let bestChain: Chain | null = null
          let bestBalance = 0
          for (const [chain, bal] of Object.entries(merged)) {
            if (bal?.usdc) {
              const usdcNum = parseFloat(bal.usdc)
              if (usdcNum > bestBalance) {
                bestBalance = usdcNum
                bestChain = chain as Chain
              }
            }
          }

          if (bestChain) {
            setSourceChain(bestChain)
            // Default dest: Base if source != Base, else Solana (G8)
            const defaultDest: Chain = bestChain !== 'base' ? 'base' : 'solana'
            setDestinationChain(defaultDest)
          }
        }
      }
    } catch (err) {
      log.warn('Failed to fetch all balances:', err)
    } finally {
      // GATE-4 fix — gate the flag clear on THIS invocation still being current, the same generation
      // check already used above (`:908`, `:981`). Without this, a superseded invocation's `return` at
      // `:981` still runs this `finally` and clears the FRESH invocation's loading flag while that
      // fetch is genuinely in flight (account switch mid-fetch) — the chain-picker shimmer lies "done",
      // and it hands SwapCard a (null balance, false flag) window for the new key that FIX A's latch
      // would otherwise have to defend against.
      if (gen === balanceFetchGenRef.current) setIsBalancesLoading(false)
    }
  }, [evmAddress, solanaAddress, tronAddress, setSourceChain, setDestinationChain])

  // Fetch all balances when any wallet connects
  useEffect(() => {
    fetchAllBalances()
  }, [fetchAllBalances])

  // ============================================
  // localStorage persistence (G8)
  // ============================================
  // Initial chain setup — localStorage or hardcoded defaults
  useEffect(() => {
    if (!bridgeState.sourceChain) {
      const saved = localStorage.getItem('parsebridge:lastSourceChain')
      if (saved && (BRIDGE_CHAINS as readonly string[]).includes(saved)) {
        setSourceChain(saved as Chain)
      } else {
        setSourceChain('base')
      }
    }
    if (!bridgeState.destinationChain) {
      const saved = localStorage.getItem('parsebridge:lastDestChain')
      if (saved && (BRIDGE_CHAINS as readonly string[]).includes(saved)) {
        setDestinationChain(saved as Chain)
      } else {
        setDestinationChain('solana')
      }
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  // ============================================
  // 30s refresh timer — BridgePage owns (single source of truth)
  // ============================================
  useEffect(() => {
    if (!evmAddress && !solanaAddress && !tronAddress) {
      setSecondsRemaining(30)
      return
    }

    const interval = setInterval(() => {
      setSecondsRemaining(prev => {
        if (prev <= 1) {
          // Refresh all balances + fees (CCTP estimate only — DEX routes self-refresh)
          fetchAllBalances()
          if (isCctpRoute) estimateFees(sourceAddress || undefined)
          return 30
        }
        return prev - 1
      })
    }, 1000)

    return () => clearInterval(interval)
  }, [evmAddress, solanaAddress, tronAddress, sourceAddress, fetchAllBalances, estimateFees, isCctpRoute])

  // Reset timer when wallet changes
  useEffect(() => {
    setSecondsRemaining(30)
  }, [evmAddress, solanaAddress])


  // ============================================
  // ============================================
  // Recovery check on wallet connect
  // ============================================
  useEffect(() => {
    if (sourceAddress && !recoveryCheckedRef.current.has(sourceAddress)) {
      recoveryCheckedRef.current.add(sourceAddress)
      triggerRecoveryCheck(sourceAddress)
    }
  }, [sourceAddress, triggerRecoveryCheck])

  // ============================================
  // Destination address sync from wallet
  // ============================================
  /**
   * #145 — the destination chain whose switch just discarded an explicitly-chosen recipient.
   * Null when there is nothing to tell the user. Cleared on dismiss and whenever the user makes a
   * fresh explicit choice (so it can never outlive the state it describes).
   */
  const [recipientResetChain, setRecipientResetChain] = useState<Chain | null>(null)

  const prevDestChainRef = useRef(bridgeState.destinationChain)
  useEffect(() => {
    const chainChanged = prevDestChainRef.current !== bridgeState.destinationChain
    prevDestChainRef.current = bridgeState.destinationChain
    // Preserve an explicitly-chosen recipient (RecipientModal) — do NOT overwrite it with the connected
    // account. A dest-CHAIN switch invalidates the choice (it was for the old chain), so in that case we
    // fall through and re-derive from the wallet (setDestinationAddress with manual=false also clears the
    // flag, cleanly reverting to the self-bridge default).
    if (bridgeState.destManuallyChosen && !chainChanged) return

    // #145 (R3) — SAY SO when a chain switch discards an explicit choice.
    //
    // Re-deriving is defensible in isolation; the defect is the interaction with #143. The reverted
    // value is the user's own wallet, so the review screen used to label it "(your wallet)" and it
    // genuinely was — nothing looked wrong, and a user who deliberately set a recipient then changed
    // the destination chain signed a transfer to a different address than the one they picked, with
    // no signal at all.
    //
    // Gated on destManuallyChosen, so the default self-bridge cohort (the overwhelming majority, who
    // never open RecipientModal) sees no new noise: for them there was no choice to invalidate.
    if (bridgeState.destManuallyChosen && chainChanged) {
      setRecipientResetChain(bridgeState.destinationChain)
    }
    if (bridgeState.destinationChain === 'tron' && tronAddress) {
      setDestinationAddress(tronAddress)
    } else if (bridgeState.destinationChain === 'solana' && solanaAddress) {
      setDestinationAddress(solanaAddress)
    } else if (bridgeState.destinationChain !== 'solana' && bridgeState.destinationChain !== 'tron' && evmAddress) {
      setDestinationAddress(evmAddress)
    } else {
      // Wallet disconnected — clear stale destination address
      setDestinationAddress('')
    }
  }, [bridgeState.destinationChain, bridgeState.destManuallyChosen, solanaAddress, evmAddress, tronAddress, setDestinationAddress])

  // Calculate estimated received from fee estimate
  // Derive estimated received — DEX vs CCTP
  // Whether the dest token is a native token (ETH, POL, BNB, etc.)
  const isNativeDest = useMemo(() => {
    const t = TOKEN_PICKER_LIST.find(tk => tk.symbol === selectedDestToken)
    return t?.isNative ?? false
  }, [selectedDestToken])

  // Whether the SOURCE token is native (drives whether the wallet debit is one token or token + native fee).
  const isNativeSrc = useMemo(() => {
    const t = TOKEN_PICKER_LIST.find(tk => tk.symbol === selectedSourceToken)
    return t?.isNative ?? false
  }, [selectedSourceToken])

  /**
   * The dest ("you receive") figure, TAGGED with which input produced it (#336 R3 + R7).
   *
   * `dexFee` (:1619) multiplies this figure by `estimatedAllInBps`, which belongs to the estimate
   * keyed on the DEBOUNCED source amount (:671). That product is only meaningful when the figure
   * came out of that same estimate. Two branches below do NOT:
   *   · 'reverseTarget' — the receive target the user typed THIS keystroke (R3), and
   *   · 'liveAmount'    — `bridgeState.amount` echoed as a rough estimate (R7).
   * Everything else reads a field of the estimate object (or is a constant) and is 'estimate'.
   *
   * #336 R11 — PRECISION ON "LIVE". `bridgeState.amount` is NOT the raw keystroke: SwapCard debounces
   * the field by 300 ms before dispatching it (SwapCard/index.tsx:353-362). The real pipeline is
   *   `localAmount` --300 ms--> `bridgeState.amount` --500 ms--> `debouncedDexAmount` (the query key).
   * 'liveAmount' means stage 2 — live RELATIVE to the estimate, one debounce ahead of it, and the
   * product below is wrong for the whole of that ~500 ms. It does NOT mean stage 1: the guard compares
   * stages 2 and 3 and is structurally blind to a keystroke still inside its own 300 ms window, so a
   * ~300 ms residual mismatch survives it. Named, not hidden — closing it means moving the field's
   * undebounced value into this component, which is a different change.
   *
   * The tag is produced HERE, in the same expression that chooses the value, and never re-derived:
   * a sibling memo restating these five conditions would be a second copy free to drift out of step
   * with this one — the exact class of split-brain this PR exists to remove. Callers ask which input
   * drove the figure; they do not re-answer it.
   *
   * VALUE SEMANTICS ARE UNCHANGED from the pre-tag memo — same branches, same order, same deps.
   * `estimatedReceived` below is that value verbatim, so every existing consumer (the receive field,
   * `amountTooLowAfterFees`, `dexReceiveNonPositive`, the review modal) sees exactly what it saw.
   */
  const estimatedReceivedDetail = useMemo<{ value: string; driver: 'estimate' | 'reverseTarget' | 'liveAmount' }>(() => {
    // Reverse mode (DEX): the user typed the dest amount directly, so the receive field IS their target —
    // show it as-is regardless of the (possibly empty) send field. Never overwrite with a forward estimate.
    if (isDexRoute && inputMode === 'receive' && receiveTarget && parseFloat(receiveTarget) > 0) {
      return { value: receiveTarget, driver: 'reverseTarget' }
    }
    // No positive send amount (and not reverse mode) → there is nothing to receive. Without this guard a STALE
    // estimate from a previous amount lingers after the send field is cleared — the dex-estimate query keeps
    // its last data when disabled on an empty amount, which showed "1.00 received" while YOU SEND was 0.00.
    if (!(parseFloat(bridgeState.amount || '0') > 0)) return { value: '0.0', driver: 'estimate' }
    if (isDexRoute) {
      // HARD below-minimum (1Click 400 marker): there is no quote — the backend refused. Show nothing (blank
      // receive) instead of falling through to the `return bridgeState.amount` rough-estimate below, which
      // would render a misleading "1.00" for an amount the route won't actually fill.
      if (dexFeeEstimate?.belowMinimum || (dexFeeEstimate as { routeUnavailable?: boolean } | undefined)?.routeUnavailable) return { value: '0.0', driver: 'estimate' }
      // Native token output: use live token amount from deBridge estimation
      if (dexFeeEstimate?.estimatedTokenAmount) return { value: dexFeeEstimate.estimatedTokenAmount, driver: 'estimate' }
      // deBridge stablecoin: use fee oracle estimate (USD ≈ token amount)
      if (dexFeeEstimate?.estimatedReceived && !isNativeDest) return { value: dexFeeEstimate.estimatedReceived, driver: 'estimate' }
      // Jupiter or no estimate yet: show input amount as rough estimate (fees are small).
      // TAGGED 'liveAmount' (#336 R7): this is the ONE forward branch whose figure is live user input
      // rather than a quoted field, so it is the one forward branch a stale bps may not be applied to.
      //
      // #261 M3 — "FEES ARE SMALL" IS A SAME-VALUE-PAIR PREMISE, so the guard needs BOTH ends.
      // `!isNativeDest` alone is blind to the SOURCE: on a native source into a stable dest this
      // mirrored 0.00041 ETH as "0.00041 USDG" — ~$0.77 rendered as ~$0.0004, about 1870× wrong — in
      // the ordinary green real-quote styling. Mirroring is only defensible where input ≈ output, i.e.
      // stable→stable (~1:1, within a cent), so it now requires both ends to be non-native. A native
      // source with no quote in hand falls through to the `'0.0'` blank on the next line — the SAME
      // idiom, and the same literal branch, the native-DEST no-estimate case has always used.
      //
      // `!isNativeSrc` IS "the source is a stablecoin": every entry in TOKEN_PICKER_LIST is either
      // `isNative` or a stable (config/tokens.ts), which is the identical partition
      // STABLECOIN_PICKER_LIST is built from. Stated because it is a PREMISE, not a tautology — add a
      // non-native non-stable token (WBTC, say) and this reopens the mirror for it. Deliberately
      // written with the same `isNativeSrc`/`isNativeDest` idiom the rest of this file uses rather
      // than a membership test against STABLECOIN_PICKER_LIST.
      //
      // DRIVER UNCHANGED, DELIBERATELY (#336 R7 / #337). 'liveAmount' means "the figure IS
      // bridgeState.amount echoed", so it stays on the branch that still echoes it; the native-source
      // path returns a CONSTANT and is therefore 'estimate', exactly like the line below. Nothing is
      // hidden by that: with value '0.0', `dexFee` returns `undefined` at its `!(destAmt > 0)` guard
      // BEFORE `dexEstimateMatchesDestDriver` is ever consulted, so the two tags are observationally
      // identical on this path — which is also why no test can pin the tag here (see the note on the
      // driver case in `BridgePage.m3PrequoteReceive.test.tsx`). Retagging would change the question
      // that guard is asked — a different change.
      //
      // INTRA-FILE REFERENCES ARE BY SYMBOL NAME, NOT LINE NUMBER, AND DELIBERATELY SO. Two review
      // rounds caught every line citation in this block stale by 33-37 lines: the first set was
      // computed before this comment existed, and the "correction" was computed before the correction
      // itself landed — each edit shifts the very targets it cites, so the numbers were wrong at birth
      // both times. Symbol names cannot drift. Cross-FILE citations (dex.ts below) keep their line
      // numbers: they are stable under edits to this file, and were verified exact at review.
      //
      // SUBMIT-GATE EFFECT — the honest statement is MONOTONICITY, not equality. An earlier draft of
      // this comment claimed submittability was IDENTICAL in both flag states and enumerated "the
      // three dex.ts sites that null estimatedReceived". That enumeration was INCOMPLETE and the
      // equality claim was false; it is corrected here rather than softened, because the deploy
      // wiring quotes it as the basis for shipping this flag dark.
      //
      // What actually holds, and is what gate-seam constraint (a) needs:
      //   THIS FLAG CAN ONLY EVER TIGHTEN THE SUBMIT GATE, NEVER LOOSEN IT. The sole change is
      //   mirror -> '0.0', and `dexReceiveNonPositive` blocks on a non-positive receive, so the flag
      //   can only move a state from submittable to blocked. It can never un-block a Send.
      //
      // In every state the backend CURRENTLY emits, the two flag states are in fact identical,
      // because each is already blocked by a gate that does not read this figure:
      //   · no estimate in hand            -> `dexEstimateMissing` (tests `!dexFeeEstimate`)
      //   · estimate present but unpriced  -> `opexGate`; every dex.ts site that nulls
      //     `estimatedReceived` (:1940 under `quoteUnavailable`, plus the Tron refusals at :915/:934)
      //     ships `viability.level:'block'` or `routeUnsupported`, and Jupiter's failure path returns
      //     503 rather than a partial estimate.
      // The exception the earlier draft missed: `dex.ts:1285` passes the BOT's bps-only /quote body
      // through verbatim (`{ corridor, ...botData, sourceAmountUsd }`), and that body carries neither
      // `estimatedReceived` nor any block marker. On a native source that shape reaches this branch
      // and the flag DOES flip the gate — in the blocking direction. It is unreachable today only by
      // coincidence: the bot's `feeOracle.getEstimate` reads the SAME `corridor_fee_cache` row
      // `dex.ts:1265` just missed, so it 404s. Do NOT rely on that; the comment at dex.ts:1278-1280
      // documents a different (and incorrect) mechanism for it. Tracked separately — a fix belongs in
      // dex.ts, which is flow-critical and outside this change's scope.
      //
      // ONE FEE-CARD STATE DOES MOVE WITH THE FLAG — enumerated here rather than left for a reader to
      // find, because "OFF is a no-op" is a claim about OFF, and this is a change to ON. On a native
      // source into a cross-chain stable dest that the provider refused to price, with the typed
      // amount ahead of the estimate: OFF gives `destAmt > 0`, the R7 staleness guard fires, `dexFee`
      // is `null`, and the card SPEAKS its refusal ("Fee unavailable"). ON gives `destAmt === 0`, so
      // `dexFee` short-circuits to `undefined` at that same guard and the card falls back to the
      // neutral "Fee details" — the #336 R10 shape, a refusal quietly downgraded to "nothing to see
      // here". It is
      // bounded and not a regression in substance: `opexGate` returns `quoteUnavailable` in that same
      // state, so the CTA is disabled with a named reason either way, and the figure OFF was pricing
      // there was the mirror — 15 bps of an ETH amount denominated in the dest token, i.e. garbage.
      // Removing a garbage fee is the right direction; saying so out loud is the point of this note.
      const mirrorInputAsRoughEstimate = isPrequoteReceiveBlankEnabled()
        ? !isNativeDest && !isNativeSrc
        : !isNativeDest
      if (mirrorInputAsRoughEstimate) return { value: bridgeState.amount, driver: 'liveAmount' }
      return { value: '0.0', driver: 'estimate' }
    }
    // CCTP
    if (feeEstimate) {
      return {
        value: bridgeState.payNative
          ? (parseFloat(bridgeState.amount) - parseFloat(feeEstimate.totalFee)).toString()
          : feeEstimate.estimatedReceived,
        driver: 'estimate',
      }
    }
    return { value: '0.0', driver: 'estimate' }
    // `isNativeSrc` (#261 M3) is read by the mirror guard above and MUST be listed: this memo also
    // feeds `dexFee`, whose own deps comment records a live bug from exactly this omission.
    // Adding it is a no-op with the flag OFF — the value is a pure function of the other deps there,
    // and every consumer reads `.value`/`.driver` (strings), never this object's identity.
  }, [isDexRoute, dexFeeEstimate, bridgeState.amount, bridgeState.payNative, feeEstimate, isNativeDest, isNativeSrc, inputMode, receiveTarget])

  const estimatedReceived = estimatedReceivedDetail.value

  // USD value for the receive field fiat subtitle (differs from estimatedReceived for native tokens)
  const estimatedReceivedUsd = useMemo(() => {
    // Same staleness guard as estimatedReceived: with no positive send amount (and not reverse mode) there is
    // no live estimate, so don't surface a stale native-dest USD subtitle (a "≈ $X" under a 0.0 receive).
    const reverse = inputMode === 'receive' && !!receiveTarget && parseFloat(receiveTarget) > 0
    if (!reverse && !(parseFloat(bridgeState.amount || '0') > 0)) return null
    // HARD below-minimum marker: no quote → no fiat subtitle (mirrors the blanked estimatedReceived above).
    if (dexFeeEstimate?.belowMinimum || (dexFeeEstimate as { routeUnavailable?: boolean } | undefined)?.routeUnavailable) return null
    if (isDexRoute && isNativeDest && dexFeeEstimate?.estimatedReceived) {
      return dexFeeEstimate.estimatedReceived
    }
    return null // null = use estimatedReceived as-is (already USD for stablecoins)
  }, [isDexRoute, isNativeDest, dexFeeEstimate, bridgeState.amount, inputMode, receiveTarget])

  // "Minimum received" for the DEX fee breakdown, in dest-token terms (null for CCTP → the
  // generic USDC line is used). Native dest: the slippage-adjusted floor from deBridge.
  // Stable dest: the net estimate (USD≈token). Trimmed to ≤6 fraction digits for display.
  const dexMinReceived = useMemo<string | null>(() => {
    if (!isDexRoute || !dexFeeEstimate) return null
    // Prefer the slippage-adjusted floor (deBridge minAmount / Jupiter otherAmountThreshold),
    // in dest-token terms. Fall back to the estimated dest amount, then the USD net estimate.
    const raw = dexFeeEstimate.minTokenAmount
      ?? dexFeeEstimate.estimatedTokenAmount
      ?? dexFeeEstimate.estimatedReceived
    const n = parseFloat(raw ?? '')
    if (!Number.isFinite(n) || n <= 0) return null
    return n.toLocaleString('en-US', { maximumFractionDigits: 6, useGrouping: false })
  }, [isDexRoute, dexFeeEstimate])

  /**
   * Full DEX fee breakdown (dest-token terms) for the fee panel — shown instead of the CCTP
   * feeEstimate when a swap route is selected. Fees are a fraction of the dest value; the all-in
   * bps drives both the total and the warning badge, the affiliate bps is the Parse platform cut.
   *
   * THREE-VALUED BY CONTRACT (#336 R10). The two falsy values mean DIFFERENT things, and both
   * consumers depend on the distinction:
   *   · `undefined` — there is no DEX fee surface here at all: a CCTP route, or a swap with no
   *     estimate / no receive figure yet. The fee card keeps its neutral 'Fee details' placeholder.
   *   · `null`      — a DEX route whose fee we REFUSE to state (the R3/R7 staleness guard below).
   *     Both surfaces must say 'Fee unavailable' out loud.
   * Collapsing the two (`?? null`, `|| undefined`, an `if (!dexFee)` that rewrites it) silently
   * downgrades a refusal to "nothing to see here" — which is the R10 defect verbatim: the card read
   * 'Fee details' and offered no panel, i.e. "this route has no fees", for a route whose fee we had
   * just decided we could not honestly state. FeeSection/index.tsx:71 and its `dexFee` prop doc are
   * the other half of this contract; ReviewTransferModal treats both as 'Fee unavailable' because it
   * already knows the route kind from its own `isDexRoute` prop.
   */
  const dexFee = useMemo(() => {
    if (!isDexRoute || !dexFeeEstimate) return undefined
    const destAmt = parseFloat(estimatedReceived || '0')
    if (!(destAmt > 0)) return undefined
    // #336 R3 + R7 — STALE BPS × FRESH AMOUNT. Everything below multiplies `estimatedAllInBps` by
    // `destAmt`, which is only meaningful while both describe the SAME amount. Two of the branches
    // that produce `destAmt` return LIVE USER INPUT rather than a quoted field, and neither can be
    // multiplied by a bps quoted for a different amount:
    //   · R3 'reverseTarget' — the receive target typed this keystroke, against an estimate keyed on
    //     the debounced SOURCE amount (:671). Typing 2 then 1000 overstated ~20×; 1000 then 2
    //     UNDERSTATED by the same factor, the dangerous direction, on the last screen before signing.
    //   · R7 'liveAmount' — `bridgeState.amount` echoed as a rough forward estimate (itself the
    //     300 ms-debounced field, see :1514-1521). Same product, reachable whenever the provider refused
    //     to price a non-native-dest cross-chain route (dex.ts:1276-1278 nulls `estimatedReceived`;
    //     :1326 omits `estimatedTokenAmount`).
    // The driver comes from the tag on the memo that CHOSE the figure (:1533) — not re-derived here,
    // so the guard cannot fall out of step with the branch it guards. Every other branch reads the
    // dest figure out of the same estimate object as the bps, so a debounce lag there is harmless and
    // stays exempt: this must not blank the fee on ordinary forward typing.
    // Refuse rather than render a fee for an amount nobody quoted — null yields 'Fee unavailable' on
    // both surfaces (fail CLOSED). BETA BUG-1b: a fee we refuse to state (null) — or one that rounds
    // below the display floor — must ALSO not be submittable. The `dexFeeUnavailable` memo below reuses
    // FeeSection's own predicate and is layered onto the SwapCard `canSend` prop (:2971) with a
    // 'Fee unavailable' / 'Amount too low' label. The bare `canSend` memo (:2352) stays fee-agnostic;
    // the gate lives at the prop, alongside the opex/min gates — so a null here now changes what is
    // SHOWN *and* what is submittable.
    // NOTE this guard answers "was this bps quoted for this AMOUNT?", not "…for this ROUTE?" — the
    // second question is answered upstream by the R9 route-change reset (:647-659), which clears the
    // receive target so a target quoted on the old pair can never reach this multiplication.
    if (!dexEstimateMatchesDestDriver({
      reverseDrivesDest: estimatedReceivedDetail.driver === 'reverseTarget',
      forwardLiveAmountDrivesDest: estimatedReceivedDetail.driver === 'liveAmount',
      amount: bridgeState.amount,
      estimateAmount: debouncedDexAmount,
    })) return null
    const allInBps = dexFeeEstimate.estimatedAllInBps ?? 0
    const affBps = dexFeeEstimate.breakdown?.affiliateBps ?? allInBps
    const fmt = (v: number) => Math.max(0, v).toLocaleString('en-US', { maximumFractionDigits: 6, useGrouping: false })
    // #94 Bug 3: drive the warning badge off the backend's feeWarningTier, which EXCLUDES the excess our own
    // $0.10 dynamic floor (#28) adds over a tiny principal — NOT estimatedAllInBps, which INCLUDES the floor
    // and so false-flags a small swap (e.g. $1.50 → 504 all-in bps, but only ~50 bps of uncontrollable rail
    // cost) as "Elevated". Fall back to the all-in heuristic only if the backend omitted the tier.
    const tier = dexFeeEstimate.feeWarningTier
    const warningLevel: 'green' | 'orange' | 'red' =
      tier === undefined ? (allInBps >= 250 ? 'red' : allInBps >= 150 ? 'orange' : 'green')
        : tier === 3 ? 'red' : (tier === 1 || tier === 2) ? 'orange' : 'green'
    /**
     * #261 F5 — is `destAmt` NET of the Parse affiliate skim, and do we have the skim itself?
     *
     * When deBridge quotes a same-chain swap with our affiliate percent attached it returns the
     * POST-skim output, and reports the exact cut on its own `SingleChainSwapAffiliateFee` cost line.
     * The backend republishes that line verbatim. In that state `(affBps/10000) × destAmt` is wrong
     * twice over — it is a rate applied to the wrong base, and it is a DERIVED number standing in for
     * a REPORTED one. So the fee below becomes the reported amount, and the total goes back to meaning
     * what it always meant: the all-in bps applied to the PRE-fee amount.
     *
     * Every conjunct is load-bearing:
     *  1-2. the backend published both halves (the flag never travels without its amount);
     *  3-4. `destAmt` came from THIS quote's `estimatedTokenAmount`. Conjunct 4 is NOT redundant with
     *       the driver tag: on a bot-down fallback `estimatedReceived` is `amountUsd × (1 − allInBps)`
     *       (:1554) — already net of the ALL-IN bps — and BOTH branches tag `driver: 'estimate'`
     *       (:1552, :1554), so only the string identity can tell them apart. Subtracting the affiliate
     *       again there would double-count it.
     *  5.   SAME-CHAIN. The backend already publishes these fields on the same-chain block alone, so
     *       this is the second of two independent gates keeping cross-chain rendering byte-identical —
     *       a cross-chain payload carrying the flag still cannot move the numbers.
     *  6.   the amount parses to a real positive number. Fail-CLOSED on a malformed payload: without
     *       it a non-numeric `affiliateFeeAmount` would render "NaN" as the fee on the last screen
     *       before a signature. The backend validates this field, so on any well-formed response this
     *       conjunct is always true and changes nothing.
     */
    const affiliateFeeAmt = parseFloat(dexFeeEstimate.affiliateFeeAmount ?? '')
    const netOfAffiliate =
      dexFeeEstimate.receiveIsNetOfAffiliate === true
      && dexFeeEstimate.affiliateFeeAmount != null
      && dexFeeEstimate.estimatedTokenAmount != null
      && estimatedReceived === dexFeeEstimate.estimatedTokenAmount
      && atomicSameChain
      && Number.isFinite(affiliateFeeAmt) && affiliateFeeAmt > 0
    /**
     * #261 F5 / F1 — the netted total is ADDITIVE: the reported skim PLUS the third-party rail cost.
     *
     *     totalFee = F + (destAmt + F) × breakdown.thirdPartyBps / 10000
     *
     * WHY NOT `(destAmt + F) × allInBps / 10000` (the form this replaces). That reads a RATE against a
     * reconstructed gross, and assumes `F == gross × realizedBps / 10000`. It is not. deBridge takes
     * the affiliate skim BEFORE its protocol fee and slippage estimate, so the reported `feeAmount`
     * runs systematically ~0.5-0.7% ABOVE what the reported `feeBps` implies — live capture
     * (ADDENDUM C §4): `feeAmount 188959` against `net 1690405` is 1005.4 bps of gross while `feeBps`
     * reads 999. On a realistic corridor (4.2 bps of third-party cost) that gap is larger than the
     * whole rail cost, so the rate-form total lands BELOW `parseFee` — `computeDexDisplayTotals`
     * (`FeeSection/dexTotal.ts:131`) then clamps the shown total up onto the Parse fee and the rail
     * cost renders as FREE, on the last screen before a signature.
     *
     * The additive form takes the reported fee as the fact it is and adds only the third-party rail
     * cost on top, so `totalFee >= parseFee` is arithmetic rather than an assumption about a
     * provider's rounding.
     *
     * THE RAIL COST IS READ, NOT DERIVED — and this is the whole of F1's second half. It used to be
     * `allInBps − affiliateFeeBpsRealized`. Those two figures are quoted at DIFFERENT rates:
     * `estimatedAllInBps` is `ceil(thirdPartyCost + the NOMINAL affiliate bps)` (`dex.ts` — F2
     * reverted the realized-rate republish), while `affiliateFeeBpsRealized` is the provider's
     * REALIZED rate, ≈1.05× nominal wherever deBridge's 10% ceiling is not binding. So the
     * subtraction never isolated the rail cost — it returned `thirdPartyCost − 0.05 × nominal`, which
     * on a 4.2-bps corridor reads +5 at $0.77, **−35** at $1.25, −20 at $2, −1 at $9 and +4 at
     * $1,000. Every negative one tripped the old `railBps >= 0` guard into the non-netted fallback,
     * whose total the display clamp (`FeeSection/dexTotal.ts:131`) then lifted onto `parseFee` — so
     * "Total fees" and "Parse DeFi fee" rendered the same number and the rail cost read as FREE. The
     * two positives were simply wrong: $1,000 × 4 bps shows $2.00 where the truth is $2.02.
     *
     * `breakdown.thirdPartyBps` is that cost as the backend computed it (`dex.ts:1179`), to 2 dp —
     * protocol + taker + opex + buffer. `totalFee >= parseFee` holds ARITHMETICALLY here GIVEN
     * `thirdPartyBps >= 0` — that is NOT "by construction": the field derives from
     * `corridor_fee_cache`, whose `taker_bps` / `opex_usd` are written from unvalidated provider cost
     * lines (`fee-oracle.ts:666-684` takes `TakerMargin.payload.feeBps` and
     * `EstimatedOperatingExpenses.payload.{usd,amountInUsd,feeAmount}` verbatim, with no sign check).
     * A malformed or adversarial provider line can make either negative; the deleted `railBps >= 0`
     * guard used to absorb that incidentally. The non-negativity premise is therefore enforced HERE —
     * `additiveTotalOk` below requires `thirdPartyBps >= 0` as well as finite — rather than assumed of
     * the backend. Do not delete this conjunct as "redundant with the old guard": it is the only thing
     * standing between a negative rail and a total that reads below the Parse fee, re-firing the
     * `FeeSection/dexTotal.ts:131` clamp this paragraph would otherwise declare unreachable.
     *
     * THE TWO REMAINING FALLBACKS both take today's non-netted form, `(allInBps / 10000) × destAmt`:
     * an OLDER BACKEND that publishes no `thirdPartyBps` at all, and a NEGATIVE `thirdPartyBps` from a
     * provider cost line that failed to validate its sign. `Number.isFinite(thirdPartyBps) &&
     * thirdPartyBps >= 0` is a presence-AND-sign check, not a resurrected rate guard: it is
     * fail-CLOSED on `undefined` / `null` / a non-numeric body / a negative number, so any of the four
     * yields today's non-netted form rather than rendering `NaN` — or an understated total — as a fee
     * on the last screen before a signature.
     */
    const thirdPartyBps = dexFeeEstimate.breakdown?.thirdPartyBps
    const additiveTotalOk = netOfAffiliate && Number.isFinite(thirdPartyBps) && (thirdPartyBps as number) >= 0
    return {
      totalFee: fmt(additiveTotalOk
        ? affiliateFeeAmt + (destAmt + affiliateFeeAmt) * (thirdPartyBps as number) / 10000
        : (allInBps / 10000) * destAmt),
      // The provider's REPORTED skim, not a rate re-applied to a base it was not quoted against.
      parseFee: fmt(netOfAffiliate ? affiliateFeeAmt : (affBps / 10000) * destAmt),
      // Source-sized Parse fee in USD when the backend provides it (near_1click appFee is a SOURCE-token
      // cut, so the dest-sized parseFee above understates it). FeeSection prefers this when present.
      parseFeeUsd: dexFeeEstimate.parseFeeUsd,
      affiliateBps: affBps,
      minimumReceived: dexMinReceived,
      currency: selectedDestToken,
      warningLevel,
      estimatedTimeMin: 1,
      // Source-side cost disclosure (deBridge flat fee + true "you'll pay"). Present on routes where
      // the backend computed it (cross-chain with a live quote); undefined → panel shows bps-only.
      feesTotalUsd: dexFeeEstimate.feesTotalUsd,
      networkFeeUsd: dexFeeEstimate.networkFeeUsd,
      tronEnergyFeeUsd: dexFeeEstimate.tronEnergyFeeUsd,
      tronHasEnergy: dexFeeEstimate.tronHasEnergy,
      fixFeeNative: dexFeeEstimate.fixFeeNative,
      fixFeeSymbol: dexFeeEstimate.fixFeeSymbol,
      fixFeeUsd: dexFeeEstimate.fixFeeUsd,
      youPaySource: dexFeeEstimate.youPaySource,
      youPayUsd: dexFeeEstimate.youPayUsd,
      sourceCurrency: selectedSourceToken,
    }
    // #261 F5 — sourceChain/destinationChain were NOT in this list, and the same-chain conjunct above
    // reads both. `react-hooks/exhaustive-deps` is 'warn' here (eslint.config.mjs:38), so a stale memo
    // across a chain switch would not have failed the build — it would have kept applying the net-fee
    // formulas to a corridor that had already become cross-chain.
  }, [atomicSameChain, isDexRoute, dexFeeEstimate, estimatedReceived, estimatedReceivedDetail.driver, dexMinReceived,
    selectedDestToken, selectedSourceToken, bridgeState.amount, debouncedDexAmount])

  // Contract MIN_AMOUNT is 1 USDC (1_000_000 subunits). Warn when post-fee amount is below it.
  const amountTooLowAfterFees = useMemo(() => {
    if (isDexRoute) return false
    if (!feeEstimate || !bridgeState.amount) return false
    // Prefer the backend's net-min truth (net after Parse% + network + Circle maxFee < $1, mirroring
    // ParseCctpBridge.sol:381) so the CTA greys BEFORE signing — no on-chain revert. Fall back to the
    // local receive math for older backends that don't return the flag.
    if (feeEstimate.belowMin) return true
    const recv = parseFloat(estimatedReceived)
    return recv > 0 && recv < 1.0
  }, [isDexRoute, feeEstimate, bridgeState.amount, estimatedReceived])

  // Native-balance pre-check for DEX routes: bridging needs native for the deBridge flat fee + gas
  // (and the send itself when the source IS native). If the user's native balance can't cover it,
  // surface the reason on the CTA instead of letting them hit a raw wallet "insufficient funds".
  // Returns the shortfall context, or null when it's fine / unknown (balance loading → don't block).
  const insufficientNativeForFees = useMemo<{ required: number; symbol: string; held: number } | null>(() => {
    if (!isDexRoute || !dexFeeEstimate) return null
    const gas = parseFloat(dexFeeEstimate.gasEstimateNative ?? '0') || 0
    const feePart = isNativeSrc
      // #261 F7-6 — this read "input + opex + fixFee". That comment was STALE and materially wrong:
      // under fixed-input the backend sets youPaySource = give + fixFee ONLY (dex.ts:1197-1198), with
      // NO opex — the opex is already inside the reduced receive. Verified in source 2026-08-04.
      ? (parseFloat(dexFeeEstimate.youPaySource ?? '0') || 0)   // input + fixFee, all in native
      : (parseFloat(dexFeeEstimate.fixFeeNative ?? '0') || 0)   // flat fee only, in the chain's native
    // gasEstimateNative is a CONSERVATIVE ceiling: the backend panel reserves a padded gas LIMIT × the
    // 1.5× price buffer (~1.5× stricter than real gas). A bare compare here false-blocks thin-balance
    // transfers when gas ISN'T spiking. Discount ONLY the padded gas portion so the button gate tracks
    // real gas; the deBridge fixFee (feePart) is a genuine native cost and stays at full weight.
    // Fail-safe: a truly underfunded send can never lose funds — native-source is re-gated by the backend
    // (INSUFFICIENT_NATIVE_FOR_FEES 400 pre-sign), and stable-source (backend gas check skipped) is caught
    // by the WALLET rejecting an unaffordable tx, which broadcasts nothing and spends zero gas.
    const required = feePart + gas * 0.7
    if (!(required > 0)) return null
    const nativeBalStr = sourceBalance?.native
    if (nativeBalStr == null) return null                       // balance unknown/loading — don't block
    const nativeBal = parseFloat(nativeBalStr)
    if (!Number.isFinite(nativeBal) || nativeBal >= required) return null
    // #94 Bug 2 — the shortfall is ALWAYS the chain's NATIVE gas token, never the source token. Native
    // source → the source token IS the native. Stable source → prefer the fixFee's symbol when present
    // (cross-chain), else the source CHAIN's native (a same-chain swap has no fixFee → fixFeeSymbol is
    // null; the OLD fallback to selectedSourceToken rendered "Need ~0.5 USDC" for a POL gas shortfall).
    const chainNative = bridgeState.sourceChain ? CHAIN_REGISTRY[bridgeState.sourceChain]?.nativeCurrency : undefined
    const symbol = isNativeSrc
      ? selectedSourceToken
      : (dexFeeEstimate.fixFeeSymbol ?? chainNative ?? selectedSourceToken)
    return { required, symbol, held: Number.isFinite(nativeBal) ? nativeBal : 0 }
  }, [isDexRoute, dexFeeEstimate, isNativeSrc, sourceBalance, selectedSourceToken, bridgeState.sourceChain])

  // Advisory native balance (wei) sent to the backend's on-click affordability check. All supported
  // EVM natives (POL/ETH/BNB) are 18-dec. Guard against malformed/over-precise strings.
  const senderNativeBalanceWei = useMemo<string | undefined>(() => {
    const n = sourceBalance?.native
    if (!n) return undefined
    try { return parseUnits(n as `${number}`, 18).toString() } catch { return undefined }
  }, [sourceBalance])

  // The source-token (ERC-20) balance in display units — used for the opex gate + threaded to the
  // backend gate. Already fetched by the all-balances query (no extra wallet read → 0 RPC CU).
  const sourceTokenBalanceStr = useMemo<string | null>(() => {
    if (selectedSourceToken === 'USDT') return sourceBalance?.usdt ?? null
    if (selectedSourceToken === 'USDC') return sourceBalance?.usdc ?? null
    if (selectedSourceToken === 'USDG') return sourceBalance?.usdg ?? null // #174 — USDG source (RH corridor)
    return null // native handled by senderNativeBalanceWei
  }, [selectedSourceToken, sourceBalance])

  // "USDC is cheaper" nudge (#35, Phase A — qualitative). When the user picks USDT as the SOURCE on a
  // corridor whose USDC equivalent routes via CCTP (no deBridge flat fee) AND they actually hold USDC,
  // offer a one-tap steer to switch. Live-verified case: a USDT source cross-chain routes via deBridge
  // and eats the ~$1.76 fixFee, whereas the USDC source routes via CCTP with no fixFee. The CCTP
  // alternative exists exactly when the destination token is USDC and the two chains differ — same-chain
  // or a non-USDC dest would still re-route via deBridge (no fee win), so those are excluded.
  // Copy stays QUALITATIVE ("skips the ~$1.76 bridge fee") — a live "$X cheaper" figure needs the
 
  // Advisory source-token balance (atomic, 6-dec stables) for the backend's opex gate. Native source
  // → undefined (covered by senderNativeBalanceWei). NO new wallet read.
  const senderTokenBalanceAtomic = useMemo<string | undefined>(() => {
    if (isNativeSrc || sourceTokenBalanceStr == null) return undefined
    try { return parseUnits(sourceTokenBalanceStr as `${number}`, 6).toString() } catch { return undefined }
  }, [isNativeSrc, sourceTokenBalanceStr])

  // Opex-floor gate (cross-chain ERC-20 source). The DLN router pulls give = input + opex; for
  // →Tron native delivery the opex floor is large ($8–27), so a small order is either uneconomical
  // (opex ≥ input → guaranteed loss) or exceeds the wallet's token balance. Computed CLIENT-side from
  // the estimate (opexUsd + input + token balance) so the button explains it pre-click; the server
  // enforces the same at submit (UNECONOMICAL_ROUTE / INSUFFICIENT_TOKEN_FOR_OPEX). Stable source ≈ $1
  // so opexUsd ≈ opex in tokens and input ≈ inputUsd.
  // BETA BUG-1b — is the DEX fee UNAVAILABLE for display? Mirrors FeeSection's own render predicate
  // (index.tsx:102 `dexFee === null` refusal + dexTotal.ts `belowDisplayFloor`) so the CTA gate and the
  // fee card can never disagree: `null` = staleness/no-quote refusal, `belowDisplayFloor` = the fee
  // rounds below the display floor (a dust amount — e.g. 0.001 USDG same-chain → "Fee unavailable"). A
  // fee the card will not state must not be silently submittable behind a green button. `undefined`
  // (no DEX fee surface yet) is NOT unavailable — the dexReceiveNonPositive backstop handles that.
  const dexFeeUnavailable = useMemo<{ blocked: boolean; reason: 'refused' | 'dust' | null }>(() => {
    if (!isDexRoute) return { blocked: false, reason: null }
    if (dexFee === null) return { blocked: true, reason: 'refused' }
    if (dexFee === undefined) return { blocked: false, reason: null }
    return computeDexDisplayTotals(dexFee).belowDisplayFloor
      ? { blocked: true, reason: 'dust' }
      : { blocked: false, reason: null }
  }, [isDexRoute, dexFee])

  /**
   * #337 — IS THE ESTIMATE IN HAND THE ONE THE GATES BELOW ARE ABOUT TO JUDGE THIS AMOUNT WITH?
   *
   * `debouncedDexAmount` IS the `/estimate` query key (:674) and lags `bridgeState.amount` by 500 ms
   * (:667-670). Every amount-dependent gate on the `canSend` prop (:3251) mixes ONE FIELD OF THE
   * QUOTE with LIVE COMPONENT STATE and is re-run by the live one:
   *   · `belowMinHintUsd` (:2212-2221) — `sourceAmountUsd` (stale) vs `minAmountUsd`, deps carry
   *     `bridgeState.amount` (fresh). Cross-chain.
   *   · `sameChainMinGate` inside `opexGate` (:2045-2050) — the identical stale read. Same-chain.
   *   · `insufficientNativeForFees` (:1806-1837) — stale `youPaySource`/`fixFeeNative` vs live balance.
   * Settle at 1000, type 0.5: `inputUsd` is still 1000, `1000 >= 2.25`, no gate fires, and the CTA is
   * GREEN on a $0.50 send into a $2.25-floor route — while `executeTransfer` submits the FRESH amount
   * (:2584). The gate is evaluated against the old amount and the transaction built for the new one.
   *
   * WHY THIS IS A GATE AND NOT A UX NICETY: no backend and no contract re-gates a below-floor
   * deBridge or Jupiter submit. `DEX_CONFIG.dexMinFloorUsd` is read at four sites, all inside
   * `GET /estimate`; `POST /dex/bridge` contains no read of it, and the only submit-time floor there
   * is 1Click's own constant set. Between deBridge's real economic floor and our static one the order
   * BUILDS, IS SIGNED AND EXECUTES. This prop is the only pre-signature check.
   *
   * WHY IT IS NOT FOLDED INTO `dexFeeUnavailable` (spec RULE 4): that memo mirrors FeeSection's own
   * render predicate so the card and the gate can never disagree. Adding a submit-only concern to it
   * would desynchronise them — the #336 R10 split-brain verbatim.
   *
   * COVERS REVERSE BY CONSTRUCTION: reverse mode writes `bridgeState.amount` too (:2534), so it flows
   * through this same comparison with nothing keystroke- or `inputMode`-specific in it.
   *
   * Two derived booleans, deliberately NOT one. They mean different things and a reviewer must be
   * able to tell them apart; the CTA collapses them into one message because from the user's side
   * both are "the quote isn't ready".
   */
  const dexEstimateStale = useMemo<boolean>(() => {
    if (!isDexRoute || !dexFeeEstimate) return false
    // EMPTY-AMOUNT EARLY RETURN — REQUIRED, not decorative. `dexFeeEstimate` stays TRUTHY on a
    // cleared field: the query key is `debouncedDexAmount`, which holds the previous amount for
    // 500 ms after the clear, so `!dexFeeEstimate` does NOT catch this state (the same retention
    // :1542-1544 documents about this query). Defensive and cheap — it keeps the memo false on an
    // empty form so the label chain is untouched there (ADDENDUM A §6.1). It cannot enable anything —
    // `canSend` (:2707) already requires a positive amount. Pinned by belowMinStaleGate T5b.
    if (!(parseFloat(bridgeState.amount || '0') > 0)) return false
    return !dexEstimateMatchesAmount({ amount: bridgeState.amount, estimateAmount: debouncedDexAmount })
  }, [isDexRoute, dexFeeEstimate, bridgeState.amount, debouncedDexAmount])

  /**
   * #337 G1 — a DEX route with a real amount and NO estimate in hand. Distinct from
   * `dexEstimateStale`: that one means "the estimate we have is for a different amount", this one
   * means "we have none". Both must block, and for the same reason.
   *
   * WITHOUT THIS, THE FIX ABOVE ONLY DISPLACES THE WINDOW 500 ms. The estimate query carries
   * `staleTime` and a cold-corridor `refetchInterval` but NO `placeholderData` and NO
   * `keepPreviousData` (:673-708), so the instant the debounce fires and the key changes,
   * `dexFeeEstimate` is `undefined` — and `belowMinHintUsd` (:2213), `opexGate` (:2002),
   * `insufficientNativeForFees` (:1807), `dexFee` (:1623 → `dexFeeUnavailable`) and
   * `dexEstimateStale` itself ALL early-return on it. `canSend` would be GRANTED with no quote
   * backing it for the whole `/estimate` round-trip, and UNBOUNDED on a cold corridor where the
   * queryFn returns null and the 8 s retry keeps it undefined.
   *
   * THE ACCEPTED COST, stated so it is not a surprise in review: THE FIRST SEND ON A ROUTE NOW WAITS
   * FOR ITS FIRST QUOTE. The CTA is disabled during the initial round-trip where today it is enabled.
   * `isQuoteLoading` (:3250) already knows this state and deliberately does not gate on it; we now
   * gate on it here, in BridgePage, where it is scoped — NOT by wiring `isQuoteLoading` into
   * SwapCard's `ctaDisabled`, which would be a SwapCard-wide behaviour change. Operator accepted this
   * false-reject at GATE 2; it is the only one accepted, and the alternative is a CTA that can be
   * clicked with no quote at all.
   */
  const dexEstimateMissing = useMemo<boolean>(() => (
    isDexRoute && parseFloat(bridgeState.amount || '0') > 0 && !dexFeeEstimate
  ), [isDexRoute, bridgeState.amount, dexFeeEstimate])

  /**
   * #337 R1-F1 — LABEL ONLY. `dexEstimateMissing` (above) is correct and UNCHANGED: `!dexFeeEstimate`
   * blocks `canSend` identically whether the estimate is `undefined` (react-query's honest "not
   * fetched for this key yet" — resolves in one round-trip) or `null` (the queryFn ran and returned
   * nothing, :697 — a STANDING refusal, e.g. #429's Tron 404 "No fee data for corridor"). What must
   * NOT stay identical is the STRING: the cold-corridor retry (`refetchInterval: 8000`, :707) means a
   * refused corridor is retried FOREVER, and 'Updating quote…' promises transience it doesn't have.
   *
   * Three label states for `dexEstimateMissing` (dexEstimateStale always takes the first — it can only
   * be true while `dexFeeEstimate` is truthy, so the null/undefined split below never applies to it):
   *   1. `dexFeeEstimate === undefined`                                    -> 'Updating quote…'
   *   2. `dexFeeEstimate === null`, less than DEX_ESTIMATE_NULL_UNAVAILABLE_MS since the first null
   *      observed for THIS query key                                      -> 'Quote retrying…'
   *   3. `dexFeeEstimate === null`, at or past that threshold              -> 'Quote temporarily unavailable'
   *
   * MECHANISM — NO TIMER. `refetchInterval` (:707) already guarantees a re-render at least every 8s
   * while the estimate is null, which lands the 2→3 transition within 8s of the threshold on its own;
   * a setTimeout/setInterval here would be a second, redundant clock racing the query's own. The ref
   * below records WHEN the first `null` was observed FOR THIS QUERY KEY — the key mirrors the estimate
   * query's own (:674) FIELD FOR FIELD, including `tronAddress` (#337 GATE-4 FIX F2 — a prior version
   * omitted it, so a wallet connect/switch mid-null-window on a Tron-source corridor inherited the old
   * clock for a stream that had genuinely just started) — and is only ever READ for the label, never
   * scheduled against.
   *
   * THRESHOLD is named (not a magic `120000`) so it is greppable, and local rather than module-scope
   * so this fix stays confined to its own insertion point instead of shifting every line below it.
   *
   * #337 GATE-4 FIX (F3) — three benign edges of this render-time ref write, named rather than fixed
   * (a timer or a query change was explicitly out of scope for this pass):
   *  (a) StrictMode double-render: this write happens during render, so under StrictMode's deliberate
   *      double-invoke the clock can start on a render that never commits. Direction is harmless — it
   *      only ever starts the clock EARLIER than the committed render would have, never later, so the
   *      120s window can only be conservative (shorter), never leak past its threshold.
   *  (b) Remount resets it: the ref is fresh per mount, so an unmount/remount on an already-null
   *      corridor (e.g. a route/token picker that unmounts BridgePage) regresses state 3 back to
   *      state 2 — `'Quote temporarily unavailable'` reverts to `'Quote retrying…'` for another 120s
   *      even though the corridor's actual refusal is unchanged.
   *  (c) Warm→null-again reuses the old clock: if the estimate goes null → non-null → null again for
   *      the SAME key (a corridor that flaps), the ref is only cleared on a KEY change (below), not on
   *      a value change — so the second null does not reset `at`, and state 3 can appear immediately
   *      on the second null if the first null's clock had already run past the threshold before it
   *      recovered.
   * All three are label-only — the gate (`dexEstimateMissing`, above) is unaffected in every case.
   */
  const DEX_ESTIMATE_NULL_UNAVAILABLE_MS = 120_000
  const dexEstimateNullKey = `${bridgeState.sourceChain}|${bridgeState.destinationChain}|${selectedSourceToken}|${selectedDestToken}|${debouncedDexAmount}|${tronAddress ?? ''}`
  const dexEstimateNullSinceRef = useRef<{ key: string; at: number } | null>(null)
  if (dexEstimateNullSinceRef.current && dexEstimateNullSinceRef.current.key !== dexEstimateNullKey) {
    dexEstimateNullSinceRef.current = null // key changed — a stale corridor's clock must not leak into a fresh one
  }
  if (dexFeeEstimate === null && !dexEstimateNullSinceRef.current) {
    dexEstimateNullSinceRef.current = { key: dexEstimateNullKey, at: Date.now() }
  }
  const dexEstimateMissingLabel =
    dexFeeEstimate === undefined ? 'Updating quote…'
    : (dexEstimateNullSinceRef.current && Date.now() - dexEstimateNullSinceRef.current.at >= DEX_ESTIMATE_NULL_UNAVAILABLE_MS)
      ? 'Quote temporarily unavailable'
      : 'Quote retrying…'

  const opexGate = useMemo<{ kind: 'uneconomical' | 'insufficientToken' | 'belowMinimum' | 'reduceAmount' | 'quoteUnavailable' | 'routeUnsupported'; needAmt: number; minUsd: number; maxSendable?: number; message?: string; code?: string } | null>(() => {
    // #614 (reviewer B7) — the reverse path's own refusal marker, checked BEFORE the estimate early-return
    // because the send field is empty (no forward estimate) exactly when it is set.
    if (isDexRoute && reverseRouteUnavailable) {
      return { kind: 'routeUnsupported', needAmt: 0, minUsd: 0, message: reverseRouteUnavailable, code: 'SAME_CHAIN_ROUTE_UNAVAILABLE' }
    }
    if (!isDexRoute || !dexFeeEstimate) return null
    // #131 C — POLICY-refused corridor (Tron USDC cross-chain). Distinct from every pricing gate below:
    // no amount makes it work, so the message must name the supported path (swap to Tron USDT) rather
    // than the "amount too low" copy the quoteUnavailable branch would otherwise produce. Checked FIRST
    // because the backend also sets viability.level 'block' on this marker, which would otherwise be
    // caught by the quoteUnavailable branch and mislabel it. Message comes from the server verbatim.
    if (dexFeeEstimate.routeUnsupported) {
      return { kind: 'routeUnsupported', needAmt: 0, minUsd: 0, message: dexFeeEstimate.routeUnsupportedReason, code: dexFeeEstimate.routeUnsupportedCode }
    }
    // HARD below-minimum (1Click 400 marker): the backend refused to quote because input < the route floor.
    // Gate the CTA for ANY source (incl. native) — this must be checked BEFORE the isNativeSrc / same-chain
    // early-returns below, which would otherwise let a native-source below-min through ungated.
    if (dexFeeEstimate.belowMinimum) {
      return { kind: 'belowMinimum', needAmt: 0, minUsd: dexFeeEstimate.minAmountUsd ?? 0 }
    }
    // UNPRICEABLE ROUTE (deBridge 400 ERROR_LOW_GIVE_AMOUNT, or any provider failure): there is no quote,
    // so the fee/opex math below has nothing real to work with. Like belowMinimum this MUST be checked
    // BEFORE the isNativeSrc / same-chain early-returns — otherwise a native-source unpriceable route
    // (e.g. solana→tron SOL→TRX) falls straight through ungated with the CTA still enabled over a "0.00"
    // receive. viability.level 'block' is the backend's own verdict and is honoured identically.
    if (dexFeeEstimate.quoteUnavailable === true || dexFeeEstimate.viability?.level === 'block') {
      return { kind: 'quoteUnavailable', needAmt: 0, minUsd: dexFeeEstimate.minAmountUsd ?? 0 }
    }
    // BETA BUG-1a — SAME-CHAIN SOURCE-TOKEN BALANCE. The same-chain early-return just below skips the
    // cross-chain OPEX floor, but must NOT skip the balance check: a same-chain RH swap of 1000 USDG on a
    // 5.85 balance would otherwise leave the CTA green and dead-end at a wallet "insufficient funds" revert.
    // Cross-chain keeps its own opex-aware balance gate below; this covers the same-chain path that never
    // reaches it. Extracted to sameChainTokenBalanceGate for deterministic unit tests (utils/sameChainGate).
    const sameChainShortfall = sameChainTokenBalanceGate({
      isNativeSrc,
      sameChain: atomicSameChain,
      sourceTokenBalanceStr,
      amount: bridgeState.amount,
    })
    if (sameChainShortfall) return sameChainShortfall
    // SAME-CHAIN MINIMUM. The same-chain early-returns just below skip the cross-chain opex floor AND the
    // cross-chain belowMinHintUsd, so a same-chain amount below the route's static per-provider floor
    // (dexFeeEstimate.minAmountUsd) otherwise went ungated with only a bare "Amount too low". Runs AFTER
    // the balance gate (insufficient balance is the more specific message) but BEFORE the isNativeSrc /
    // same-chain early-returns so a same-chain NATIVE source (e.g. Jupiter SOL→USDC) is min-gated too.
    // Compares the input's USD value (native ≠ $1 → sourceAmountUsd; stable ≈ $1 → raw amount); fails OPEN
    // on cross-chain / no-floor / unpriced input. belowMinHintUsd suppresses itself when opexGate is set,
    // so the min is never shown twice.
    const sameChainMin = sameChainMinGate({
      sameChain: atomicSameChain,
      minAmountUsd: dexFeeEstimate.minAmountUsd,
      inputUsd: dexFeeEstimate.sourceAmountUsd ?? parseFloat(bridgeState.amount || '0'),
    })
    if (sameChainMin) return sameChainMin
    if (isNativeSrc) return null
    if (atomicSameChain) return null // ATOMIC same-chain: no opex floor (a same-chain deposit flow keeps the cross-chain gates, #614)
    const input = parseFloat(bridgeState.amount || '0')
    if (!(input > 0)) return null
    const opexUsd = dexFeeEstimate.opexUsd ?? 0
    const minUsd = dexFeeEstimate.minAmountUsd ?? 0
    if (opexUsd >= input) return { kind: 'uneconomical', needAmt: 0, minUsd }
    // #261 F7-4 / F8-2 — GIVE MODEL. The legacy `give = input + opexUsd` encodes deBridge's PREPEND
    // behaviour, which F8's A2 falsified: `prependOperatingExpenses=false` (debridge-client.ts:159)
    // means the source pull IS the typed input and the opex comes out of the OUTPUT instead. Under the
    // flag, `give == input` and the token balance is NOT reduced by opex — otherwise a legitimate
    // full-balance send (the new stable Max) is blocked by a phantom `input + opex` requirement that
    // the router never actually charges. The `uneconomical` guard above is untouched: opex still eats
    // the receive, so `opexUsd >= input` is still a guaranteed-loss trade.
    const f7GiveEqualsInput = isNativeMaxGasReserveEnabled()
    const give = f7GiveEqualsInput ? input : input + opexUsd
    if (sourceTokenBalanceStr == null) return null // balance loading — don't block
    const tokenBal = parseFloat(sourceTokenBalanceStr)
    if (Number.isFinite(tokenBal) && tokenBal < give) {
      // Legacy: the route fee (opex) is pulled ON TOP of the send amount, so max sendable = balance −
      // fee. Raising the input can never close the gap when the balance is short — it just raises the
      // target (Shane's "ever-increasing amount"). Under give==input the whole balance is sendable, so
      // the only way to reach here is a typed amount above the balance, and the guidance is simply
      // "reduce to what you hold". Distinguish two cases so the message names the real cause:
      const maxSendable = f7GiveEqualsInput ? Math.max(0, tokenBal) : Math.max(0, tokenBal - opexUsd)
      // In range: only suggest "reduce to X" when X is a GENUINELY valid, economical send — at/above the
      // route floor (minUsd) AND strictly above opex. Without the `> opexUsd` guard, a high-opex route
      // (→Tron opex $13–27, minUsd ~$2) would suggest e.g. "~$3", whose requote then trips the
      // `uneconomical` branch → a contradictory "reduce to ~$3" ↔ "fees exceed amount" loop. The `>= 0.01`
      // floor stops a zero-min route from rendering "send up to ~0.00". Otherwise they're simply short.
      if (maxSendable >= minUsd && maxSendable > opexUsd && maxSendable >= 0.01) {
        return { kind: 'reduceAmount', needAmt: 0, minUsd, maxSendable }
      }
      // Out of range: even the max sendable is below the route min / uneconomical — they're simply short.
      return { kind: 'insufficientToken', needAmt: Math.ceil(give), minUsd, maxSendable }
    }
    return null
  }, [isDexRoute, dexFeeEstimate, isNativeSrc, atomicSameChain, reverseRouteUnavailable, bridgeState.amount, sourceTokenBalanceStr])

  // #132 — the 'warn' viability tier is deliberately NOT surfaced. A disclosure panel + acknowledgment
  // was built for #131 and then removed by product decision (Josh + Shane): a threshold warning on the
  // primary flow is the same clutter as the "Elevated" fee badge and the USDC nudge banner, both already
  // removed for panicking users. The swap card ALREADY shows everything needed to judge the trade —
  // "YOU RECEIVE ≈ $2.48" against "≈ $2.21 total fees" on a $2.83 send — so a checkbox only restates it.
  //
  // The backend still computes `warn` (and #131's corrected ALL-IN arithmetic, which is what makes
  // 'block' fire on a genuinely losing trade); the client simply renders nothing for it. 'block' remains
  // a SILENT hard disable — the CTA just doesn't enable, with no banner. See #132 for the deferred
  // question of whether there is a non-intrusive way to steer users off uneconomical small transfers.

  // Fee-aware "Max" reserve.
  //
  // ── FLAG OFF (today's production behaviour) ──────────────────────────────────────────────────────
  // On a cross-chain DEX route with a STABLE source, the route fee (opex) is pulled ON TOP of the send
  // amount, so Max = full balance is always over-budget (Shane: "stables should be able to send max if
  // they're paying gas"). Reserve the opex so Max = balance − opex is sendable. CCTP, same-chain, and
  // native source all reserve nothing. Stable source ≈ $1, so opexUsd ≈ opex in tokens.
  //
  // ── FLAG ON (#261 F7) ────────────────────────────────────────────────────────────────────────────
  // The reserve is the ON-TOP NATIVE costs only — NEVER opex, NEVER the Parse %. Both of those come
  // out of the RECEIVE under fixed-input (`give == input`, debridge-client.ts:159), so reserving them
  // from the SEND understates Max and double-counts.
  //
  //   native source → padded gas + the deBridge flat fixFee (both native, both leave the wallet
  //                   ON TOP of the give). This is the whole fix: reserving gas ALONE does not
  //                   unblock Max. With reserve = g the native gate wants
  //                   `B >= (B−g) + fixFee + 0.7g` = `B + fixFee − 0.3g`, which still fails unless
  //                   0.3g >= fixFee. With reserve = g + fixFee it wants `B >= B − 0.3g` — passes,
  //                   with 0.3g of margin against a gas wiggle between tap and sign (I-3).
  //   stable source → NOTHING. Max = the full balance; its gas and fixFee are separate NATIVE
  //                   payments, and its opex is on the receive. `opexGate` below is realigned to the
  //                   same give==input model in the same flag branch — the two are COUPLED, because
  //                   zeroing this reserve alone would make a full-balance Max trip
  //                   `tokenBal < input + opexUsd` there.
  //
  // FAILS CLOSED. No usable `gasEstimateNative` → reserve 0, i.e. exactly today's behaviour (Max fills
  // the full balance and the existing gate blocks it). That covers Solana/Tron, which the backend
  // excludes outright (dex.ts:1222), and the dex.ts:1242 catch. A fixFee-ONLY reserve would be worse
  // than none: it under-reserves, so Max would fill an amount the wallet then rejects at signing.
  const maxSendReserve = useMemo<number>(() => {
    if (!isDexRoute) return 0
    if (!isNativeMaxGasReserveEnabled()) {
      if (isNativeSrc) return 0
      if (atomicSameChain) return 0
      const opexUsd = dexFeeEstimate?.opexUsd ?? 0
      return opexUsd > 0 ? opexUsd : 0
    }
    if (!isNativeSrc) return 0
    const gas = parseFloat(dexFeeEstimate?.gasEstimateNative ?? '0') || 0
    if (!(gas > 0)) return 0
    // UNITS (F7-1): everything added here is in NATIVE TOKEN units. `opexUsd` is USD and must never
    // reach this sum (the #94 / 3600× class). The fixFee is the source CHAIN's native token, which for
    // a native source IS the source token — but assert that rather than assume it: a symbol mismatch
    // means our units model is wrong, so fail CLOSED to today's behaviour instead of adding a POL
    // figure to an ETH balance. A same-chain swap has no fixFee (symbol null) → gas only.
    const fixFee = parseFloat(dexFeeEstimate?.fixFeeNative ?? '0') || 0
    const fixSym = dexFeeEstimate?.fixFeeSymbol
    // Fail closed on a mismatched symbol AND on an UNKNOWN one. A missing `fixFeeSymbol` alongside a
    // real non-zero fee is reachable — dex.ts:1158-1159 formats the fee with a DEFAULTED 18 decimals
    // while the symbol comes from a `supported_tokens` lookup that can miss — so trusting the absent
    // case would be trusting a figure whose units we could not name. A same-chain swap has no fixFee
    // at all (symbol null AND fee 0), which is why the guard keys on the fee being non-zero.
    if (fixFee > 0 && fixSym !== selectedSourceToken) return 0
    return gas + (fixFee > 0 ? fixFee : 0)
  }, [isDexRoute, isNativeSrc, atomicSameChain, dexFeeEstimate, selectedSourceToken])

  // #261 F7 item 4 — the NATIVE middle zone: the user HOLDS the balance but not the gas headroom.
  //
  // RELABEL ONLY (operator decision, GATE 1, 2026-08-04). The blocking predicate is unchanged: this
  // derives FROM `insufficientNativeForFees`, which still uses its ×0.7 gas discount and still drives
  // `canSend`. So no amount that sends today is newly blocked (I-4), and gasGate.test.tsx stays green.
  // The spec's literal `B >= a > B − reserve` rule was NOT implemented: the padded reserve is stricter
  // than the ×0.7 gate, so keying the block on it would newly reject the band in between — the #94
  // Bug 2 over-reserve class the spec's own falsifier names.
  //
  // What changes is only the CTA COPY. "Need ~0.004 ETH for gas — you have 0.5" is actively confusing
  // to someone holding 0.5 ETH; "Reduce — you can send up to 0.4959 ETH" names the real action. An
  // amount ABOVE the balance keeps the gas label (they genuinely lack the native), per F7-3.
  const nativeReduceTo = useMemo<number | null>(() => {
    if (!isNativeMaxGasReserveEnabled()) return null
    if (!isNativeSrc || !insufficientNativeForFees) return null
    if (!(maxSendReserve > 0)) return null
    const bal = parseFloat(sourceBalance?.native ?? '')
    const entered = parseFloat(bridgeState.amount || '0')
    if (!Number.isFinite(bal) || !(entered > 0) || entered > bal) return null
    // FLOOR to the same 6dp the Max button uses (SwapCard `dp = srcIsNative ? 6 : 2`), never round.
    // The number in this label is a promise the Max button has to keep — rounding UP would name an
    // amount Max cannot fill, which is the I-1 (WYSIWYG) violation in miniature.
    const sendable = floorToDecimals(Math.max(0, bal - maxSendReserve), 6)
    if (!(sendable > 0)) return null
    // ROUTE-FLOOR GUARD — mirrors the stable `reduceAmount` sibling above (:1185), whose comment names
    // exactly the failure this reintroduced without it: telling the user to reduce to an amount that
    // is then rejected as below the route minimum ("reduce to X" -> "Amount too low — minimum Y").
    // Guiding someone DOWN into a different rejection is worse than the gas label it replaces, so
    // when the sendable amount cannot clear the floor we fall through to that label unchanged.
    // The input's USD value is the comparable unit here — a native amount is not $1-per-token — so
    // scale by the estimate's own price rather than comparing tokens to dollars.
    const minUsd = dexFeeEstimate?.minAmountUsd ?? 0
    if (minUsd > 0) {
      const enteredUsd = dexFeeEstimate?.sourceAmountUsd ?? 0
      // Price per source token, derived from the estimate we already have. Guard the divide.
      const pricePerToken = entered > 0 && enteredUsd > 0 ? enteredUsd / entered : 0
      if (pricePerToken > 0 && sendable * pricePerToken < minUsd) return null
    }
    return sendable
  }, [isNativeSrc, insufficientNativeForFees, maxSendReserve, sourceBalance, bridgeState.amount, dexFeeEstimate])

  // Soft "Min ~$X on this route" hint when no amount is entered yet (cross-chain DEX, nonzero floor).
  const dexMinHintUsd = useMemo<number | null>(() => {
    if (!isDexRoute || !dexFeeEstimate) return null
    if (atomicSameChain) return null
    if (parseFloat(bridgeState.amount || '0') > 0) return null
    const m = dexFeeEstimate.minAmountUsd ?? 0
    return m > 0 ? m : null
  }, [isDexRoute, dexFeeEstimate, atomicSameChain, bridgeState.amount])

  // "Minimum ~X <token>" hint for the MAIN form when the user HAS typed a cross-chain DEX amount that's below the
  // route floor (minAmountUsd = opex floor + buffer the backend already returns; ~$2 for Tron, 0 elsewhere).
  // This is the entered-but-too-small counterpart to dexMinHintUsd (empty amount). Suppressed when opexGate
  // already puts a min message on the CTA (uneconomical / insufficient balance) so the min isn't shown twice.
  // Reuses the backend's minAmountUsd verbatim — never recomputed here.
  const belowMinHintUsd = useMemo<number | null>(() => {
    if (!isDexRoute || !dexFeeEstimate || opexGate) return null
    if (atomicSameChain) return null
    const min = dexFeeEstimate.minAmountUsd ?? 0
    if (!(min > 0)) return null
    // Compare the input's USD value: native source ≠ $1 (use sourceAmountUsd); stable source ≈ $1 (raw amount).
    const inputUsd = dexFeeEstimate.sourceAmountUsd ?? parseFloat(bridgeState.amount || '0')
    if (!(inputUsd > 0) || inputUsd >= min) return null
    return min
  }, [isDexRoute, dexFeeEstimate, opexGate, atomicSameChain, bridgeState.amount])

  // SINGLE SOURCE OF TRUTH for "minimum to send" in USD (#27). Both the chain-picker balance gate
  // (minAmountFlat) and the CTA below-min copy (belowMinHintUsd → the ctaLabel "minimum $X") derive from
  // THIS one value, so the two surfaces can never disagree. (#334 FE-3 retired the duplicate swap-card
  // under-Send hint; the minimum now lives only in the CTA.) Prefers the LIVE quote's computed floor:
  //   • DEX routes  → dexFeeEstimate.minAmountUsd (opex floor + buffer; 0 = no floor on this route)
  //   • CCTP routes → feeEstimate.minSourceUsd (smallest source whose net clears the product floor)
  // Falls back to the backend config base (configMinUsd, the CCTP MIN_AMOUNT) when no route quote is
  // live — e.g. while the user is switching source chains. Never recomputed from raw fees here.
  const sendMinUsd = useMemo<number>(() => {
    if (isDexRoute) {
      const m = dexFeeEstimate?.minAmountUsd ?? 0
      if (m > 0) return m
    } else if (feeEstimate?.minSourceUsd != null && feeEstimate.minSourceUsd > 0) {
      return feeEstimate.minSourceUsd
    }
    // #60: the raw CCTP MIN_AMOUNT (configMinUsd, ~$1) EXCLUDES our fee. The live-quote paths above are
    // already fee-inclusive (minSourceUsd = net-clears-floor; dex minAmountUsd = opex+buffer). Bake the
    // #28 ~$0.10 platform floor into the PRE-QUOTE fallback too, so the displayed min (~$1.10) reflects
    // what the user actually needs incl. our take — and matches the fee-aware paths once a quote loads.
    return configMinUsd + PLATFORM_MIN_FEE_USD
  }, [isDexRoute, dexFeeEstimate, feeEstimate, configMinUsd])

  // Denominate a "minimum to send" figure (#27). The backend min is a USD value; when the SEND token is a
  // stablecoin (USDC/USDT, 1 token ≈ $1) show it in TOKEN terms ("~2 USDC") so the unit matches what the
  // user actually sends and stays OFF the app's fiat currency picker (a "$" clashes with GBP/EUR/etc.).
  // A native source keeps the USD "$": a USD floor can't be expressed in native units without a price, and
  // we deliberately avoid FX in this hint. The chain-picker + swap-card below-min hints use the same rule.
  // #60: always show 2 decimals (~1.10, not ~1.1) — the tilde signals a cent-level ballpark, so the
  // 2nd decimal must be visible; the min value is fee-inclusive (see sendMinUsd) so users see our take.
  const denomMin = (v: string | number): string =>
    (selectedSourceToken === 'USDC' || selectedSourceToken === 'USDT')
      ? `~${Number(v).toFixed(2)} ${selectedSourceToken}`
      : `~$${Number(v).toFixed(2)}`

  // Give-amount disclosure for the review step (cross-chain ERC-20 source): the wallet pulls
  // input + opex = give. Stable source ≈ $1 so opexUsd ≈ opex in tokens. Null when there's no opex.
  const dexGiveDisclosure = useMemo<{ input: number; opex: number; give: number } | null>(() => {
    if (!isDexRoute || !dexFeeEstimate || isNativeSrc) return null
    // #261 F7-4 / F8-2 — this is the SECOND consumer of the `give = input + opex` model that F8's A2
    // falsified, and it must move with `opexGate`, not after it. Under fixed-input the wallet is
    // pulled for the input alone (`prependOperatingExpenses=false`, debridge-client.ts:159), so
    // there is no prepended opex to disclose. Leaving it live under the flag made the review modal
    // tell a user with exactly 100 USDC that they would "pay 100.85" — on the very full-balance
    // stable send this change exists to enable. Caught in review: realigning one consumer of a
    // falsified model and not the other is worse than realigning neither, because the two then
    // contradict each other on the same journey.
    // #261 F8 / C-9 + G3 (E2) — the POSITIVE direction, and it is AND-guarded on purpose. Showing a
    // corrected bare-`input` row is a CLAIM about the debit, so it may only be made on a build where
    // F7 has already realigned `opexGate` and `maxSendReserve` to the same `give == input` model.
    // Checked FIRST because the two suppressions below would otherwise swallow it.
    //
    // SOLANA IS EXCLUDED, and that is a real bug this closes rather than a stylistic omission. The
    // row's whole purpose is the ERC-20 approval caveat ("don't reduce the approval — the swap pulls
    // the full amount"). A Solana-USDC source has no allowance to reduce; today's guard excludes only
    // `isNativeSrc`, and Solana USDC is not native, so the row renders an ERC-20 warning about a
    // mechanism that does not exist on that chain. Tron does use approvals, so it keeps the row.
    if (isNativeMaxGasReserveEnabled() && isCrossChainFeeDisplayEnabled()) {
      if (bridgeState.sourceChain === 'solana') return null
      if (atomicSameChain) return null
      const input = parseFloat(bridgeState.amount || '0')
      if (!(input > 0)) return null
      return { input, opex: 0, give: input }
    }
    if (isNativeMaxGasReserveEnabled()) return null
    // #261 F8 / C-9 — F8 ALONE must suppress this row too, even though F7 already does today.
    // Without this, an F8-ON/F7-OFF build renders `Total fees $1.01` beside
    // `You pay 2.50 + 1.0069 fee = 3.5069`, and those two AGREE to the cent — because on the stable
    // branch `feesTotalUsd − fixFeeUsd ≡ realOpexUsd` exactly (dex.ts:1248 vs :1264). The corrected
    // total would then CORROBORATE the wrong debit, and today's visible disagreement between the two
    // rows is the only signal the row is wrong at all. That state is reachable only by unsetting
    // VITE_NATIVE_MAX_GAS_RESERVE (a repo variable since 2026-08-04), so this is a regression guard —
    // including for the eventual "delete the toggles, make them defaults" step, when flags come out
    // one at a time. Suppression WITHDRAWS a claim, so unlike the branch above it needs no AND-guard.
    if (isCrossChainFeeDisplayEnabled()) return null
    if (atomicSameChain) return null
    const input = parseFloat(bridgeState.amount || '0')
    const opex = dexFeeEstimate.opexUsd ?? 0
    if (!(input > 0) || !(opex > 0)) return null
    return { input, opex, give: input + opex }
  }, [isDexRoute, dexFeeEstimate, isNativeSrc, bridgeState.sourceChain, atomicSameChain, bridgeState.amount])

  // Auto-estimate fees when inputs change.
  // CCTP ONLY: the CCTP /quote/estimate can't price a non-USDC swap (it errors "Invalid
  // destination chain: bnb/tron" or values a native token as 6-dec USDC). DEX routes have their
  // own dexFeeEstimate query, and a no-route selection has nothing to estimate — so fire this
  // ONLY for an actual CCTP route.
  useEffect(() => {
    if (!isCctpRoute) return
    const amountNum = parseFloat(bridgeState.amount || '0')
    const isValidAmount = bridgeState.amount && !bridgeState.amount.endsWith('.') && amountNum > 0

    if (isValidAmount && bridgeState.sourceChain && bridgeState.destinationChain) {
      const timer = setTimeout(() => {
        estimateFees(sourceAddress || undefined)
      }, 500)
      return () => clearTimeout(timer)
    }
  }, [bridgeState.amount, bridgeState.sourceChain, bridgeState.destinationChain, sourceAddress, estimateFees, isCctpRoute])

  // Bump source if verified receive < user's target (truncation edge case — fires once per target).
  // CCTP ONLY: it compares against CCTP's USDC-denominated feeEstimate and bumps via .toFixed(2).
  // For DEX/Jupiter routes the reverse quote already computes the exact source (often 6dp native),
  // so this must NOT run — it would stomp that value and re-truncate it to 2 decimals.
  useEffect(() => {
    if (isDexRoute || inputMode !== 'receive' || !feeEstimate || bumpedRef.current) return
    const target = parseFloat(receiveTarget)
    const verified = parseFloat(feeEstimate.estimatedReceived)
    if (isNaN(target) || isNaN(verified) || target <= 0) return

    if (verified < target - 0.001) {
      bumpedRef.current = true
      const currentSource = parseFloat(bridgeState.amount)
      if (!isNaN(currentSource)) {
        // #94 Bug 1 — never bump the SEND past the balance (this +0.01 on a near-max reverse swap was a
        // direct cause of "insufficient balance"). Clamp to the floored source-token balance.
        const bal = sourceTokenBalanceStr != null ? floorToDecimals(sourceTokenBalanceStr, 2) : null
        const next = bal != null ? Math.min(currentSource + 0.01, bal) : currentSource + 0.01
        setAmount(next.toFixed(2))
      }
    }
  }, [feeEstimate, inputMode, receiveTarget, bridgeState.amount, setAmount, isDexRoute, sourceTokenBalanceStr])

  // Track which transfers we've started polling for
  const pollingStartedRef = useRef<Set<string>>(new Set())

  // Auto-resume polling for pending transfers
  useEffect(() => {
    if (pendingTransfers.length === 0) return
    for (const pt of pendingTransfers) {
      if (pt.burnTxHash && !pollingStartedRef.current.has(pt.burnTxHash)) {
        pollingStartedRef.current.add(pt.burnTxHash)
        log.debug('Auto-resuming polling for:', pt.burnTxHash.slice(0, 10))
        resumeTransfer(pt.burnTxHash)
      }
    }
  }, [pendingTransfers, resumeTransfer])

  // ============================================
  // Chain mismatch detection
  // ============================================
  const expectedChainId = bridgeState.sourceChain && bridgeState.sourceChain !== 'solana' && bridgeState.sourceChain !== 'tron'
    ? (CCTP_CONFIG as any)[bridgeState.sourceChain]?.chainId as number | undefined
    : undefined
  const isSourceChainMismatch = Boolean(expectedChainId && evmChainId && evmChainId !== expectedChainId)

  // ============================================
  // Incompatibility detection
  // ============================================
  const isSourceIncompatible = Boolean(
    bridgeState.sourceChain &&
    ((bridgeState.sourceChain === 'solana' && evmAddress && !solanaAddress) ||
     (bridgeState.sourceChain === 'tron' && (evmAddress || solanaAddress) && !tronConnected) ||
     (bridgeState.sourceChain !== 'solana' && bridgeState.sourceChain !== 'tron' && solanaAddress && !evmAddress))
  )
  const isDestIncompatible = Boolean(
    bridgeState.destinationChain &&
    ((bridgeState.destinationChain === 'solana' && evmAddress && !solanaAddress) ||
     (bridgeState.destinationChain === 'tron' && (evmAddress || solanaAddress) && !tronConnected) ||
     (bridgeState.destinationChain !== 'solana' && bridgeState.destinationChain !== 'tron' && solanaAddress && !evmAddress))
  )
  const isRecipientMissing = !destinationAddress && !bridgeState.destinationAddress

  // ============================================
  // Wallet modal handlers
  // ============================================
  const openWalletModal = useCallback((target: 'source' | 'destination') => {
    setModalChainFamily('all')
    setModalTarget(target)
  }, [])

  // Family selected from unified wallet modal (source or dest)
  const handleFamilySelected = useCallback((family: 'evm' | 'solana' | 'tron') => {
    if (modalTarget === 'source') {
      if (family === 'solana') setSourceChain('solana')
      else if (family === 'tron') setSourceChain('tron')
      else {
        setAutoOpenedEvmSourcePicker(true)
        setChainPicker('send')
        // Show loading shimmer immediately if balances haven't loaded yet
        // (the balance fetch effect runs AFTER the render, so without this
        // the chain picker briefly shows "0.00 USDC" before the shimmer)
        if (Object.keys(allBalances).length === 0) setIsBalancesLoading(true)
      }
    } else if (modalTarget === 'destination') {
      if (family === 'solana') setDestinationChain('solana')
      else if (family === 'tron') setDestinationChain('tron')
      else {
        setAutoOpenedEvmDestPicker(true)
        setChainPicker('receive')
        if (Object.keys(allBalances).length === 0) setIsBalancesLoading(true)
      }
    }
  }, [modalTarget, setSourceChain, setDestinationChain, allBalances])

  // Token selector handlers
  const handleSourceTokenClick = useCallback(() => {
    setTokenSelectorSide('source')
    setIsTokenSelectorOpen(true)
  }, [])

  const handleDestTokenClick = useCallback(() => {
    setTokenSelectorSide('destination')
    setIsTokenSelectorOpen(true)
  }, [])

  const handleTokenSelect = useCallback((selection: TokenSelection) => {
    const { symbol, chain } = selection
    if (tokenSelectorSide === 'source') {
      setSelectedSourceToken(symbol)
      // #254 — a network-first native selection carries its EXACT chain; pin it (never
      // availableOn[0], which would silently drop ETH-on-Base to ethereum). A stablecoin
      // carries no chain and keeps the existing availableOn constraint (USDG is restricted;
      // USDC/USDT are all-chain and never switch).
      if (chain) {
        // Only switch when it actually changes — setSourceChain clears the fee estimate (useBridge),
        // so an unconditional call on a no-op re-select of the current native would needlessly blank
        // and refetch the quote.
        if (chain !== bridgeState.sourceChain) setSourceChain(chain)
        localStorage.setItem('parsebridge:lastSourceChain', chain)
      } else {
        const item = TOKEN_PICKER_LIST.find(t => t.symbol === symbol)
        if (item?.availableOn && !item.availableOn.includes(bridgeState.sourceChain as Chain)) {
          const newChain = item.availableOn[0]
          setSourceChain(newChain)
          localStorage.setItem('parsebridge:lastSourceChain', newChain)
        }
      }
      localStorage.setItem('parsebridge:lastSourceToken', symbol)
    } else {
      setSelectedDestToken(symbol)
      if (chain) {
        if (chain !== bridgeState.destinationChain) setDestinationChain(chain)
        localStorage.setItem('parsebridge:lastDestChain', chain)
      } else {
        const item = TOKEN_PICKER_LIST.find(t => t.symbol === symbol)
        if (item?.availableOn && !item.availableOn.includes(bridgeState.destinationChain as Chain)) {
          const newChain = item.availableOn[0]
          setDestinationChain(newChain)
          localStorage.setItem('parsebridge:lastDestChain', newChain)
        }
      }
      localStorage.setItem('parsebridge:lastDestToken', symbol)
    }
  }, [tokenSelectorSide, bridgeState.sourceChain, bridgeState.destinationChain, setSourceChain, setDestinationChain])

  // Open RecipientModal instead of raw clipboard paste
  const handleSetRecipient = useCallback(() => {
    // Guard: don't open without a destination chain (no way to validate address format)
    if (!bridgeState.destinationChain) return
    setIsRecipientModalOpen(true)
  }, [bridgeState.destinationChain])

  // Handle swap direction — swap chains AND tokens
  const handleSwapDirection = useCallback(() => {
    swapDirection()
    const prevSource = selectedSourceToken
    const prevDest = selectedDestToken
    setSelectedSourceToken(prevDest)
    setSelectedDestToken(prevSource)
  }, [swapDirection, selectedSourceToken, selectedDestToken])

  // Handle amount change (send field → forward direction)
  const handleAmountChange = useCallback((amount: string) => {
    setInputMode('send')
    setAmount(amount)
  }, [setAmount])

  // Handle receive amount change (receive field → reverse direction)
  const handleReceiveAmountChange = useCallback((destAmount: string) => {
    setInputMode('receive')
    setReceiveTarget(destAmount)
    bumpedRef.current = false
    const dest = parseFloat(destAmount)
    if (isNaN(dest) || dest <= 0) {
      // #336 R12 — bump the race-guard BEFORE returning. Clearing the receive field is a new intent,
      // so any reverse quote already in flight for the previous target is stale. The bump happened
      // only on the quoting path below (:2234), so on this path the in-flight `.then` still matched
      // the sequence and wrote its source amount (:2255) for a transfer the user had just cancelled:
      // type 1000, clear it before the quote lands, watch the send field fill itself with 1005.32.
      reverseSeqRef.current++
      setAmount('')
      return
    }

    // DEX reverse: the rate is non-linear (aggregator/solver-set), so we can't invert it algebraically
    // like CCTP — the backend computes the required source by inverting forward quotes. Available for
    // ALL DEX routes (same-chain & cross-chain, native & stable source) whenever the DEST is a
    // stablecoin; native dest is deferred to the future USD-amount toggle. Sequence-guarded so a slower
    // in-flight quote can't overwrite a newer keystroke's result.
    if (isDexRoute && selectedRoute && !isNativeDest) {
      const seq = ++reverseSeqRef.current
      setReverseRouteUnavailable(null)
      // Clear the source while the (latency-prone) reverse quote is in flight, so a stale
      // amount never sits next to the freshly-typed receive value (misleading for a money field).
      setAmount('')
      dexApi.getReverseEstimate({
        sourceChain: selectedRoute.sourceChain,
        destChain: selectedRoute.destChain,
        sourceToken: selectedRoute.sourceToken,
        destToken: selectedRoute.destToken,
        destAmount,
      }).then(res => {
        if (seq !== reverseSeqRef.current) return // stale — a newer keystroke won
        // #614 (reviewer B7) — a same-chain 1Click refusal is surfaced, not swallowed: the send field
        // stays cleared and the CTA/notice carry the server's sentence via opexGate.
        if (!res.success && res.error?.code === 'SAME_CHAIN_ROUTE_UNAVAILABLE') {
          setReverseRouteUnavailable(res.error.message || 'This route is temporarily unavailable. Please try again in a few minutes.')
          return
        }
        if (res.success && res.data?.sourceAmount) {
          const n = parseFloat(res.data.sourceAmount)
          if (Number.isFinite(n) && n > 0) {
            // Precision by token type: stablecoins (6-decimal) round to a clean 2 dp; native
            // assets (18-decimal) keep finer precision (6 dp) since values are tiny. Round UP
            // either way so the user always sends enough to clear the target.
            const isNativeSrc = (res.data.sourceDecimals ?? 6) > 6
            const p = isNativeSrc ? 6 : 2
            const factor = 10 ** p
            setAmount(String(Math.ceil(n * factor) / factor))
          }
        }
      }).catch(() => { /* leave the field cleared on failure */ })
      return
    }

    // DEX route with a native destination: reverse isn't supported yet (the receive field is read-only
    // in this case). Never fall through to the CCTP linear math for a DEX route — it would size the
    // source with USDC-1:1 logic that doesn't apply.
    if (isDexRoute) {
      setAmount('')
      return
    }

    // CCTP dual-cap reverse (#28/#60): the fee is max($0.10 platform floor, bps% of source) + networkFee,
    // NOT a flat bps. The pure-% inverse alone UNDER-sizes the source for small amounts — where the $0.10
    // floor dwarfs the tiny %-fee — so the user would receive LESS than the amount they typed (e.g. type 1,
    // get sized to 1.02, actually receive ~0.92). cctpReverseSource sizes to whichever fee model dominates
    // so the received amount always clears the target. See cctpReverse.test.ts for the round-trip proof.
    const networkFee = parseFloat(feeEstimate?.networkFeeUsdc ?? '0')
    const rawSource = cctpReverseSource(dest, networkFee, { platformMinFeeUsd: PLATFORM_MIN_FEE_USD })
    const ceiledSource = Math.ceil(rawSource * 100) / 100

    // #94 Bug 1 — the reverse math CEILs the source, which can exceed the wallet balance and revert with
    // "insufficient balance". Clamp to the floored source-token balance; a shortfall vs the receive target
    // just means the user sends their max-after-fees, never a doomed tx.
    const bal = sourceTokenBalanceStr != null ? floorToDecimals(sourceTokenBalanceStr, 2) : null
    const send = bal != null ? Math.min(ceiledSource, bal) : ceiledSource
    setAmount(send.toFixed(2))
  }, [feeEstimate, setAmount, isDexRoute, selectedRoute, isNativeDest, sourceTokenBalanceStr])

  // Execute the actual transfer — forks between CCTP and DEX flows
  const executeTransfer = useCallback(async () => {
    if (!sourceAddress || !destinationAddress) {
      return
    }

    try {
      if (isDexRoute && selectedRoute) {
        // Clear any stale/terminal CCTP progress panel before a DEX start so a completed "Transfer Complete"
        // card doesn't stack above the new live DEX panel. Only reset the SIBLING (CCTP), never the engine
        // we're about to show, and NEVER a CCTP transfer still actively in-flight (guard below) — resetting a
        // live post-burn transfer would wipe its progress tracking.
        if (!isCctpInFlight) reset()
        await executeDexBridge({
          sourceChain: selectedRoute.sourceChain,
          destChain: selectedRoute.destChain,
          sourceToken: selectedRoute.sourceToken,
          destToken: selectedRoute.destToken,
          amount: bridgeState.amount,
          recipientAddress: destinationAddress,
          senderAddress: sourceAddress,
          senderNativeBalanceWei,
          senderTokenBalanceAtomic,
          route: selectedRoute,
          confirmRecipientRisk,
        })
      } else {
        // Symmetric: clear a stale/terminal DEX progress panel before a CCTP start, but never a live DEX order.
        if (!isDexTransferring) resetDex()
        await executeBridge(sourceAddress, destinationAddress, confirmRecipientRisk)
      }
    } catch (error) {
      log.error('Bridge error:', error)
    }
  }, [sourceAddress, destinationAddress, executeBridge, isDexRoute, selectedRoute, executeDexBridge, bridgeState.amount, senderNativeBalanceWei, senderTokenBalanceAtomic, reset, resetDex, isCctpInFlight, isDexTransferring, confirmRecipientRisk])

  // CTA click opens review modal instead of executing directly
  const handleSend = useCallback(() => {
    setIsReviewOpen(true)
  }, [])

  /**
   * #261 L2 — the review card's "Est. delivery", route-aware.
   *
   * Was `String(feeEstimate?.estimatedTime ?? '~2 min')`. `feeEstimate` is CCTP-only and
   * structurally absent on every DEX route, so EVERY swap's review card read a flat "~2 min" — a
   * number tied to nothing, on the last screen before the user commits, contradicting the fee card
   * they had just left. (The CCTP arm was no better: it rendered `String(60)` → "~1 min".)
   *
   * Same rule as the fee card (`FeeSection`, via `isSameChain`) and the bridge progress row in
   * `utils/steps.ts`, and the three must agree. `formatDeliveryTime` in ReviewTransferModal passes
   * any string containing letters through untouched.
   *
   * Null-guarded for the same reason as the `SwapCard` call site: a bare equality makes the
   * nothing-picked-yet state read as same-chain and quote the shorter time.
   */
  const reviewDeliveryTime = atomicSameChain ? '1–3 min' : '1–5 min'

  // Review modal fee computations
  const reviewTotalFee = useMemo(() => {
    const input = parseFloat(bridgeState.amount || '0')
    const output = parseFloat(estimatedReceived || '0')
    if (input <= 0 || output <= 0) return '0.00'
    return Math.max(0, input - output).toFixed(4)
  }, [bridgeState.amount, estimatedReceived])

  const reviewFeePercentage = useMemo(() => {
    const input = parseFloat(bridgeState.amount || '0')
    const fee = parseFloat(reviewTotalFee)
    if (input <= 0 || fee <= 0) return '0%'
    return `${(fee / input * 100).toFixed(2)}%`
  }, [bridgeState.amount, reviewTotalFee])

  /**
   * #261 F5 §13.0 — the review card's `To` figure, and ONLY the review card's.
   *
   * In reverse (exact-out) mode the receive field holds the user's TYPED TARGET, and until F5 the
   * review card echoed it. F5 makes `minTokenAmount` net of the affiliate skim, so FeeSection's
   * "Minimum received" (`dexFee.minimumReceived` = `dexMinReceived`, :1592) drops while the card's
   * `To` did not — two surfaces stating different numbers for the same quote, with the honest one
   * living on the surface the user is NOT looking at when they sign (I-3).
   *
   * So on a SAME-CHAIN reverse dex quote the card states the MINIMUM ESTIMATED ARRIVAL — the same
   * `dexMinReceived` string FeeSection renders. That is not a pessimisation: `/reverse-estimate` is a
   * receive-AT-LEAST contract (`dex.ts:1878-1881`, "I enter X, I receive at least X"), so the minimum
   * IS the figure the quote is written in. The underlying under-sizing (the inversion prices the
   * affiliate at the BASE bps against a skim taken at the CHARGED rate, `dex.ts:1735`/`:1748`) is
   * #171 / #152 — this makes the card honest about it, it does not fix it.
   *
   * ── THIS IS A PROP, NOT THE `estimatedReceived` MEMO (:1536-1573). DO NOT "CLEAN IT UP" INTO IT. ──
   * The memo has three other consumers, and folding this in breaks every one of them:
   *  1. it feeds the receive INPUT FIELD, which `SwapCard/index.tsx:951-958` rewrites from it ON BLUR
   *     — firing exactly when the user leaves the box to click Review. The memo would therefore
   *     overwrite the user's own typed target, and the compensating fix lives in `SwapCard/**`, which
   *     this job may not touch;
   *  2. it is `dexFee`'s `destAmt` (:1624), where `parseFee = (affBps/10000) × destAmt` would then run
   *     on a figure ~1 slippage-band low and UNDERSTATE the fee on the last screen before a signature
   *     — the F5 defect re-introduced, MP-1;
   *  3. it is conjunct 4 of the net-fee predicate (:1698), a string identity against
   *     `estimatedTokenAmount`; feeding it the minimum flips that conjunct and silently moves the FEE
   *     on what is meant to be a display-only change.
   * Reading the prop instead makes all three impossible BY CONSTRUCTION rather than by guard.
   * `BridgePage.dexFeeStaleness.test.tsx:388` (`totalFee === '50'` on a reverse quote) is the tripwire.
   *
   * ONE PROP, NOT TWO: the modal's `≈ $` subtitle for this row derives from this same
   * `estimatedReceived` prop (`ReviewTransferModal/index.tsx:260-264`), so token figure and dollars
   * move together. A second prop would print the TARGET's dollars under the MINIMUM's token amount.
   *
   * Same-chain conjunct: reverse also serves cross-chain (`dex.ts:1739`), whose `minTokenAmount` is
   * net independently of F5 (`dex.ts:1384`) and is therefore not something this job changed. Moving
   * cross-chain rendering is a halt-unconditionally invariant, so it is gated out here; the
   * cross-chain reverse question is filed (§13.1), not fixed.
   *
   * Fail-CLOSED, stated precisely — an earlier draft of this comment was wrong and the error is worth
   * not repeating. `dexMinReceived` is NOT null under the `'invalid'` suppression branch
   * (`dex.ts:1327-1331`): that nulls `estimatedTokenAmount`/`minTokenAmount`, but the bps-derived
   * `estimatedReceived` survives and `dexMinReceived` (:1596-1598) falls through to it. So under
   * suppression this row states that surviving figure — and I-3 still holds, because it is the same
   * value FeeSection renders. The genuinely-null paths are a bot non-200 or an estimate carrying none
   * of the three amount fields; both fall through to the typed target. All three are pinned in
   * `BridgePage.f5ReverseReceive.test.tsx` V5.
   */
  const reviewReceive = useMemo(() => {
    if (isDexRoute
      && inputMode === 'receive'
      && atomicSameChain
      && dexMinReceived != null) return dexMinReceived
    return estimatedReceived
  }, [isDexRoute, inputMode, atomicSameChain, dexMinReceived, estimatedReceived])

  const canSend = Boolean(
    sourceAddress &&
    destinationAddress &&
    bridgeState.amount &&
    parseFloat(bridgeState.amount) > 0 &&
    selectedRoute &&
    (isDexRoute ? !isDexTransferring : (feeEstimate && !isTransferring))
  )

  // Defence-in-depth backstop: a DEX route must NEVER be submittable while the receive is non-positive.
  // opexGate('quoteUnavailable') already covers the cross-chain unpriceable case, but it does NOT cover
  // a same-chain or Jupiter route whose estimate resolves to 0 (dex.ts:614 returns estimatedReceived
  // '0.0'), nor a native-dest route still awaiting its first quote (estimatedReceived falls through to
  // '0.0' at BridgePage.tsx:831) — both would otherwise submit against a "0.00" receive field.
  // Reads the SAME `estimatedReceived` the receive field renders, so what the user sees is what is
  // gated. In reverse mode that value IS the user's own target (:815, always > 0), so this cannot
  // break inputMode === 'receive'.
  const dexReceiveNonPositive = useMemo<boolean>(() => {
    if (!isDexRoute) return false
    if (!(parseFloat(bridgeState.amount || '0') > 0)) return false // nothing entered — other gates own this
    return !(parseFloat(estimatedReceived || '0') > 0)
  }, [isDexRoute, bridgeState.amount, estimatedReceived])

  /**
   * #337 GATE-4 FIX (F1) — SINGLE SOURCE OF TRUTH for "may this send proceed right now".
   *
   * Before this fix the CTA's `canSend` prop (below, `:3251` pre-fix) and `handleReviewConfirm`'s
   * pre-submit guard were TWO SEPARATE EXPRESSIONS. The CTA checked all nine conjuncts; the guard
   * checked only `dexEstimateStale || dexEstimateMissing` — two of nine, and GATE-4 proved both are
   * TRANSIENT: once the estimate for a newly-typed below-floor amount arrives (~1-1.5s after the
   * modal opens), both go false while the DURABLE floor gates (`belowMinHintUsd`, `opexGate`) go
   * true. A confirm landing in that window called `executeDexBridge` with a below-floor amount while
   * `canSend === false` on the underlying CTA composite — the exact submit this gate exists to
   * prevent, reachable on the majority path. GATE-4 rejected the branch on this; see
   * `docs/postv1launch/spec-337-belowmin-submit-gate-stale-estimate-ADDENDUM-A.md`.
   *
   * Fix: hoist the full composite here ONCE. The CTA and the confirm guard both read this memo, so
   * they cannot diverge again by construction — there is exactly one place to add or remove a
   * conjunct, and no second copy to forget.
   *
   * NAMED FOR THE GUARD'S POLARITY, not the CTA's: `true` means "do not allow submit". The CTA below
   * negates it (`canSend={!submitBlocked}`); `handleReviewConfirm` reads it directly
   * (`if (submitBlocked) { …close, don't submit… }`). The body is the exact negation of the prior
   * nine-conjunct CTA expression — not a re-derivation conjunct-by-conjunct — specifically so CTA
   * behaviour stays byte-identical (De Morgan's law makes `!( A && !B && !C && … )` the same truth
   * table as `!A || B || C || …`, but writing it as a literal negation removes any chance of a typo'd
   * polarity flip on any one of the nine).
   *
   * TRANSFERRING CONJUNCT — included, not left CTA-only (the judgement call this fix cycle calls
   * out). `isTransferring` / `isDexTransferring` can become true ONLY as a downstream consequence of
   * `executeBridge` / `executeDexBridge`, and those are called ONLY from `executeTransfer`, called
   * ONLY from `handleReviewConfirm` itself, below — so by construction this flag is always `false` at
   * the moment the guard runs during a legitimate confirm; including it cannot false-reject one. (The
   * review modal cannot even be reached while a transfer is in flight: SwapCard's own `isLoading`
   * branch, `SwapCard/index.tsx:645-646`, fully disables the CTA and no-ops `onClick` before `canSend`
   * is ever consulted, so `handleSend` cannot fire.) It IS a genuine reason to refuse a submit
   * (double-submit guard), so it belongs in the shared source of truth rather than living only on the
   * CTA where a future caller of `handleReviewConfirm` could miss it.
   */
  /**
   * #614 — pre-sign gate for a native-source deposit-address route on a bundle without the native
   * deposit arms (production, where VITE_ENABLE_NATIVE_1CLICK_DEPOSIT is withheld): refuse at the CTA,
   * before the review modal, instead of throwing at sign time inside the hook (whose own throw stays
   * as the second line). Scoped to the Tron SAME-CHAIN cells this job opens (reviewer B4): the CTA copy
   * names TRX, and a cross-chain native-source 1Click route (ETH/SOL/BNB → Tron USDT) keeps today's
   * behaviour — the hook's own generic throw — rather than a TRX sentence on an ETH swap.
   */
  const nativeDepositUnavailable = useMemo<boolean>(
    () => isDexRoute && selectedRoute?.provider === 'near_1click' && isNativeSrc && !isNativeOneclickDepositEnabled()
      && bridgeState.sourceChain === 'tron' && bridgeState.destinationChain === 'tron',
    [isDexRoute, selectedRoute?.provider, isNativeSrc, bridgeState.sourceChain, bridgeState.destinationChain],
  )

  const submitBlocked = useMemo<boolean>(() => !(
    canSend &&
    !nativeDepositUnavailable &&
    !amountTooLowAfterFees &&
    !insufficientNativeForFees &&
    !opexGate &&
    !belowMinHintUsd &&
    !dexReceiveNonPositive &&
    !dexFeeUnavailable.blocked &&
    !dexEstimateStale &&
    !dexEstimateMissing &&
    !(isDexRoute ? isDexTransferring : isTransferring)
  ), [canSend, nativeDepositUnavailable, amountTooLowAfterFees, insufficientNativeForFees, opexGate, belowMinHintUsd, dexReceiveNonPositive, dexFeeUnavailable, dexEstimateStale, dexEstimateMissing, isDexRoute, isDexTransferring, isTransferring])

  // Review modal confirm → close modal and execute
  const handleReviewConfirm = useCallback(() => {
    /**
     * #337 G2 — THE PRE-WINDOW ESCAPE. The `canSend` gate is evaluated when the modal OPENS, and a
     * click can land before the window ever opens: SwapCard dispatches the amount field on its OWN
     * 300 ms debounce (SwapCard/index.tsx:350-358), which `handleSendBlur` does not flush. Type 0.5
     * over a settled 1000, tap Send at t=150 — every gate still sees 1000 — and at t=300
     * `bridgeState.amount` becomes 0.5 BEHIND the open modal. The modal re-renders live but its
     * confirm has no `disabled` prop and nothing closes it, and `executeTransfer` submits the FRESH
     * amount. Gating `canSend` does not touch this: the click beat the gate.
     *
     * #337 GATE-4 FIX (F1) — re-check the SAME `submitBlocked` memo the CTA reads (above), not a
     * hand-picked subset. The pre-fix guard here checked only `dexEstimateStale || dexEstimateMissing`
     * — the two TRANSIENT conjuncts — and missed the DURABLE floor gates (`belowMinHintUsd`,
     * `opexGate`, …) that are exactly what this re-check exists to catch once the fresh estimate for a
     * below-floor amount lands. See the memo's own comment for the full account.
     *
     * Re-checked HERE rather than by adding `disabled={!canSend}` to ReviewTransferModal (#337 N13).
     * That is the inviting one-line fix and it is the wrong one: it breaks that component's
     * green-and-unmodified invariant and introduces an unspecified false-reject surface — a confirm
     * that greys mid-review with no explanation.
     *
     * CLOSE, don't silently no-op: a confirm button that does nothing is worse than one that returns
     * the user to a screen whose CTA now explains itself. Closing also re-enters the normal gate, so
     * what they see next is the same state the CTA shows.
     */
    if (submitBlocked) { setIsReviewOpen(false); return }
    setIsReviewOpen(false)
    // Starting a NEW transfer must drop any drawer-selected (historical) transfer panel, else the old
    // panel and the new live progress panel both render at once (the "two modals shown" bug).
    setProgressTransfer(null)
    executeTransfer()
  }, [executeTransfer, submitBlocked])

  // Combined error banner (#182). Tracks the on-screen ACTIVE order's own error, falling back to the
  // selected route's error only when no order is active — see selectBannerError above. For a no-route
  // selection with nothing active this is null, so the CTA shows "Route Not Available", never a stale
  // CCTP bridgeError banner.
  const error = selectBannerError({ dexStep, burnTxHash, isTransferring, isDexRoute, isCctpRoute, dexError, bridgeError })

  // ══ DEV-375 — is this terminal state a FAILURE, or an UNCONFIRMED transfer? ═════════════════════
  // Both are derived from the DISPLAY string the hook already produced, because that string is what
  // `classifyError` tags and what the user reads; a second, parallel signal could disagree with the
  // one on screen. Conjoined with the terminal step so a stale message from an earlier attempt can
  // never neutralise a live, healthy panel.
  //
  // Deliberately NOT flag-gated (P-B). `VITE_FAILURE_STEP_STATE` gates the per-step render
  // experiment; it does not gate whether the app may tell a user their delivered transfer failed.
  const dexReceiptUnknown = dexStep === 'error' && isReceiptUnknownMessage(dexError)
  // The CCTP mount inherits this through `approveErc20Exact`, which useBurn calls for the
  // contract-account bridge() path — deliberately, per the spec's GATE-2 decision. The burn leg's own
  // `tx.wait()` predicate inside useBurn.ts is a separate provider (ethers, via the wallet) and is a
  // filed follow-up, so a burn-leg receipt failure is NOT covered by this and still reads as failed.
  const cctpReceiptUnknown = transferStep === 'error' && isReceiptUnknownMessage(burnError)
  const dexFailureKind: StepFailureKind = dexReceiptUnknown ? 'unknown' : 'failed'
  const cctpFailureKind: StepFailureKind = cctpReceiptUnknown ? 'unknown' : 'failed'

  const c = useThemeColors()
  const UI = useUI()

  // Build progress steps for ProgressPanel
  const cctpSteps = useMemo(() => makeSteps({
    transferStep,
    sourceChain: bridgeState.sourceChain,
    destChain: bridgeState.destinationChain,
    burnTxHash,
    mintTxHash,
    burnError,
    mintError,
    // ── DEV-375, AND AN HONEST LIMIT ON IT ────────────────────────────────────────────────────────
    // This argument is INERT AT THIS CALL SITE TODAY, and saying so is better than letting it look
    // wired. `useBridge` collapses ANY burn/mint failure to `transferStep: 'error'`
    // (`useBridge.ts:669-672`), and `'error'` is not a member of `makeSteps`' phase ORDER — so no row's
    // `errorPhase` is ever true on the CCTP card and no row reaches the branch `failureKind` selects.
    // The CCTP error card renders all-'pending' rows regardless, which is a PRE-EXISTING defect in
    // that builder's handling of the terminal step, not one this change introduces or repairs.
    //
    // CORRECTED (DEV-375 finding #12, both reviewers independently): this comment used to assert
    // "the PANEL-level half of the fix at this mount ... IS live." That claim is FALSE and the
    // branch it describes is UNREACHABLE — flagged plainly per this project's dead-code-that-looks-
    // wired rule rather than left to look load-bearing.
    //
    // The ONLY producer of a marker-bearing `burnError` is `approveErc20Exact`, and it runs BEFORE
    // the burn transaction is even signed (`useBurn.ts`'s EVM path calls it ahead of the burn write).
    // `useBurn.ts`'s catch, on ANY throw from that call, sets `isBurning: false` and leaves
    // `burnTxHash: null` (it is never assigned a value before a successful burn). So the instant
    // `cctpReceiptUnknown` (`transferStep === 'error' && isReceiptUnknownMessage(burnError)`) is
    // true, `burnTxHash` is null AND `isTransferring` is false — exactly the two conditions the panel
    // mount below is gated on (`(burnTxHash || isTransferring) && ...`). The mount guard is false
    // precisely when this branch would matter, so `isError`/`isUnknown`/the demoted footer at this
    // call site have never rendered for a receipt-unknown approve failure.
    //
    // Kept anyway, deliberately: the fail-closed 'failed' default means passing this argument cannot
    // misfire today, and removing it guarantees the two call sites (this one and the DEX mount, where
    // the equivalent branch IS live) diverge in what they tell the builder the day `useBurn.ts`'s own
    // DEV-375 follow-up (GATE-1 decision O-C) makes `burnTxHash`/`isTransferring` reachable together
    // with a receipt-unknown `burnError`.
    failureKind: cctpFailureKind,
    getExplorerUrl,
  }), [transferStep, bridgeState.sourceChain, bridgeState.destinationChain, burnTxHash, mintTxHash, burnError, mintError, cctpFailureKind])

  const dexSteps = useMemo(() => {
    const isSameChain = executedAtomicSameChain
    // CROSS-CHAIN DEX (e.g. deBridge Base→Bnb) renders the SAME 5-step structure as CCTP — Approving / Confirm
    // in wallet / Confirming on {src} / Moving between chains / Delivering to {dst} — with the source tx + dest
    // FILL tx as explorer links, so the live swap card matches the CCTP progress card (and the drawer-opened one,
    // which already uses makeSteps). Same-chain swaps keep the compact 3-step card (single chain — no two legs).
    // 'confirming' added by DEV-375 — the receipt-unknown phase (see the ternary's own note below).
    const crossChainStep: 'idle' | 'switching_network' | 'signing' | 'burning' | 'confirming' | 'attesting' | 'complete' =
      dexStep === 'complete' ? 'complete'
      : dexStep === 'polling' ? 'attesting'
      : dexStep === 'broadcasting' ? 'burning'
      : dexStep === 'signing' ? 'signing'
      // DEV-375: a receipt-unknown lands on 'confirming' — the "Confirming on {src}" row, which is
      // literally the step we could not complete. Without this the hoisted hash routes it to
      // 'attesting', whose row carries NO error gate, so the source row would render a green
      // DONE check for a transaction we never confirmed — a false success claim, the I1 breach in
      // its other direction. (This is the intended F-5 effect, made honest rather than left to the
      // pre-existing mapping.)
      //
      // A GENUINE SOURCE-leg failure (dexSourceTxHash set, dexReceiptUnknown false — e.g. a
      // wallet-cancelled 1Click deposit normalised to 'reverted') must land here too, not on
      // 'attesting': 'attesting' has no error gate, so it would paint the source row a green DONE
      // check under a red "Transfer Failed" title. `failureKind` (not hash presence) decides the
      // colour on both branches.
      //
      // GATE-4 finding #1 — a DOWNSTREAM failure (deBridge OrderCancelled, 1Click
      // REFUNDED/FAILED/INCOMPLETE_DEPOSIT) is the MIRROR case: the source leg SUCCEEDED
      // (useDexBridge.ts's poller/reconcile comments say so), so routing it here would red the
      // source row for a transaction that landed. `failedLeg` — set at every terminal-failure
      // writer, not derived from hash presence — is what tells the two apart: 'downstream' still
      // needs the hash present (no hash means the swap never got far enough to have a backend order
      // to cancel), so it routes to 'attesting' (source row reads done; the tracking row reds — see
      // steps.ts's `bridge` row gate). Anything else with a hash ('source', receipt-unknown, or the
      // pre-failedLeg default of `null`) keeps the existing 'confirming' mapping.
      : dexStep === 'error' ? (!dexSourceTxHash ? 'signing' : dexFailedLeg === 'downstream' ? 'attesting' : 'confirming')
      : (dexStep === 'approving' || dexStep === 'fetching_quote' || dexStep === 'confirming_route') ? 'switching_network'
      : 'idle'
    // Dest FILL tx (deBridge fulfillment) — populated by the order-status poll once the fill lands + is captured;
    // the same mint_tx_hash → getExplorerUrl(bscscan) path CCTP uses, so the dest link is identical.
    const fillTxHash = dexOrderStatus?.fulfilledDstEventMetadata?.transactionHash ?? null
    return buildRouteSteps(
      isSameChain,
      {
        dexStep,
        sourceChain: bridgeState.sourceChain,
        destChain: bridgeState.destinationChain,
        sourceTxHash: dexSourceTxHash,
        orderStatus: dexOrderStatus?.state ?? null,
        error: dexError,
        // #261 F9 (I-1) — red only the step that failed. The hook captures this regardless of the flag;
        // withholding it here is what makes the flag-off render identical to pre-F9 (F-C).
        failedStep: isFailureStepStateEnabled() ? dexFailedStep : undefined,
        // DEV-375 — NOT flag-gated, unlike `failedStep` above. With the flag OFF the builder's
        // retained short-circuit reds ALL three rows; this makes those three rows neutral instead of
        // red, which is exactly the flag-OFF half the ticket is about (P-B).
        failureKind: dexFailureKind,
        getExplorerUrl,
      },
      {
        transferStep: crossChainStep,
        sourceChain: bridgeState.sourceChain,
        destChain: bridgeState.destinationChain,
        burnTxHash: dexSourceTxHash,
        mintTxHash: fillTxHash,
        // #614 (reviewer B3) — a same-chain DEPOSIT-flow order (Tron TRX↔USDT on 1Click) takes this
        // cross-chain template; the hop row must say "Swapping on Tron", never "Moving between chains".
        sameChainDeposit: !executedAtomicSameChain && bridgeState.sourceChain === bridgeState.destinationChain,
        burnError: dexStep === 'error' ? dexError : null,
        mintError: null,
        // The cross-chain arm needs it too: the 1Click deposit path — the site with the receipt read
        // — is cross-chain, so this arm, not makeDexSteps, renders the ticket's headline case.
        failureKind: dexFailureKind,
        getExplorerUrl,
      },
    )
  }, [dexStep, executedAtomicSameChain, bridgeState.sourceChain, bridgeState.destinationChain, dexSourceTxHash, dexOrderStatus?.state, dexOrderStatus?.fulfilledDstEventMetadata?.transactionHash, dexError, dexFailedStep, dexFailedLeg, dexFailureKind, dexReceiptUnknown])

  // Steps for the overlay ProgressPanel opened from the Activity drawer — derived from the SELECTED transfer's
  // backend status (a historical / in-flight row, not the current session). Maps the status → a makeSteps stage.
  const progressTransferSteps = useMemo(() => {
    if (!progressTransfer) return []
    const s = progressTransfer.status
    // Same-chain swaps settle in ONE atomic tx → the dest ("Delivering to X") leg IS the swap tx, so link it
    // even when no separate fill/mint hash exists (stored orders persist only the source tx client-side).
    // #614 — ATOMIC same-chain only: a same-chain deposit-flow row (near_1click) takes the cross-chain
    // builder and never has its deposit hash faked into the destination slot.
    const sameChain = isAtomicSameChain(progressTransfer.sourceChain, progressTransfer.destinationChain, progressTransfer.provider)

    const step: 'burning' | 'attesting' | 'minting' | 'complete' | 'error' =
      ['mint_success', 'complete', 'recovered'].includes(s) ? 'complete'
      : ['failed', 'error', 'mint_failed'].includes(s) ? 'error'
      : s === 'mint_submitted' ? 'minting'
      : ['burn_success', 'confirmed', 'attestation_pending', 'attestation_received'].includes(s) ? 'attesting'
      : 'burning'

    // #261 F9 (I-2) — a same-chain swap gets the SAME builder here that the live panel uses, chosen by
    // the SAME helper. Without it the drawer narrated "Moving between chains" and "Delivering to
    // {chain}" for a swap that never left one chain: `sameChain` was already computed here, but only
    // to fake the mint hash.
    return buildRouteSteps(
      isFailureStepStateEnabled() && sameChain,
      {
        dexStep: drawerDexPhase(s, progressTransfer.burnTxHash),
        sourceChain: progressTransfer.sourceChain,
        destChain: progressTransfer.destinationChain,
        // ActivityTransfer names the source hash `burnTxHash`, whatever produced the row.
        sourceTxHash: progressTransfer.burnTxHash,
        // Display copy, never the raw status token: makeDexSteps prints this verbatim as the confirm
        // row's sublabel, where the live panel shows deBridge's "Fulfilled"/"Created".
        orderStatus: drawerOrderSublabel(s),
        error: progressTransfer.error ?? null,
        failedStep: progressTransfer.failedStep ?? null,
        getExplorerUrl,
      },
      {
        transferStep: step,
        sourceChain: progressTransfer.sourceChain,
        destChain: progressTransfer.destinationChain,
        burnTxHash: progressTransfer.burnTxHash ?? '',
        mintTxHash: progressTransfer.mintTxHash ?? (sameChain ? (progressTransfer.burnTxHash ?? '') : ''),
        // #614 (reviewer B3) — same as the live builder: a reopened same-chain deposit-flow order.
        sameChainDeposit: !sameChain && progressTransfer.sourceChain === progressTransfer.destinationChain,
        burnError: null,
        mintError: null,
        getExplorerUrl,
      },
    )
  }, [progressTransfer])

  // Scroll the inline progress view into focus when a transfer is opened from the Activity drawer.
  useEffect(() => {
    if (progressTransfer) selectedProgressRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }, [progressTransfer])

  // Cancel-control descriptor (visibility + enabled + reason) for a stored DEX order — drives the
  // ActivityDrawer button. Extracted to @/utils/cancelEligibility so the gating is unit-testable.
  const connectedWallets = useMemo(
    () => ({ evmAddress, solanaAddress, tronAddress }),
    [evmAddress, solanaAddress, tronAddress])
  const isCancelInFlight = useCallback((id: string): boolean => {
    const s = cancelStates[id]?.status
    return s === 'signing' || s === 'submitting'
  }, [cancelStates])
  const storedCancelControl = useCallback((order: StoredDexOrder) =>
    storedOrderCancelControl(order, connectedWallets, isCancelInFlight(order.transferId)),
    [connectedWallets, isCancelInFlight])

  // Build activity transfers for ActivityDrawer (history only — active transfer is a separate prop).
  const activityTransfers = useMemo((): ActivityTransfer[] => {
    const items: ActivityTransfer[] = []
    const seen = new Set<string>()

    // Backend-history rows keyed by transferId — used to enrich stored DEX orders, which only persist the
    // SOURCE tx client-side (StoredDexOrder has no dest/fill hash). Without this, a completed near_1click/
    // deBridge order rendered from storedDexOrders shows the source explorer link but no DESTINATION link
    // (the richer backendHistory row, which has mintTxHash, gets deduped away).
    const bhById = new Map(backendHistory.map(b => [b.id, b]))

    // Exclude the active transfer's burnTxHash from the history list to avoid duplicates
    if (burnTxHash && transferStep !== 'complete' && transferStep !== 'error') seen.add(burnTxHash)
    if (dexSourceTxHash && dexStep !== 'idle' && dexStep !== 'complete' && dexStep !== 'error') seen.add(dexSourceTxHash)

    // Priority 1: Current-session completed transfers
    for (const ct of completedTransfers) {
      if (ct.burnTxHash && seen.has(ct.burnTxHash)) continue
      items.push(ct)
      if (ct.burnTxHash) seen.add(ct.burnTxHash)
    }

    // Priority 2: Pending CCTP transfers (from recovery / localStorage)
    for (const pt of pendingTransfers) {
      if (pt.burnTxHash && seen.has(pt.burnTxHash)) continue
      items.push({
        id: pt.id,
        amount: pt.amount,
        sourceToken: 'USDC',
        destToken: 'USDC',
        sourceChain: pt.sourceChain,
        destinationChain: pt.destinationChain,
        status: pt.backendStatus || pt.pendingStatus || 'pending',
        provider: 'cctp',
        burnTxHash: pt.burnTxHash,
        mintTxHash: null,
        timestamp: parseActivityTimestamp(pt.createdAt),
      })
      if (pt.burnTxHash) seen.add(pt.burnTxHash)
    }

    // Priority 3: Stored DEX orders
    for (const order of storedDexOrders) {
      if (order.sourceTxHash && seen.has(order.sourceTxHash)) continue
      items.push({
        id: order.transferId,
        amount: order.amount,
        sourceToken: order.sourceToken,
        destToken: order.destToken || order.sourceToken,
        sourceChain: order.sourceChain as Chain,
        destinationChain: order.destinationChain as Chain,
        status: order.orderStatus,
        provider: order.provider,
        burnTxHash: order.sourceTxHash,
        // Dest/fill tx: prefer the value the reconcile loop captured into the stored order; fall back to backend
        // history. This is what renders the destination ("Delivering to {chain}") explorer link for completed
        // cross-chain DEX orders (transfer-history may not return cross-chain transfers under this address).
        mintTxHash: order.destTxHash ?? bhById.get(order.transferId)?.mintTxHash ?? null,
        timestamp: order.createdAt,
        cancelControl: storedCancelControl(order),
        // #261 F9 — carried purely so the ProgressPanel this row opens can red the step that actually
        // failed. `failedStep` is what selects the per-step render; without it the panel falls back to
        // all-red, which is the degraded-but-honest case. (`error` is threaded alongside it and is no
        // longer read by makeDexSteps — the fallback is keyed on the phase, because a row from
        // /bridge/history can never carry an error string and used to render three green checkmarks.)
        error: order.error ?? null,
        failedStep: order.failedStep ?? null,
      })
      if (order.sourceTxHash) seen.add(order.sourceTxHash)
    }

    // Priority 4: Backend history (lowest priority — cross-device persistence)
    for (const bh of backendHistory) {
      if (bh.burnTxHash && seen.has(bh.burnTxHash)) continue
      items.push(bh)
    }

    // Priority 5: Reward claim history (from FeeSplitter claims)
    for (const cl of claimHistory) {
      if (cl.burnTxHash && seen.has(cl.burnTxHash)) continue
      items.push(cl)
      if (cl.burnTxHash) seen.add(cl.burnTxHash)
    }

    return items.sort((a, b) => (b.timestamp ?? 0) - (a.timestamp ?? 0))
  }, [burnTxHash, transferStep, dexSourceTxHash, dexStep,
      completedTransfers, pendingTransfers, storedDexOrders, backendHistory, claimHistory, storedCancelControl])

  // In-progress count for Layout badge — derived from activityTransfers + active transfer
  const activeTransferForDrawer: ActivityTransfer | null = useMemo(() => {
    // CCTP active: burn in progress or burn done but not yet completed
    const cctpDone = completedTransfers.some(t => burnTxHash && t.burnTxHash === burnTxHash)
    if ((isTransferring || (burnTxHash && !cctpDone)) && bridgeState.sourceChain && bridgeState.destinationChain
        && transferStep !== 'complete' && transferStep !== 'error') {
      return {
        id: burnTxHash || 'active-cctp',
        amount: bridgeState.amount,
        sourceToken: selectedSourceToken,
        destToken: selectedDestToken,
        sourceChain: bridgeState.sourceChain,
        destinationChain: bridgeState.destinationChain,
        status: 'in_progress',
        burnTxHash: burnTxHash ?? null,
        mintTxHash: mintTxHash ?? null,
        timestamp: Date.now(),
      }
    }
    // DEX active
    const dexDone = completedTransfers.some(t => dexSourceTxHash && t.burnTxHash === dexSourceTxHash)
    if ((isDexTransferring || (dexSourceTxHash && dexStep !== 'idle' && !dexDone)) && bridgeState.sourceChain && bridgeState.destinationChain
        && dexStep !== 'complete' && dexStep !== 'error') {
      // The in-flight order's persisted record (sourceAddress + transferId + createdAt) — present once the
      // source tx is confirmed (the cancellable window). The cancel control hangs off the REAL deBridge state
      // here and reveals only once the order has been stuck in 'Created' for ≥ 60s. The ~5s order-status poll
      // sets a fresh dexOrderStatus object each tick → this memo re-runs (see deps) → ageMs is re-checked, so
      // the button appears shortly after 60s WITHOUT a manual refresh. id = transferId so onCancelOrder
      // resolves the stored order; the list-dedup (seen.add(dexSourceTxHash)) keeps it from also showing below.
      const activeStored = dexSourceTxHash ? storedDexOrders.find(o => o.sourceTxHash === dexSourceTxHash) : undefined
      const cancelControl = buildActiveDexCancelControl({
        dexOrderStatus,
        storedOrder: activeStored,
        connectedWallets,
        cancelInFlight: activeStored ? isCancelInFlight(activeStored.transferId) : false,
        now: Date.now(),
      })
      return {
        id: activeStored?.transferId || dexSourceTxHash || 'active-dex',
        // #94 Bug 4 — read the PERSISTED order's amount (the authoritative snapshot taken at submit,
        // persistOrder→params.amount), NOT the live input. The near_1click deposit-address flow parks the
        // user in 'polling' with this card showing; bound to bridgeState.amount it would render "0 USDT →
        // USDC" the moment the input is cleared/changed after depositing. activeStored is present once the
        // source tx is broadcast; if the persisted order isn't in state yet (storedDexOrders not refreshed
        // mid-poll), use the in-hook submit snapshot (dexSubmittedAmount) before falling back to the live input.
        amount: activeStored?.amount || dexSubmittedAmount || bridgeState.amount,
        sourceToken: selectedSourceToken,
        destToken: selectedDestToken,
        sourceChain: bridgeState.sourceChain,
        destinationChain: bridgeState.destinationChain,
        status: 'in_progress',
        burnTxHash: dexSourceTxHash ?? null,
        // #614 (S1+C S3-1) — the LIVE in-flight row is a provider WRITER too: absent ⇒ atomic (G6), so a
        // tron→tron 1Click order in flight would render the single "Swap" leg pointing at the user's own
        // deposit. The persisted order outranks the executed response outranks the picker.
        provider: activeStored?.provider ?? dexBridgeResponse?.provider ?? selectedRoute?.provider ?? null,
        timestamp: Date.now(),
        cancelControl,
      }
    }
    return null
  }, [isTransferring, transferStep, burnTxHash, mintTxHash, isDexTransferring, dexStep, dexSourceTxHash,
      bridgeState.sourceChain, bridgeState.destinationChain, bridgeState.amount,
      selectedSourceToken, selectedDestToken, completedTransfers,
      // Depend on the dexOrderStatus OBJECT (fresh ref each ~5s poll), not just .state, so the memo re-runs
      // each tick and re-evaluates the stuck-≥60s ageMs even when the state string stays 'Created'.
      storedDexOrders, dexOrderStatus, connectedWallets, isCancelInFlight, dexBridgeResponse?.provider, selectedRoute?.provider])

  const inProgressCount = useMemo(() => {
    let count = activityTransfers.filter(t =>
      t.status === 'in_progress' || t.status === 'pending' ||
      t.status === 'burn_submitted' || t.status === 'burn_success' ||
      t.status === 'attestation_pending' || t.status === 'attestation_received' ||
      t.status === 'mint_submitted' ||
      t.status === 'signed' || t.status === 'confirmed' || t.status === 'polling'
    ).length
    if (activeTransferForDrawer) count += 1
    return count
  }, [activityTransfers, activeTransferForDrawer])

  return (
    <Layout
      onActivityClick={() => setActivityOpen(true)}
      onCurrencyClick={() => setIsFiatModalOpen(true)}
      inProgressCount={inProgressCount}
      fiatCurrency={fiatCurrency}
      fiatSymbol={fiatSymbol}
    >
      <div style={{
        width: '100%', maxWidth: UI.cardMax, margin: '0 auto', padding: '0 16px', marginTop: 4,
        fontFamily: FONT_BODY,
      }}>
        {/* Server down banner */}
        {serverDown && (
          <div
            role="alert"
            style={{
              padding: '10px 14px', borderRadius: 14, marginBottom: 8,
              background: c.eB, border: `1px solid ${c.eC}33`,
              display: 'flex', alignItems: 'flex-start', gap: 10,
              color: c.eC, fontSize: 13, lineHeight: 1.5,
              animation: 'fadeIn 0.25s ease-out',
            }}
          >
            <WifiOff size={16} style={{ flexShrink: 0, marginTop: 1 }} />
            <div>
              <div style={{ fontWeight: 600, fontSize: 14 }}>Temporarily unavailable</div>
              <div style={{ opacity: 0.85, marginTop: 2 }}>
                We're having trouble connecting. In-progress transfers aren't affected.
              </div>
            </div>
          </div>
        )}

        {/* #145 — a destination-chain switch discarded an explicitly-chosen recipient.
            Only ever rendered for users who actually made an explicit choice; the default
            self-bridge cohort never sees it. Actionable, not merely informational: the button
            reopens the chooser so the reset can be undone in one tap. */}
        {recipientResetChain && (
          <div
            role="status"
            style={{
              padding: '10px 14px', borderRadius: 14, marginBottom: 8,
              background: c.iB, border: `1px solid ${c.dv}`,
              display: 'flex', alignItems: 'flex-start', gap: 10,
              color: c.tx, fontSize: 13, lineHeight: 1.5,
              animation: 'fadeIn 0.25s ease-out',
            }}
          >
            <AlertTriangle size={16} style={{ flexShrink: 0, marginTop: 1, color: '#E8A33D' }} />
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 600, fontSize: 14 }}>
                Recipient reset for {CHAIN_REGISTRY[recipientResetChain]?.name ?? recipientResetChain}
              </div>
              <div style={{ opacity: 0.85, marginTop: 2 }}>
                The address you entered was for a different network, so we switched back to your
                connected wallet.
              </div>
              <button
                onClick={() => setIsRecipientModalOpen(true)}
                style={{
                  marginTop: 6, padding: 0, border: 'none', background: 'transparent',
                  color: c.aC, fontSize: 13, fontWeight: 600, fontFamily: FONT_BODY,
                  cursor: 'pointer', textDecoration: 'underline',
                }}
              >
                Choose a different recipient
              </button>
            </div>
            <button
              aria-label="Dismiss"
              onClick={() => setRecipientResetChain(null)}
              style={{
                border: 'none', background: 'transparent', color: c.mu,
                cursor: 'pointer', fontSize: 16, lineHeight: 1, padding: 0,
              }}
            >
              ×
            </button>
          </div>
        )}

        {/* Error display — DEV-375 finding #5: a receipt-unknown must not read as the same failure as a
            genuine error. Without this it kept role="alert", the RED palette, and an AlertTriangle
            WHILE the neutral ProgressPanel rendered alongside it, so the page said two contradictory
            things at once and the loudest element ("failed") won. */}
        {error && (
          <div
            role={isReceiptUnknownMessage(error) ? 'status' : 'alert'}
            style={{
              padding: '10px 14px', borderRadius: 14, marginBottom: 8,
              background: isReceiptUnknownMessage(error) ? c.wB : c.eB,
              border: `1px solid ${isReceiptUnknownMessage(error) ? c.wC : c.eC}33`,
              display: 'flex', alignItems: 'flex-start', gap: 10,
              color: isReceiptUnknownMessage(error) ? c.wC : c.eC, fontSize: 13, lineHeight: 1.5,
              animation: 'fadeIn 0.25s ease-out',
            }}
          >
            <AlertTriangle size={16} style={{ flexShrink: 0, marginTop: 1 }} />
            <div>
              <div style={{ fontWeight: 600, fontSize: 14 }}>{error}</div>
            </div>
          </div>
        )}

        {/* Network mismatch warning — hidden for now (set `false &&` to false to restore) */}
        {false && isSourceChainMismatch && bridgeState.sourceChain && (
          <div
            role="status"
            style={{
              padding: '10px 14px', borderRadius: 14, marginBottom: 8,
              background: c.wB, border: `1px solid ${c.wC}33`,
              display: 'flex', alignItems: 'flex-start', gap: 10,
              color: c.wC, fontSize: 13, lineHeight: 1.5,
              animation: 'fadeIn 0.25s ease-out',
            }}
          >
            <AlertTriangle size={16} style={{ flexShrink: 0, marginTop: 1 }} />
            <div>
              <div style={{ fontWeight: 600, fontSize: 14 }}>Switch network in your wallet</div>
              <div style={{ opacity: 0.85, marginTop: 2 }}>
                Your wallet is on a different network. We'll ask to switch to{' '}
                <span style={{ fontWeight: 600, textTransform: 'capitalize' }}>{bridgeState.sourceChain}</span> when you send.
              </div>
            </div>
          </div>
        )}

        {/* REMOVED 2026-07-18 — the "USDC is cheaper on this route" nudge (#35 Phase A). Product decision:
            the routing assistance is no longer wanted. The `usdcCheaperNudge` computation is left in place
            (it is cheap and feeds nothing else) so the steer can be restored by re-rendering it here. */}

        {(!hasActiveTransfer || progressDismissed) && !progressTransfer && (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            {/* Main Swap Card */}
            <SwapCard
              isAtomicSameChain={atomicSameChain}
              sourceChain={bridgeState.sourceChain}
              destinationChain={bridgeState.destinationChain}
              sourceAddress={sourceAddress}
              destinationAddress={destinationAddress}
              isSourceIncompatible={isSourceIncompatible}
              isDestIncompatible={isDestIncompatible}
              isRecipientMissing={isRecipientMissing}
              amount={bridgeState.amount}
              onAmountChange={handleAmountChange}
              estimatedReceived={estimatedReceived}
              estimatedReceivedUsd={estimatedReceivedUsd}
              sendAmountUsd={isDexRoute && dexFeeEstimate?.sourceAmountUsd != null ? String(dexFeeEstimate.sourceAmountUsd) : null}
              sourceBalance={(() => {
                if (selectedSourceToken === 'USDC') return sourceBalance?.usdc ?? null
                if (selectedSourceToken === 'USDT') return sourceBalance?.usdt ?? null
                if (selectedSourceToken === 'USDG') return sourceBalance?.usdg ?? null // #174 — USDG, not native
                const td = TOKEN_PICKER_LIST.find(t => t.symbol === selectedSourceToken)
                if (td?.isNative && td.availableOn && !td.availableOn.includes(bridgeState.sourceChain as Chain)) {
                  return null
                }
                return sourceBalance?.native ?? null
              })()}
              // #301 item 3 — the BALANCE-fetch flag, deliberately not `isLoading` (that is the
              // transfer state, bound separately below).
              isBalanceLoading={isBalancesLoading}
              // GATE-4 fix (finding 2) — lets SwapCard's established-absent latch tell a completed
              // load with no balance apart from a completed load that FAILED to find out.
              lastBalanceLoadSucceeded={lastBalanceLoadSucceeded}
              // #301a RE-ENTRY 3 — the cold-load window. `selectedSourceToken` defaults to 'USDC'
              // and `dexRoutes` is `[]` until this query lands, so a network picked RIGHT NOW is
              // decided with no evidence at all: tapping Robinhood strands the user on the
              // zero-route `robinhood/USDC` pair, PERSISTS `lastSourceChain`, and nothing recovers
              // it afterwards (the #240 effect's `isValidOn('USDC')` returns true — USDC declares no
              // `availableOn`). The chain control is unavailable, with a loading affordance, until
              // the table is in.
              isRoutesLoading={isRoutesLoading}
              maxReserve={maxSendReserve}
              // #261 F7 — the flag itself, NOT `maxReserve > 0`. Under the flag OFF this component
              // still receives the legacy stable `opexUsd` reserve, so gating the card's new
              // behaviours on a non-zero reserve shipped them to production users unflagged.
              reserveAware={isNativeMaxGasReserveEnabled()}
              // Freeze the Max re-snap while the review dialog is open — the modal reads `amount` as
              // a live prop and confirms whatever it is at CONFIRM time.
              snapSuspended={isReviewOpen}
              // #261 F7 (GATE-4 F-A, then pass-3 door A) — may `maxReserve` license an UPWARD revision?
              //
              // The first version of this asked only `!!dexFeeEstimate`, which certifies the wrong
              // fact: that an estimate OBJECT exists, not that the reserve in force is real.
              // `gasEstimateNative` is computed in its own try/catch (dex.ts:1223-1244), so a
              // gas-oracle failure returns a perfectly successful estimate with NO gas figure —
              // `maxSendReserve` then fails closed to 0 (:1244) while the estimate is present, and the
              // card snapped the field UP to the full balance. `maxSendReserve > 0` is the term that
              // says the reserve is genuinely in force rather than failed-closed.
              reserveReady={isDexRoute && !!dexFeeEstimate && maxSendReserve > 0}
              // #261 F7 (pass-3 door B) — WHEN was this reserve fetched?
              //
              // `staleTime: 30_000` with no `placeholderData` means a fresh cached estimate for a
              // previously-seen amount is served SYNCHRONOUSLY on a key change, with no blank window
              // at all. Two crossed cache entries (one fetched during a gas glitch, one after) then
              // feed the card alternating reserves indefinitely, and every up-leg looks legitimate
              // because an estimate really is present. Freshness is the only thing that distinguishes
              // "new information" from "the same two cached answers again": react-query stamps
              // `dataUpdatedAt` at FETCH time, not at serve time, so a re-served cache entry carries
              // its original timestamp and the card can refuse to act on it twice.
              reserveUpdatedAt={dexEstimateUpdatedAt}
              destBalance={(() => {
                if (!destBalance) return null
                // Don't show a balance for a recipient we didn't fetch (externally-pasted address) — it
                // would be the connected wallet's balance, not the recipient's.
                if (!recipientIsConnectedWallet) return null
                if (selectedDestToken === 'USDC') return destBalance.usdc ?? null
                if (selectedDestToken === 'USDT') return destBalance.usdt ?? null
                if (selectedDestToken === 'USDG') return destBalance.usdg ?? null // #174 — USDG, not native
                const td = TOKEN_PICKER_LIST.find(t => t.symbol === selectedDestToken)
                if (td?.isNative && td.availableOn && !td.availableOn.includes(bridgeState.destinationChain as Chain)) {
                  return null
                }
                return destBalance.native ?? null
              })()}
              sourceToken={selectedSourceToken}
              destToken={selectedDestToken}
              onSourceChainClick={() => setChainPicker('send')}
              onDestChainClick={() => setChainPicker('receive')}
              onSourceTokenClick={handleSourceTokenClick}
              onDestTokenClick={handleDestTokenClick}
              // #261 F3 — the wallet-chip brand icons, via the existing `walletKind` →
              // WALLET_LOGO_KEY → WalletLogos seam (NOT a new connector.icon path). The DEST kind is
              // already null unless the recipient IS the connected wallet (A4).
              sourceWalletKind={sourceWalletKind}
              destWalletKind={destWalletKind}
              // Fixer F-1 — the receive chip's accessible-name ownership claim. UNFLAGGED (ADDENDUM
              // A5): unlike `destWalletKind` above, this is the raw fund-safety value, not gated on
              // the wallet-type-label flag.
              recipientIsConnectedWallet={recipientIsConnectedWallet}
              onSourceWalletClick={() => openWalletModal('source')}
              // The dest chip opens the RecipientModal (choose recipient: use connected / paste a different
              // address / connect a cross-family wallet) — NOT the raw wallet modal, which would fight the
              // single same-ecosystem slot and loop. This is the reachable entry for a different destination.
              onDestWalletClick={handleSetRecipient}
              onSwapDirection={handleSwapDirection}
              onSetRecipient={handleSetRecipient}
              onSend={handleSend}
              onConnectWallet={() => openWalletModal('source')}
              isLoading={isDexRoute ? isDexTransferring : isTransferring}
              isQuoteLoading={isDexRoute && isDexEstimateFetching}
              // #337 GATE-4 FIX (F1) — reads the SAME `submitBlocked` memo `handleReviewConfirm` re-checks
              // at confirm time (defined above, after `dexReceiveNonPositive`). One composite, two readers.
              canSend={!submitBlocked}
              ctaLabel={
                // #614 — the native-deposit arms are withheld from this bundle (production): say so at
                // the CTA rather than throwing inside the wallet step. Amount-independent, so it sits first.
                nativeDepositUnavailable ? 'Native TRX swaps are not yet available here'
                : opexGate?.kind === 'routeUnsupported' ? (opexGate.message ?? 'Route not supported')
                // #337 — SECOND, immediately after routeUnsupported. That branch stays first because it
                // is amount-INDEPENDENT and true regardless of STALENESS (a policy marker on an estimate
                // already in hand survives a later keystroke) — but that is NOT the same as independent
                // of a MISSING estimate: `opexGate` early-returns `null` on `!dexFeeEstimate` (:2002), so
                // when `dexEstimateMissing` is the reason we're here, the routeUnsupported marker is gone
                // right along with the rest of `opexGate` — the two conditions never actually race for
                // the same case. Every branch below this one is amount-dependent and, inside the stale
                // window, was computed from the WRONG amount — including the mirror-case mislabel at
                // :3312 (pinned by belowMinStaleGate T9, R2-F1) reading "Amount too low — minimum ~$2.25"
                // over a 1000 the user has just typed.
                //
                // The copy WITHDRAWS a claim rather than asserting one: it names no minimum, no
                // shortfall and no amount, because there is no amount we can honestly speak about yet.
                // `dexEstimateMissing` takes this SAME branch — from the user's side both are "the
                // quote isn't ready", and minting two strings for one user-visible condition is the
                // split-brain this file keeps warning about. Tests assert SHAPE, never this literal,
                // so the wording stays the operator's to change. R1-F1 refines the `dexEstimateMissing`
                // half into three states (see `dexEstimateMissingLabel`'s doc comment); state 1 there is
                // this same 'Updating quote…' copy.
                //
                // ⚠️ IT SUPERSEDES 'Fee unavailable' IN ONE WINDOW, AND THAT IS INTENTIONAL. On an
                // UNPRICED estimate both `dexFeeUnavailable.blocked` (:3316) and this flag are true.
                // This is the more honest of the two there — the fee is not unavailable, the quote is
                // being updated — and the fee CARD still reads 'Fee unavailable' because FeeSection
                // reads `dexFee`, not the CTA. So the CTA gains one state and, in that window,
                // supersedes one.
                : dexEstimateStale ? 'Updating quote…'
                : dexEstimateMissing ? dexEstimateMissingLabel
                // #301 item #7a + #334 bug-3 — name the figure, but ONLY when it rests on a real
                // measurement AND is actionable. History: `quoteUnavailable` withholds `opexUsd` (null by
                // construction, api.ts) and `minAmountUsd` used to carry a ~2e-11 cache leftover — shown as
                // "minimum ~$1.00" on a Tron corridor whose real floor is ~$25 (a min BELOW what the user
                // typed). The old guard proxied "is this real?" via `opexUsd != null`, which NEVER fires for
                // quoteUnavailable — so the min was never named on a failed quote (the bare "Amount too low
                // for this route", bug-3). FIX: the backend now derives the floor from the cached fixFee when
                // the live opex is absent and tags it `minAmountTrusted` — a positive "this is a real number"
                // signal, not the ~0 leftover. Print when (a) `minAmountTrusted`, AND (b) the minimum exceeds
                // what was typed (mirrors `belowMinHintUsd`). `minUsd > 0` stays as a cheap first filter.
                : opexGate?.kind === 'quoteUnavailable'
                  ? (opexGate.minUsd > 0
                      && dexFeeEstimate?.minAmountTrusted
                      && opexGate.minUsd > (dexFeeEstimate?.sourceAmountUsd ?? parseFloat(bridgeState.amount || '0'))
                      ? `Amount too low for this route — minimum ${denomMin(opexGate.minUsd)}`
                      : 'Amount too low for this route')
                : opexGate?.kind === 'belowMinimum' ? `Amount too low — minimum ${denomMin(opexGate.minUsd)}`
                : opexGate?.kind === 'uneconomical' ? `Fees exceed amount — minimum ${denomMin(opexGate.minUsd)}`
                : opexGate?.kind === 'reduceAmount' ? `Reduce amount — you can send up to ${denomMin(opexGate.maxSendable ?? 0)}`
                : opexGate?.kind === 'insufficientToken' ? `Not enough ${selectedSourceToken}`
                : amountTooLowAfterFees ? (feeEstimate?.minSourceUsd
                    ? `Amount too low — minimum ${denomMin(feeEstimate.minSourceUsd.toFixed(2))}`
                    : 'Amount too low')
                // #261 F7 item 4 — NATIVE middle zone: they HOLD the balance, just not the gas headroom.
                // Sits directly in front of the gas label it replaces, so it can only ever re-word that
                // one state — `canSend` is untouched, so this changes copy, never what blocks (I-4).
                // Above-balance falls through to the gas label below (F7-3): there is no "Insufficient
                // balance" CTA on this path and inventing one would be a lie about which check fired.
                : nativeReduceTo != null ? `Reduce — you can send up to ${nativeReduceTo.toLocaleString('en-US', { maximumFractionDigits: 6 })} ${selectedSourceToken}`
                : insufficientNativeForFees ? `Need ~${insufficientNativeForFees.required.toLocaleString('en-US', { maximumFractionDigits: 4 })} ${insufficientNativeForFees.symbol} for gas — you have ${insufficientNativeForFees.held.toLocaleString('en-US', { maximumFractionDigits: 4 })}`
                : belowMinHintUsd ? `Amount too low — minimum ${denomMin(belowMinHintUsd)}`
                : dexMinHintUsd ? `Enter an amount (min ${denomMin(dexMinHintUsd)} on this route)`
                // BETA BUG-1b — the fee card refuses to state a figure; do not offer a green button.
                : dexFeeUnavailable.reason === 'dust' ? 'Amount too low'
                : dexFeeUnavailable.reason === 'refused' ? 'Fee unavailable'
                : ctaLabel
              }
              feeEstimate={isCctpRoute ? (feeEstimate ?? undefined) : undefined}
              secondsRemaining={secondsRemaining}
              fiatSymbol={fiatSymbol}
              fiatRate={fiatRate}
              fiatStale={fiatStale}
              formatFiatAmount={formatFiatAmount}
              onReceiveAmountChange={handleReceiveAmountChange}
              receiveEditable={(!isDexRoute || !isNativeDest) && selectedDestToken !== 'USDG'}
              minReceivedAmount={dexMinReceived}
              minReceivedSymbol={selectedDestToken}
              dexFee={dexFee}
            />
            {/* #94 Bug 2 — a dedicated native-gas shortfall line (not just the CTA), denominated in the
                chain's NATIVE token with both sides (needed vs held), so the user knows they're short on
                gas (e.g. POL), not on the stablecoin they're sending. Reuses the FeeSection warning tokens. */}
            {/* #261 F7 — suppressed when the CTA carries the "Reduce" state. This panel says "Not
                enough ‹native› for gas … Add ‹native› to cover the fee", which in the middle zone is
                false twice over: the user is NOT short of the native token (they hold it — they just
                lack headroom on top of a full-balance send), and "Add ETH" instructs them to go SPEND
                money they do not need to spend. Its `required` figure is not gas either — it is the
                whole send plus fees, labelled "for network gas". Before F7 the CTA and this panel
                agreed (both said "need gas"), so the contradiction is new: fixing one surface and
                leaving the other is what creates it. The CTA already names the real action, so the
                panel adds nothing true here. Every other state it serves is untouched. */}
            {insufficientNativeForFees && nativeReduceTo == null && (
              <div
                role="status"
                style={{
                  padding: '10px 14px', borderRadius: 14, marginTop: 8,
                  background: c.wB, border: `1px solid ${c.wC}33`,
                  display: 'flex', alignItems: 'flex-start', gap: 10,
                  color: c.wC, fontSize: 13, lineHeight: 1.5,
                }}
              >
                <AlertTriangle size={16} style={{ flexShrink: 0, marginTop: 1 }} />
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 600, fontSize: 14 }}>Not enough {insufficientNativeForFees.symbol} for gas</div>
                  <div style={{ opacity: 0.85, marginTop: 2 }}>
                    This swap needs ~{insufficientNativeForFees.required.toLocaleString('en-US', { maximumFractionDigits: 4 })} {insufficientNativeForFees.symbol} for network gas — you have {insufficientNativeForFees.held.toLocaleString('en-US', { maximumFractionDigits: 4 })} {insufficientNativeForFees.symbol}. Add {insufficientNativeForFees.symbol} to cover the fee.
                  </div>
                </div>
              </div>
            )}
            {/* #131 C — POLICY-refused corridor (Tron USDC cross-chain). The CTA is already disabled via
                opexGate; this panel supplies the WHY and names the supported path, so the user is steered
                to Tron USDT instead of staring at a dead button. Message is the server's, rendered verbatim.
                Reuses the same warning tokens as the gas-shortfall panel above — no new styling. */}
            {opexGate?.kind === 'routeUnsupported' && (
              <div
                role="status"
                style={{
                  padding: '10px 14px', borderRadius: 14, marginTop: 8,
                  background: c.wB, border: `1px solid ${c.wC}33`,
                  display: 'flex', alignItems: 'flex-start', gap: 10,
                  color: c.wC, fontSize: 13, lineHeight: 1.5,
                }}
              >
                <AlertTriangle size={16} style={{ flexShrink: 0, marginTop: 1 }} />
                <div style={{ flex: 1 }}>
                  {/* #614 — a transient same-chain 1Click refusal reuses this box; it is not a policy refusal. */}
                  <div style={{ fontWeight: 600, fontSize: 14 }}>{
                    opexGate.code === 'SAME_CHAIN_ROUTE_UNAVAILABLE' ? 'Temporarily unavailable'
                    : opexGate.code === 'PROVIDER_MIN_ABOVE_TIER' ? 'Minimum changed'
                    : 'Route not supported'
                  }</div>
                  <div style={{ opacity: 0.85, marginTop: 2 }}>
                    {opexGate.message ?? 'Tron USDC is same-chain only. Use Tron USDT to bridge in or out of Tron.'}
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Activity → click a transfer → its ProgressPanel rendered INLINE here (the same progress view a live
            bridge shows), not a popup. Scrolled into view on open. Dismiss returns to the form. */}
        {progressTransfer && (
          <div ref={selectedProgressRef} style={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
            <ProgressPanel
              title={
                ['mint_success', 'complete', 'recovered'].includes(progressTransfer.status)
                  ? terminalPanelTitle('complete', isAtomicSameChain(progressTransfer.sourceChain, progressTransfer.destinationChain, progressTransfer.provider), 'Transfer Complete')
                : ['failed', 'error', 'mint_failed'].includes(progressTransfer.status)
                  ? terminalPanelTitle('failed', isAtomicSameChain(progressTransfer.sourceChain, progressTransfer.destinationChain, progressTransfer.provider), 'Transfer Failed')
                : 'Transfer Progress'
              }
              amount={progressTransfer.amount}
              sourceToken={progressTransfer.sourceToken}
              destToken={progressTransfer.destToken}
              sourceChain={progressTransfer.sourceChain}
              destChain={progressTransfer.destinationChain}
              steps={progressTransferSteps}
              isComplete={['mint_success', 'complete', 'recovered'].includes(progressTransfer.status)}
              isError={['failed', 'error', 'mint_failed'].includes(progressTransfer.status)}
              onDismiss={() => setProgressTransfer(null)}
              onStartNew={() => setProgressTransfer(null)}
              // This mount's onStartNew is identical to onDismiss and to the X — it closes the panel
              // and nothing else. "Start over" here would be a NEW mislabel, not a fix (I-3) — and so
              // would "Start new transfer" on the success footer, which is the same button.
              startNewLabel={startNewLabelFor('dismiss-only')}
              completeLabel={completeLabelFor('dismiss-only')}
            />
          </div>
        )}

        {/* Active CCTP Transfer — ProgressPanel. Suppressed while a drawer-selected transfer is open so the
            two never stack (mutual exclusion with the progressTransfer panel above). Also suppressed while a DEX
            transfer is ACTIVELY in-flight (belt-and-suspenders vs. the sibling-reset on initiation); keyed on the
            active (non-terminal) DEX state only, so a completed/errored DEX panel can never deadlock this one. */}
        {(burnTxHash || isTransferring) && !progressDismissed && !progressTransfer && !isDexTransferring && (
          <ProgressPanel
            title={
              transferStep === 'complete'
                ? terminalPanelTitle('complete', executedAtomicSameChain, 'Transfer Complete')
              // DEV-375 — checked BEFORE the failed branch, which is the branch that was lying.
              : cctpReceiptUnknown
                ? terminalPanelTitle('unknown', executedAtomicSameChain, 'Transfer Failed')
              : transferStep === 'error'
                ? terminalPanelTitle('failed', executedAtomicSameChain, 'Transfer Failed')
              : 'Current Transfer'
            }
            amount={bridgeState.amount || '0'}
            sourceToken="USDC"
            destToken="USDC"
            sourceChain={bridgeState.sourceChain}
            destChain={bridgeState.destinationChain}
            steps={cctpSteps}
            isComplete={transferStep === 'complete'}
            // DEV-375 — `isError` drives the red header AND the reset-bearing error footer, so an
            // unconfirmed burn must NOT set it. `isUnknown` renders the neutral header + a single
            // "Close".
            isError={transferStep === 'error' && !cctpReceiptUnknown}
            isUnknown={cctpReceiptUnknown}
            onDismiss={dismissLiveProgress}
            // ask (3): a burn hash means USDC is already in flight, so the primary control must not
            // reset the form. This is the highest-value mount for the guard — its `onStartNew` is a
            // real form reset on a live burn, and this panel renders only when `burnTxHash ||
            // isTransferring`, i.e. it is hash-present by construction most of the time.
            onStartNew={transferStep !== 'complete' && burnTxHash ? dismissLiveProgress : () => { reset(); setProgressDismissed(false) }}
            startNewLabel={startNewLabelFor('resets-the-form', !!burnTxHash)}
            completeLabel={completeLabelFor('resets-the-form')}
          />
        )}

        {/* Active DEX Transfer — ProgressPanel. Suppressed while a drawer-selected transfer is open (mutual
            exclusion with the progressTransfer panel above) so the live + historical cards never stack. Also
            suppressed while a CCTP transfer is ACTIVELY in the burn phase (isTransferring); keyed on the active
            (non-terminal) CCTP burn state only, so a completed/errored CCTP panel can never deadlock this one. */}
        {dexStep !== 'idle' && !progressDismissed && !progressTransfer && !isTransferring && (
          <ProgressPanel
            title={
              // The TERMINAL titles take their same-chain answer from the transfer that RAN
              // (bridgeState), never from `selectedRoute` — which is null whenever the current token
              // pair has no route, and whose `undefined === undefined` then reads as same-chain. The
              // helper refuses an empty pair as a second line of defence. The in-progress line below
              // keeps its existing expression (and that pre-existing collapse) unchanged — out of
              // scope by the spec's NOT DOING.
              dexStep === 'complete'
                ? terminalPanelTitle('complete', executedAtomicSameChain, 'Swap Complete')
              // DEV-375 — before the failed branch, for the same reason as the CCTP mount.
              : dexReceiptUnknown
                ? terminalPanelTitle('unknown', executedAtomicSameChain, 'Swap Failed')
              : dexStep === 'error'
                ? terminalPanelTitle('failed', executedAtomicSameChain, 'Swap Failed')
              : `${executedAtomicSameChain ? 'Swap' : 'Transfer'}`
            }
            amount={dexSubmittedAmount || bridgeState.amount || '0'}
            sourceToken={selectedSourceToken}
            destToken={selectedDestToken}
            sourceChain={bridgeState.sourceChain}
            destChain={bridgeState.destinationChain}
            steps={dexSteps}
            isComplete={dexStep === 'complete'}
            // DEV-375 — see the CCTP mount's note; same reasoning, same pair of props.
            isError={dexStep === 'error' && !dexReceiptUnknown}
            isUnknown={dexReceiptUnknown}
            onDismiss={dismissLiveProgress}
            // ask (3). `dexSourceTxHash` is the value-moving broadcast hash (an ERC-20 APPROVE hash
            // is deliberately excluded — an approve moves nothing and `approveErc20Exact`'s no-op
            // branch makes its retry safe, per I5).
            onStartNew={dexStep !== 'complete' && dexSourceTxHash ? dismissLiveProgress : () => { resetDex(); setProgressDismissed(false) }}
            startNewLabel={startNewLabelFor('resets-the-form', !!dexSourceTxHash)}
            completeLabel={completeLabelFor('resets-the-form')}
          />
        )}

        {/* NEAR 1Click deposit-address details (Tron corridor) — surfaces the deposit address +
            exact send amount + quote deadline + terminal dest-tx link alongside the progress panel.
            HIDDEN FOR NOW (SHOW_ONECLICK_DEPOSIT_PANEL=false) — component + wiring kept intact for later. */}
        {SHOW_ONECLICK_DEPOSIT_PANEL && selectedRoute?.provider === 'near_1click' && dexStep !== 'idle' && !progressDismissed && !progressTransfer && (
          <Near1ClickDepositPanel
            dexStep={dexStep}
            depositAddress={dexBridgeResponse?.depositAddress ?? null}
            depositMemo={dexBridgeResponse?.depositMemo ?? null}
            amount={bridgeState.amount || '0'}
            sourceToken={selectedSourceToken}
            sourceChain={bridgeState.sourceChain}
            destToken={selectedDestToken}
            destChain={bridgeState.destinationChain}
            amountOut={dexBridgeResponse?.amountOut ?? null}
            minAmountOut={dexBridgeResponse?.minAmountOut ?? null}
            deadlineIso={dexBridgeResponse?.deadline ?? null}
            destTxHash={dexOrderStatus?.fulfilledDstEventMetadata?.transactionHash ?? null}
            error={dexError}
            getExplorerUrl={getExplorerUrl}
          />
        )}

        {/* Pending Transfers inline card REMOVED — pending CCTP transfers now surface in the Activity drawer
            (top-right). Click a transfer row there to open its ProgressPanel ("see where it's at"). This drops
            the inline duplicate and the stale-status display it could show after a mid-progress refresh. */}
      </div>

      {/* Wallet Modal */}
      <WalletModal
        isOpen={isWalletModalOpen}
        onClose={() => setModalTarget(null)}
        chainFamily={modalChainFamily}
        onSelectFamily={handleFamilySelected}
        // Recipient-only paste path: for a destination connect, offer "Enter a custom address" that closes
        // the wallet list and opens the RecipientModal. Never for source (can't send from an address you
        // don't control) — so the handler is undefined there and the row doesn't render.
        onEnterCustomAddress={modalTarget === 'destination'
          ? () => { setModalTarget(null); setIsRecipientModalOpen(true) }
          : undefined}
      />

      {/* Chain Picker Modal */}
      <ChainPickerModal
        show={chainPicker !== null}
        onClose={() => { setChainPicker(null); setAutoOpenedEvmSourcePicker(false); setAutoOpenedEvmDestPicker(false) }}
        selectedChain={chainPicker === 'send' ? bridgeState.sourceChain : bridgeState.destinationChain}
        disabledChain={
          chainPicker === 'send'
            ? (autoOpenedEvmSourcePicker || selectedSourceToken !== selectedDestToken
                ? null : bridgeState.destinationChain)
            : (autoOpenedEvmDestPicker || selectedSourceToken !== selectedDestToken
                ? null : bridgeState.sourceChain)
        }
        disabledLabel={chainPicker === 'send' ? 'Same as destination' : 'Same as source'}
        chains={
          chainPicker === 'receive' && autoOpenedEvmDestPicker ? EVM_DEST_CHAINS
          : chainPicker === 'send' && autoOpenedEvmSourcePicker ? EVM_DEST_CHAINS
          : ALL_CHAINS
        }
        balances={allBalances}
        selectedToken={chainPicker === 'send' ? selectedSourceToken : selectedDestToken}
        isLoading={isBalancesLoading}
        evmConnected={!!evmAddress}
        solanaConnected={!!solanaAddress}
        tronConnected={tronConnected}
        minAmountByDest={chainPicker === 'receive' ? minAmountByDest : undefined}
        minAmountFlat={chainPicker === 'send' ? sendMinUsd : undefined}
        // Destination picks must set the chain even without a connected wallet of that family — the user
        // can receive to a pasted address. (Source keeps the connect gate: you need a wallet to send.)
        allowUnconnectedSelect={chainPicker === 'receive'}
        onConnectWallet={(family) => {
          setChainPicker(null)
          setAutoOpenedEvmSourcePicker(false)
          setAutoOpenedEvmDestPicker(false)
          setModalChainFamily(family)
          setModalTarget(chainPicker === 'send' ? 'source' : 'destination')
        }}
        onSelect={(chain) => {
          setAutoOpenedEvmSourcePicker(false)
          setAutoOpenedEvmDestPicker(false)
          if (chainPicker === 'send') {
            setSourceChain(chain)
            localStorage.setItem('parsebridge:lastSourceChain', chain)
            // #301a — ROUTE-AWARE, not `availableOn`-shaped, and never the constant 'USDC'. See
            // chainPickFallbackToken above for why the old static check both missed the USDC/USDT
            // case entirely and manufactured a zero-route pair when it did fire. Persist the token
            // actually chosen, never a constant.
            const next = chainPickFallbackToken(dexRoutes, chain, selectedSourceToken)
            if (next) {
              setSelectedSourceToken(next)
              localStorage.setItem('parsebridge:lastSourceToken', next)
            }
          } else {
            setDestinationChain(chain)
            localStorage.setItem('parsebridge:lastDestChain', chain)
            // #301a RE-ENTRY 3 — THE ROUTE-AWARE DEST ARM IS DELETED. This is the pre-#301a static
            // rule, restored verbatim, and it is deliberately the ONLY thing the picker does to the
            // dest token: a token with a restricted `availableOn` (natives + #174 USDG) on a chain
            // that does not carry it falls back to USDC rather than stranding an invalid combo.
            //
            // WHY THE ROUTE-AWARE VERSION HAD TO GO. It called `chainPickFallbackToken` against a
            // `destChain`-only filter, which sees dest tokens reachable from ANY source and therefore
            // cannot know the source half of the pair. On a SAME-CHAIN pick it landed on the token
            // equal to the current SOURCE token — an identity pair the live table never carries (0 of
            // 62 same-chain rows have `sourceToken === destToken`) — PERSISTED it, and was then
            // overridden by the dest effect one commit later: a visible flip plus a non-routable
            // saved value. Replayed over the live 756-row table, every one of the 32 regressions and
            // all 44 picker-vs-effect disagreements came from this arm.
            //
            // The dest effect (`:593`-`:616`) is the better-informed writer — it filters on
            // sourceChain + sourceToken + destChain together — and is now the SOLE route-aware dest
            // writer. Same call already made for #240's dest arm: one dest writer, structurally.
            const tokenDef = TOKEN_PICKER_LIST.find(t => t.symbol === selectedDestToken)
            if (tokenDef?.availableOn && !tokenDef.availableOn.includes(chain)) {
              setSelectedDestToken('USDC')
              localStorage.setItem('parsebridge:lastDestToken', 'USDC')
            }
          }
        }}
      />

      {/* Token Selector Modal */}
      <TokenSelectorModal
        isOpen={isTokenSelectorOpen}
        onClose={() => setIsTokenSelectorOpen(false)}
        onSelect={handleTokenSelect}
        selectedToken={tokenSelectorSide === 'source' ? selectedSourceToken : selectedDestToken}
        selectedChain={tokenSelectorSide === 'source' ? bridgeState.sourceChain : bridgeState.destinationChain}
      />

      {/* Recipient Modal */}
      <RecipientModal
        show={isRecipientModalOpen}
        onClose={() => setIsRecipientModalOpen(false)}
        destChain={bridgeState.destinationChain}
        sourceChain={bridgeState.sourceChain}
        tokenSymbol={selectedDestToken}
        connectedAddress={
          bridgeState.destinationChain === 'tron'
            ? tronAddress ?? null
            : bridgeState.destinationChain === 'solana'
              ? solanaAddress ?? null
              : evmAddress ?? null
        }
        // #261 F3 (spec item 3) — name/icon the connected-address option's PROVIDER, so the user sees
        // it is their MetaMask/Phantom rather than their Robinhood app. A LABEL, not caution copy:
        // there is deliberately no "paste your Robinhood address" guidance, because RH deliverability
        // on 4663 is UNVERIFIED (ADDENDUM A1(ii)) and steering users there is a new loss vector.
        // Already null when the flag is off.
        connectedWalletKind={recipientModalWalletKind}
        // Fixer F-3 — the option's NAME text (wallet + network), routed through the same
        // buildRecipientWalletLabel the review card uses. Already null when the flag is off.
        connectedWalletLabel={recipientModalWalletLabel}
        onUseConnected={(addr) => {
          setDestinationAddress(addr)
          // #145 — the user has answered the question the notice was asking. Drop it.
          setRecipientResetChain(null)
          setIsRecipientModalOpen(false)
        }}
        onPasteAddress={(addr) => {
          // Explicit recipient choice — must win over the ambient connected account and survive re-renders.
          setDestinationAddress(addr, true)
          setRecipientResetChain(null)
          setIsRecipientModalOpen(false)
        }}
        onConnectWallet={(family) => {
          setIsRecipientModalOpen(false)
          setModalChainFamily(family)
          setModalTarget('destination')
        }}
      />

      {/* Fiat Currency Modal */}
      <FiatCurrencyModal
        show={isFiatModalOpen}
        onClose={() => setIsFiatModalOpen(false)}
        selected={fiatCurrency}
        onSelect={setFiatCurrency}
      />

      {/* Review Transfer Modal.
          #336 D5 — `dexFee` is the SAME memo (:1619) the fee card reads, and it is already falsy when
          !isDexRoute, so the modal renders the same expression over the same object and the two
          cannot state different fees. It must never be fed `totalFee` below: that is `reviewTotalFee`
          (:2338), an input−output subtraction across two different tokens.
          The modal collapses the memo's `undefined`/`null` distinction (#336 R10) on purpose — it is
          told the route kind by `isDexRoute` below, so it needs no second signal.
          #261 F8 / C-10 — `estimatedReceivedUsd` is the receive's USD value (:1576), passed so the TO
          row can carry a fiat subtitle and `You pay − Total fees ≈ receive` becomes performable on a
          NATIVE dest, where the row otherwise reads "0.000794 ETH". Non-null only in that case. It is
          passed unconditionally and gated by the flag INSIDE the modal, so this call site stays
          flag-free — and, critically, `dexFee` below is still the UNFLAGGED memo: the F8 display flag
          is read by the two display surfaces themselves and never rides on this payload, so it cannot
          reach `dexFeeUnavailable` (:1832), which grades the CTA (C-7 / F-C7).
          #261 F5 / C1 — `sourceAmountUsd` (the input's USD value) and `isNativeDest` (whether the dest
          token's own figure can stand in for dollars) feed the two per-row `≈ $` subtitles. Both are
          DEX-only — gated `isDexRoute ? … : X` exactly like `youPaySource`, so a CCTP review card
          cannot grow a row. (B3 — the two X's DIFFER, deliberately: `sourceAmountUsd` falls back to
          `null` (its prop type is `number | null`); `isNativeDest` falls back to `undefined` (its prop
          type is `boolean | undefined`) — do not "fix" one to match the other's literal.) The modal
          decides whether to RENDER; this call site holds no copy of that decision, exactly as with
          `estimatedReceivedUsd` above.
          #261 F5 / §13.0 — `estimatedReceived` below is `reviewReceive` (:2514), NOT the
          `estimatedReceived` memo (:1536). On a same-chain REVERSE quote the card's To row states the
          minimum estimated arrival, so it matches FeeSection's "Minimum received"; every other case
          passes the memo's value verbatim. The memo itself is deliberately untouched — it also drives
          the receive INPUT field (rewritten on blur by SwapCard, which is Forbidden here) and
          `dexFee`'s `destAmt`, so editing it would move the FEE on a display-only change. */}
      <ReviewTransferModal
        isOpen={isReviewOpen}
        onConfirm={handleReviewConfirm}
        onCancel={() => setIsReviewOpen(false)}
        amount={bridgeState.amount}
        estimatedReceived={reviewReceive}
        sourceToken={selectedSourceToken}
        destToken={selectedDestToken}
        sourceChain={bridgeState.sourceChain}
        destinationChain={bridgeState.destinationChain}
        totalFee={reviewTotalFee}
        feePercentage={reviewFeePercentage}
        estimatedTime={reviewDeliveryTime}
        recipientAddress={reviewRecipientAddress}
        recipientIsConnectedWallet={recipientIsConnectedWallet}
        // #261 F3 — a LABEL, never an address. Already `undefined` when the flag is off; the modal
        // re-gates on the flag and refuses an address-shaped value regardless.
        recipientWalletLabel={recipientWalletLabel}
        isDexRoute={isDexRoute}
        dexFee={dexFee}
        formatFiatAmount={formatFiatAmount}
        estimatedReceivedUsd={estimatedReceivedUsd}
        sourceAmountUsd={isDexRoute ? dexFeeEstimate?.sourceAmountUsd ?? null : null}
        isNativeDest={isDexRoute ? isNativeDest : undefined}
        youPaySource={isDexRoute ? dexFeeEstimate?.youPaySource ?? null : null}
        sourceCurrency={selectedSourceToken}
        dexGive={dexGiveDisclosure}
        // DEV-363 ITEM C — the route FACT, not the flag decision. Same expression the 1Click deposit
        // panel already gates on (:3945), so one answer to "is this a deposit-address route" serves
        // both. Passed unconditionally; the modal re-reads VITE_DEPOSIT_ADDRESS_NOTICE itself, exactly
        // as it does for F8 and F3, so this call site holds no copy of that decision.
        isDepositAddressRoute={selectedRoute?.provider === 'near_1click'}
        isAtomicSameChain={atomicSameChain}
      />

      {/* #144 — warn + confirm on a "code here, not there" recipient. Never a hard block. */}
      <RecipientRiskModal
        risk={recipientRisk?.risk ?? null}
        onProceed={() => answerRecipientRisk(true)}
        onCancel={() => answerRecipientRisk(false)}
      />

      {/* Activity Drawer */}
      <ActivityDrawer
        open={activityOpen}
        onClose={() => setActivityOpen(false)}
        transfers={activityTransfers}
        activeTransfer={activeTransferForDrawer}
        onDismiss={dismissTransfer}
        onDismissAll={dismissAllTransfers}
        getExplorerUrl={getExplorerUrl}
        inProgressCount={inProgressCount}
        walletConnected={isEvmConnected || isSolanaConnected || tronConnected}
        onConnectWallet={() => { setActivityOpen(false); setModalTarget('source') }}
        cancelStates={cancelStates}
        onCancelOrder={(id) => {
          const order = storedDexOrders.find(o => o.transferId === id)
          if (order) openCancelReview(order)
        }}
        onSelectTransfer={(t) => { setActivityOpen(false); setProgressTransfer(t) }}
      />


      {cancelReview && (() => {
        const { order, nonce, deadline } = cancelReview
        const st = cancelStates[order.transferId]
        return (
          <CancelReviewModal
            isOpen={true}
            rows={cancelDisplayFields({
              transferId: order.transferId,
              wallet: order.sourceAddress,
              nonce,
              deadline,
              chain: order.sourceChain,
            })}
            status={st?.status ?? 'idle'}
            error={st?.error}
            onConfirm={() => { void cancelOrder(order, { nonce, deadline }) }}
            onRetry={() => {
              // Re-mint a fresh single-use nonce/deadline (the prior one was consumed/expired) and re-submit
              // with those exact values — the displayed rows update from the same fresh values (shown==signed).
              const freshNonce = generateCancelNonce()
              const freshDeadline = cancelDeadline()
              setCancelReview({ order, nonce: freshNonce, deadline: freshDeadline })
              void cancelOrder(order, { nonce: freshNonce, deadline: freshDeadline })
            }}
            onClose={() => { resetCancelState(order.transferId); setCancelReview(null) }}
          />
        )
      })()}
    </Layout>
  )
}
